import React, { useEffect, useMemo, useRef, useState } from 'react';
import { spPing } from './socialProof.jsx';
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { getMaskedSlug, storeUrl, loginUrl, dashboardUrl, officeUrl, maskedActive, accountProfileUrl, APP_SIGNIN_PARAM } from "../../util/maskedDomain";
import { Get } from "../../hooks/useFetch";
import { SendRequest, SendRawRequest } from "../../hooks/usePost";
import { GetScope, GetToken } from "../../features/authentication/hooks/useToken.jsx";
import { isProtectedPage, pageHasUnsavedInput } from "../../util/versionWatchdog";
import DataLoading from "../../components/dataLoading";
import { cacheStoreSkin, readStoreSkin } from "../../util/storeSkinCache";
import { findTemplate } from './templateMeta';
import { MD2_TEXTS, MD2_LISTS, md2BadgeColors, md2ProductInfo } from './md2Defaults';
import { PAYMENT_ICONS_HTML } from './md2FooterIcons';
import { T8_TEXTS, T8_LISTS, T9_TEXTS, T9_LISTS, T10_TEXTS, T10_LISTS, T11_TEXTS, T11_LISTS, T12_TEXTS, T12_LISTS, T14_TEXTS, T14_LISTS, T15_TEXTS, T15_LISTS, T16_TEXTS, T16_LISTS, T17_TEXTS, T17_LISTS, T18_TEXTS, T18_LISTS } from './nativeTplDefaults';
import {
  money, EDIT_OUTLINE, DEFAULT_CART_ICON, EditableText, EditableImage,
  itemEdit as md2ItemEdit, DelChip as Md2Del, AddChip as Md2Add,
  HideSection as Md2Section, HideText as Md2Text,
  storefrontCategories, filterByCategory, customCssFor, isEnrollmentOnlyProduct,
  AuthHeaderButton, EnrollHeaderButton, loadAcceptJs, dispatchAcceptData, MYUS_RADIO_CSS, EditDock, PageSeoEditor, ProvenSeal, CustomHtml } from './editKit';
import { CartProvider, CartDrawer, CartIcon, useCart, SubscriptionPriceTag, lineDiscountFor, cartDiscountPct, maxQtyFor } from './cartKit';
import { StorePhoneField, toE164, phoneIsValid, safeParsePhone } from './phoneField';
import { useWizloCustomer, useWizloCoupon, useWizloFrame, WizloCouponBanner } from './wizloKit';
import { hrtEnabled } from './hrtContent';
import { MOBILE_NAV_CSS, MobileMenuIcon, MobileDrawer, HeaderMenuLinks } from './mobileNav';
import { GuestCheckout } from './guestCheckout';
import { NmiCardFields, isNmiGateway, gatewayIsReady } from './nmiKit.jsx';
import SeoHead, { resolvePageSeo } from './seoHead.jsx';
import StorefrontLeadWheel, { wheelCoupon } from './leadWheel.jsx';
import StorefrontSocialProof from './socialProof.jsx';
import StorefrontScratchCard, { scratchCoupon } from './scratchCard.jsx';
import { FreeShipNotice, FreeShipBanner, FreeShipBadge, readFreeShip } from './freeShipping.jsx';
import { checkoutTheme, checkoutVariant, CHECKOUT_GRID_CSS, ckColumns } from './checkoutSkin';
import CheckoutTimer from './checkoutTimer';
import CheckoutDelivery from './checkoutDelivery';
import { LowStockPill, readLowStock, lowStockMap, firstOfProduct, qtyOfProduct } from './lowStock.jsx';
import { CtaSubline, readCtaBadge, CTA_STACK } from './ctaBadge.jsx';
import LegalFallback from './legalFallback.jsx';
import { homeLanderOn } from './naturalSleepConfig';
import { PAYMENT_MARKS } from './md2FooterIcons';
import { pageByKey, subForPage } from './storefrontPages';
import { checkoutBackUrl } from './checkoutEntry';
import { identifyEmail, StorefrontTracking, trackPurchase, attributionFields, setTrackingIdentity, trackAddPaymentInfo, trackAddShippingInfo, trackingCartSnapshot } from './tracking';
import { useGoogleReviewsOptIn } from './googleReviews';
import { SponsorBanner, RefCapture, SponsorPage, getSponsor, captureSponsorById } from './sponsorKit';
import { StorefrontChatwoot } from './chatwoot.jsx';
import StorefrontWebPush from './webPush.jsx';
import { loadChunk } from '../../util/chunkRecovery';
// Checkout-only, auth-only and template-2-only screens load when reached, not
// with the home page. Each is a component-only import (verified), so the lazy
// boundary really does keep its module out of the storefront chunk.
const StorefrontBlogBody = React.lazy(() => loadChunk(() => import('./storefrontBlog.jsx')));
const PaymentRequestCheckout = React.lazy(() => loadChunk(() => import('./prCheckout.jsx')).then((m) => ({ default: m.PaymentRequestCheckout })));
const Md2HrtHome = React.lazy(() => loadChunk(() => import('./md2Hrt')).then((m) => ({ default: m.Md2HrtHome })));
const Md2HrtGender = React.lazy(() => loadChunk(() => import('./md2Hrt')).then((m) => ({ default: m.Md2HrtGender })));
const Md2HrtPortal = React.lazy(() => loadChunk(() => import('./md2Hrt')).then((m) => ({ default: m.Md2HrtPortal })));
const AuthGateModal = React.lazy(() => loadChunk(() => import('./authGate')).then((m) => ({ default: m.AuthGateModal })));
const RegisterPage = React.lazy(() => loadChunk(() => import('./authGate')).then((m) => ({ default: m.RegisterPage })));
// Sign in inside an iPhone Home Screen app (?aregoSignIn=…, see appSignIn.jsx).
const AppSignIn = React.lazy(() => loadChunk(() => import('./appSignIn.jsx')).then((m) => ({ default: m.StorefrontAppSignIn })));
import { TPL } from './templateChunks.js';
import { blobUrl } from "../../util/blobUrl";

// ── Template skins, one chunk each ──────────────────────────────────────────
// Every store runs exactly one template, but all eleven used to be imported
// statically — so a shopper downloaded ~490 KB of designs for stores they will
// never see before their own could paint. Each is a dynamic import now; the
// Suspense boundary around <kit.Shell> shows the same loading screen the store
// fetch already shows, in the store's own colours.
//
// The kit maps below keep EXACTLY the key sets they had: the page switch tests
// `kit.Legal`, `kit.SleepSpray` and friends for existence and falls back to
// Home, so a template must not appear to export something it does not.
const lazyTpl = (tid, name) => React.lazy(() => loadChunk(TPL[tid]).then((m) => ({ default: m[name] })));
const lazyFrom = (loader, name) => React.lazy(() => loadChunk(loader).then((m) => ({ default: m[name] })));

// Template 17 renders its own static content pages outside the kit map.
const T17Info = lazyTpl(17, 'T17Info');

// Marketplace storefront engine: one runtime, ten template skins driven by
// templateMeta palettes + the marketplace's saved edits. Product data stays
// dynamic from inventory. HIPAA marketplaces swap checkout for the secure
// portal iframe. Templates 2/8/9/10 are dedicated designs with their own
// shells; the rest share the generic Shell.

const GRID_TEMPLATES = new Set(['1', '4', '5', '7']);

// Per-template default copy for the dedicated skins; all share the edits.md2
// storage bucket (one template per marketplace — no collisions).
// Template 13 (Auera Skin Care) reuses tpl11's content model verbatim, so it
// shares T11_TEXTS/T11_LISTS — a store duplicated from an Aurea Build (tpl11)
// marketplace renders identically under tpl13.
const SKIN_TEXTS = { 2: MD2_TEXTS, 8: T8_TEXTS, 9: T9_TEXTS, 10: T10_TEXTS, 11: T11_TEXTS, 12: T12_TEXTS, 13: T11_TEXTS, 14: T14_TEXTS, 15: T15_TEXTS, 16: T16_TEXTS, 17: T17_TEXTS, 18: T18_TEXTS };
const SKIN_LISTS = { 2: MD2_LISTS, 8: T8_LISTS, 9: T9_LISTS, 10: T10_LISTS, 11: T11_LISTS, 12: T12_LISTS, 13: T11_LISTS, 14: T14_LISTS, 15: T15_LISTS, 16: T16_LISTS, 17: T17_LISTS, 18: T18_LISTS };

// The template dispatch table.
//
// Built on FIRST USE, not at module scope: template 2's components are plain
// consts declared further down this same file, and reading them while the
// module is still initialising throws "Cannot access … before initialization"
// — which takes down every storefront, not just template 2. Cached after the
// first call so each React.lazy component keeps one identity for the life of
// the page; recreating them per render would remount the whole subtree on
// every state change.
let KITS_CACHE = null;
const kitFor = (tid) => {
  KITS_CACHE ??= {
  2: { Shell: Md2Shell, Home: Md2Home, Listing: Md2Listing, Product: Md2Product },
  8: { Shell: lazyTpl(8, 'T8Shell'), Home: lazyTpl(8, 'T8Home'), Listing: lazyTpl(8, 'T8Listing'), Product: lazyTpl(8, 'T8Product') },
  9: { Shell: lazyTpl(9, 'T9Shell'), Home: lazyTpl(9, 'T9Home'), Listing: lazyTpl(9, 'T9Listing'), Product: lazyTpl(9, 'T9Product') },
  10: { Shell: lazyTpl(10, 'T10Shell'), Home: lazyTpl(10, 'T10Home'), Listing: lazyTpl(10, 'T10Listing'), Product: lazyTpl(10, 'T10Product') },
  11: { Shell: lazyTpl(11, 'T11Shell'), Home: lazyTpl(11, 'T11Home'), Listing: lazyTpl(11, 'T11Listing'), Product: lazyTpl(11, 'T11Product'), SecondPage: lazyTpl(11, 'T11SecondPage') },
  12: { Shell: lazyTpl(12, 'T12Shell'), Home: lazyTpl(12, 'T12Home'), Listing: lazyTpl(12, 'T12Home'), Product: lazyTpl(12, 'T12Product'), LabTested: lazyTpl(12, 'T12LabTested'), Peptides: lazyTpl(12, 'T12Peptides'), Testing: lazyTpl(12, 'T12Testing'), COALibrary: lazyTpl(12, 'T12COALibrary') },
  13: { Shell: lazyTpl(13, 'T13Shell'), Home: lazyTpl(13, 'T13Home'), Listing: lazyTpl(13, 'T13Listing'), Product: lazyTpl(13, 'T13Product'), SecondPage: lazyTpl(13, 'T13SecondPage') },
  14: { Shell: lazyTpl(14, 'T14Shell'), Home: lazyTpl(14, 'T14Home'), HomeLander: lazyFrom(() => import('./tpl14Natural.jsx'), 'T14HomeLander'), ProductLander: lazyFrom(() => import('./tpl14Natural.jsx'), 'T14ProductLander'), Listing: lazyTpl(14, 'T14Listing'), Product: lazyTpl(14, 'T14Product'), Policy: lazyTpl(14, 'T14Policy'), Refund: lazyTpl(14, 'T14Refund'), SleepSpray: lazyTpl(14, 'T14SleepSpray'), SleepSprayHub: lazyTpl(14, 'T14SleepSprayHub'), NaturalSleep: lazyFrom(() => import('./tpl14Natural.jsx'), 'T14NaturalSleep'), SleepAid: lazyFrom(() => import('./tpl14Natural.jsx'), 'T14SleepAid'), SleepDeprivation: lazyFrom(() => import('./tpl14Natural.jsx'), 'T14SleepDeprivation'), Grogginess: lazyFrom(() => import('./tpl14Natural.jsx'), 'T14Grogginess'), Adaptogens: lazyFrom(() => import('./tpl14Natural.jsx'), 'T14Adaptogens'), StressRelief: lazyFrom(() => import('./tpl14Natural.jsx'), 'T14StressRelief'), OralSpray: lazyFrom(() => import('./tpl14Natural.jsx'), 'T14OralSpray'), VeganSpray: lazyFrom(() => import('./tpl14Natural.jsx'), 'T14VeganSpray'), PillowSpray: lazyFrom(() => import('./tpl14Natural.jsx'), 'T14PillowSpray'), BestSpray: lazyFrom(() => import('./tpl14Natural.jsx'), 'T14BestSpray'), MelatoninSpray: lazyFrom(() => import('./tpl14Natural.jsx'), 'T14MelatoninSpray') },
  15: { Shell: lazyTpl(15, 'T15Shell'), Home: lazyTpl(15, 'T15Home'), Listing: lazyTpl(15, 'T15Listing'), Product: lazyTpl(15, 'T15Product'), Legal: lazyTpl(15, 'T15Legal') },
  16: { Shell: lazyTpl(16, 'T16Shell'), Home: lazyTpl(16, 'T16Home'), Listing: lazyTpl(16, 'T16Listing'), Product: lazyTpl(16, 'T16Product'), SecondPage: lazyTpl(16, 'T16SecondPage'), Legal: lazyTpl(16, 'T16Legal') },
  17: { Shell: lazyTpl(17, 'T17Shell'), Home: lazyTpl(17, 'T17Home'), Listing: lazyTpl(17, 'T17Listing'), Product: lazyTpl(14, 'T14Product') },
  18: { Shell: lazyTpl(18, 'T18Shell'), Home: lazyTpl(18, 'T18Home'), Listing: lazyTpl(18, 'T18Listing'), Product: lazyTpl(18, 'T18Product') },
  };
  return KITS_CACHE[tid];
};

// The guest storefront payload the server inlined into the head
// (window.__AREGO_STOREFRONT__, seorender.go) — the same bytes the public GET
// answers with. Used only when this visit would have made exactly that
// request: no session token (a member sees a different catalogue) and no
// pay-link token. Read once; the object is then dropped so a later store
// switch in the same document cannot pick up stale data.
const takeInlineStorefront = (slug) => {
  try {
    const inline = window.__AREGO_STOREFRONT__;
    if (!inline || inline.marketplace?.slug !== slug) return null;
    if (GetToken()?.token || new URLSearchParams(window.location.search).get('pr')) return null;
    delete window.__AREGO_STOREFRONT__;
    return inline;
  } catch { return null; }
};

// An open store tab kept the store as it was when the page loaded, so an
// edit made meanwhile (prices, texts, products) showed only after a full
// reload. Coming back to the tab after a few minutes away reads the store
// again, quietly: an unchanged answer changes nothing. Not on checkout,
// sign-up, member-portal or payment-link pages, in the visual editor, or
// while the visitor has typed something there. One request per store
// however many components use the hook.
const STOREFRONT_REFRESH_AFTER_MS = 3 * 60_000;
const storefrontRefreshes = new Map(); // slug → { at, promise }

const fetchStorefrontAgain = (slug) => {
  const recent = storefrontRefreshes.get(slug);
  if (recent && Date.now() - recent.at < 10_000) return recent.promise;
  const base = GetToken()?.token ? '/api/v1/Marketplaces' : '/api/v1/public/Marketplaces';
  const promise = new Promise((resolve, reject) => {
    Get(`${base}/${encodeURIComponent(slug)}/storefront`, resolve, reject);
  });
  storefrontRefreshes.set(slug, { at: Date.now(), promise });
  return promise;
};

export const useStorefront = (slug, previewData) => {
  const [data, setData] = useState(() => previewData ?? takeInlineStorefront(slug) ?? null);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (previewData || !slug) return undefined;
    let hiddenAt = 0;
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') { hiddenAt = Date.now(); return; }
      const away = hiddenAt ? Date.now() - hiddenAt : 0;
      hiddenAt = 0;
      if (away < STOREFRONT_REFRESH_AFTER_MS) return;
      if (isProtectedPage() || pageHasUnsavedInput() || /[?&]edit=1/.test(window.location.search)) return;
      fetchStorefrontAgain(slug)
        .then((fresh) => {
          if (!fresh?.marketplace) return;
          setData((cur) => {
            if (!cur || cur.marketplace?.slug !== fresh.marketplace.slug) return cur;
            try { return JSON.stringify(cur) === JSON.stringify(fresh) ? cur : fresh; } catch { return cur; }
          });
        })
        .catch(() => { /* keep the store on screen */ });
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [slug, previewData]);
  useEffect(() => {
    // Template-preview mode renders from injected mock data — no fetch.
    if (previewData) { setData(previewData); return; }
    // Already rendered from the inlined copy: nothing to wait for.
    if (data && data.marketplace?.slug === slug) return;
    // Anonymous visitors (no token) hit the unauthenticated public route —
    // it 404s unless the marketplace turned on Public Storefront. Logged-in
    // corporate/member sessions keep using the authenticated route as before.
    const base = GetToken()?.token ? '/api/v1/Marketplaces' : '/api/v1/public/Marketplaces';
    // A corporate-issued payment link (?pr=TOKEN) must render the checkout
    // shell even on members-only stores: the public storefront GET accepts
    // the token as authorization (prToken bypass, server-side validated).
    const prToken = new URLSearchParams(window.location.search).get('pr');
    const prQs = prToken && base.includes('/public/') ? `?prToken=${encodeURIComponent(prToken)}` : '';
    Get(`${base}/${encodeURIComponent(slug)}/storefront${prQs}`, (d) => setData(d), (err) => setError(String(err)));
  }, [slug, previewData]);
  return { data, error, setData };
};

// Visual editing helpers now live in editKit.jsx (shared with tpl8/9/10).

export const useSkin = (data) => useMemo(() => {
  if (!data) return null;
  const mkt = data.marketplace ?? {};
  const t = findTemplate(mkt.template);
  // Remember the palette so the NEXT visit's loading screen can wear the
  // store's own colours — while this one is loading there is no skin to read.
  cacheStoreSkin(mkt.slug, t);
  const edits = mkt.edits ?? {};
  const texts = edits.texts ?? {};
  const images = edits.images ?? {};
  const banner = edits.topBanner ?? { show: !!t.defaultBanner, text: t.defaultBanner ?? '' };
  // Template-2 "AregoMD" design config: dummy defaults overlaid with saved
  // edits. Text slots live in edits.texts (so pre-redesign custom texts carry
  // over); list content in edits.md2; per-slot/section hiding in edits.md2.hide.
  const md2raw = edits.md2 ?? {};
  const md2hide = new Set(Array.isArray(md2raw.hide) ? md2raw.hide : []);
  const skinTexts = SKIN_TEXTS[t.id];
  const skinLists = SKIN_LISTS[t.id];
  const md2 = skinTexts ? {
    tid: t.id,
    hide: md2hide,
    hidden: (k) => md2hide.has(k),
    text: (k) => (md2hide.has(k) ? null : ((texts[k] ?? '').toString().trim() !== '' ? texts[k]
      // The brand hub's footer address follows Settings → Company unless the
      // store typed its own.
      : (k === 'footerAddress' && data?.company?.address) ? data.company.address : skinTexts[k])),
    lists: Object.fromEntries(Object.keys(skinLists).map((k) => [k, Array.isArray(md2raw[k]) ? md2raw[k] : skinLists[k]])),
    hiwImage: images.hiw,
    // Template 12 (AregoRX) image blocks — wired for upload via EditableImage
    // but need the saved URL forwarded here to actually render (see tpl12.jsx).
    sciImage: images.sciImage,
    ltLabImage: images.ltLabImage,
    ltWhereImage: images.ltWhereImage,
    tstCoaImage: images.tstCoaImage,
    pepReconImage: images.pepReconImage,
    pepStorageImage: images.pepStorageImage,
  } : null;
  return {
    t, mkt, edits, md2,
    // Raw edit maps — the HRT pages resolve their slots (hrt_* keys) from
    // these directly; without them every saved edit is invisible to the page.
    texts, images,
    products: data.products ?? [],
    gateway: data.gateway ?? {},
    // ISO2 list of countries the store's shipping zones cover — null/absent
    // means no restriction (catch-all zone, HIPAA, or an older API). Country
    // pickers filter on this; the quote's shippingOk stays the enforcement.
    shippingCountries: Array.isArray(data.shippingCountries) && data.shippingCountries.length > 0 ? data.shippingCountries : null,
    // "required" | "optional" — whether guest checkout insists on a phone.
    // Resolved by the API from the global Advance Setting and the store's
    // override; an older API sending nothing means required, which is the
    // default and the safer way to be wrong.
    checkoutPhone: data.checkoutPhone === 'optional' ? 'optional' : 'required',
    // Brevo onsite-tracker client key — present only when this store's
    // Brevo integration is enabled (see writeStorefrontResponse).
    brevoMaKey: data.brevoMaKey ?? '',
    isHipaa: mkt.storeType === 'hipaa',
    banner,
    badges: edits.badges ?? {},
    // Proven Seal (The New Deal): products switched to "Graduated" in the
    // admin (edits.productInfo[pid].graduated) and graduated brand blocks
    // (edits.provenBrands). Never on the AregoMD (2) / AregoRX (12) stores.
    ...(() => {
      const sealOk = mkt.storeType !== 'hipaa' && !['2', '12'].includes(String(t.id));
      const pi = edits.productInfo ?? {};
      return {
        graduated: new Set(sealOk ? Object.keys(pi).filter((pid) => pi[pid]?.graduated === true) : []),
        provenBrands: sealOk ? (edits.provenBrands ?? {}) : {},
      };
    })(),
    logo: images.logo,
    // White/light version for dark headers; falls back to the main logo.
    logoDark: images.logoDark || images.logo,
    // Square icon for the browser tab and search results; falls back to the logo.
    icon: images.icon || images.logo,
    heroImage: images.hero,
    heroTitle: texts.heroTitle || t.defaultHero,
    heroSubtitle: texts.heroSubtitle || (mkt.storeType === 'hipaa' ? 'Licensed providers review every treatment plan.' : 'Curated products, delivered to your door.'),
    ctaLabel: texts.ctaLabel || (mkt.storeType === 'hipaa' ? 'View treatments' : 'Start shopping'),
    listingTitle: texts.listingTitle || (mkt.storeType === 'hipaa' ? 'Treatments' : 'Shop all'),
    footerNote: texts.footerNote || '',
    cartIcon: texts.cartIcon || DEFAULT_CART_ICON,
    cartCheckoutLabel: texts.cartCheckoutLabel || 'Go to checkout',
  };
}, [data]);

const Placeholder = ({ skin, style }) => (
  <div style={{
    background: `repeating-linear-gradient(45deg, ${skin.t.dark ? '#1a1d22' : '#eceae6'}, ${skin.t.dark ? '#1a1d22' : '#eceae6'} 8px, ${skin.t.dark ? '#22262c' : '#e3e0da'} 8px, ${skin.t.dark ? '#22262c' : '#e3e0da'} 16px)`,
    ...style,
  }}></div>
);

export const Shell = ({ skin, slug, edit, children, page }) => {
  const t = skin.t;
  const qs = edit ? '?edit=1' : '';
  const [menuOpen, setMenuOpen] = useState(false);
  const headerColor = t.darkHeader || t.dark ? '#9aa5b4' : t.muted;
  const logoEl = <a href={storeUrl(slug, '') + qs} style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 8 }}>
    <EditableImage edit={edit} k="logo" label="logo" inline>
      {skin.logo
        ? <img src={skin.logo} alt="logo" style={{ height: 30 }} />
        : <span style={{ fontSize: 18, fontWeight: 700, color: t.darkHeader || t.dark ? '#fff' : t.ink, letterSpacing: '.02em' }}>{t.brand[0]}<span style={{ color: t.accent }}>{t.brand[1]}</span></span>}
    </EditableImage>
  </a>;
  const ctaEl = <a href={storeUrl(slug, 'shop') + qs} style={{ fontSize: 14, color: t.darkHeader || t.dark ? '#9aa5b4' : t.muted, textDecoration: 'none' }}>{skin.isHipaa ? 'Treatments' : 'Shop'}</a>;
  return <div style={{ minHeight: '100vh', background: t.bg, color: t.dark ? '#fff' : t.ink, fontFamily: 'inherit', paddingBottom: edit ? 54 : 0 }}>
    <style>{MOBILE_NAV_CSS}</style>
    <style>{customCssFor(skin.mkt, page)}</style>
    {skin.banner?.show && (skin.banner?.text || edit) && (
      <div style={{ background: t.accent, color: '#fff', textAlign: 'center', fontSize: 13, fontWeight: 600, padding: '7px 12px', position: 'relative' }}>
        <EditableText edit={edit ? { setText: (k, v) => edit.setBanner({ ...skin.banner, text: v }) } : null} k="bannerText" value={skin.banner.text} />
        {edit && <span onClick={() => edit.setBanner({ ...skin.banner, show: false })}
          style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: '#fff', color: t.accent, borderRadius: 99, fontSize: 11, fontWeight: 700, padding: '2px 10px', cursor: 'pointer' }}>Hide banner ×</span>}
      </div>
    )}
    {edit && !skin.banner?.show && (
      <div onClick={() => edit.setBanner({ show: true, text: skin.banner?.text || t.defaultBanner || 'Free shipping this week only' })}
        style={{ border: EDIT_OUTLINE, textAlign: 'center', fontSize: 12, fontWeight: 600, padding: '6px 12px', color: '#f59f00', cursor: 'pointer' }}>
        Top banner is hidden — click to show it
      </div>
    )}
    <header className="mkt-header-desktop" style={{
      justifyContent: 'space-between', alignItems: 'center', padding: '14px 22px',
      background: t.darkHeader ? t.ink : (t.dark ? t.bg : '#fff'),
      borderBottom: t.dark ? '1px solid rgba(255,255,255,.08)' : '1px solid rgba(0,0,0,.06)',
    }}>
      {logoEl}
      <span style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        {ctaEl}
        <HeaderMenuLinks links={skin.mkt.headerMenu} linkStyle={{ fontSize: 14, color: t.darkHeader || t.dark ? '#9aa5b4' : t.muted, textDecoration: 'none' }} />
        <EnrollHeaderButton skin={skin} slug={slug} edit={edit} accent={t.accent} />
        <AuthHeaderButton skin={skin} edit={edit} color={headerColor} />
        <CartIcon skin={skin} edit={edit} />
      </span>
    </header>
    <header className="mkt-header-mobile" style={{
      justifyContent: 'space-between', alignItems: 'center', padding: '14px 22px',
      background: t.darkHeader ? t.ink : (t.dark ? t.bg : '#fff'),
      borderBottom: t.dark ? '1px solid rgba(255,255,255,.08)' : '1px solid rgba(0,0,0,.06)',
    }}>
      {logoEl}
      <MobileMenuIcon skin={skin} edit={edit} onClick={() => setMenuOpen(true)} color={t.darkHeader || t.dark ? '#fff' : t.ink} />
    </header>
    <MobileDrawer skin={skin} edit={edit} open={menuOpen} onClose={() => setMenuOpen(false)} menuLinks={skin.mkt.headerMenu} ctaSlot={ctaEl} />
    {children}
    {(skin.footerNote || edit) && <div style={{ textAlign: 'center', padding: 18, fontSize: 12, color: t.muted }}>
      <EditableText edit={edit} k="footerNote" value={skin.footerNote || (edit ? 'Footer note — click to edit' : '')} />
    </div>}
    {edit && <div style={{
      position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 100, background: '#1d273b', color: '#fff',
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 18, padding: '11px 16px', fontSize: 13,
    }}>
      <span>🖉 <b>Visual editing</b> — click any dashed element to edit it. Changes save automatically.</span>
      <span style={{ color: edit.saving ? '#f59f00' : '#7fe0a7', fontWeight: 700, minWidth: 64 }}>{edit.saving ? 'Saving…' : (edit.flash || 'Saved ✓')}</span>
      <a href={`/marketplace/manage/${skin.mkt.id}`} style={{ color: '#9ec1f7', fontWeight: 600 }}>Exit editing</a>
    </div>}
  </div>;
};

// ── Landing ────────────────────────────────────────────────────────────────
const Landing = ({ skin, slug, edit }) => {
  const t = skin.t;
  const qs = edit ? '?edit=1' : '';
  return <div style={{ maxWidth: 960, margin: '0 auto', padding: '40px 22px', textAlign: 'center' }}>
    {skin.isHipaa && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 700, color: t.accent, background: t.dark ? 'rgba(255,255,255,.08)' : '#fff', border: `1px solid ${t.accent}33`, borderRadius: 99, padding: '4px 12px' }}>🛡 HIPAA COMPLIANT</span>}
    <h1 style={{ fontSize: 40, fontWeight: 700, letterSpacing: '-0.5px', lineHeight: 1.15, margin: '18px 0 10px', color: t.dark ? '#fff' : t.ink }}>
      <EditableText edit={edit} k="heroTitle" value={skin.heroTitle} />
    </h1>
    <p style={{ fontSize: 16, color: t.muted, maxWidth: 520, margin: '0 auto' }}>
      <EditableText edit={edit} k="heroSubtitle" value={skin.heroSubtitle} />
    </p>
    <a href={edit ? undefined : `/marketplace/${slug}/shop${qs}`} style={{ display: 'inline-block', marginTop: 22, fontSize: 15, fontWeight: 600, background: t.accent, color: '#fff', borderRadius: 99, padding: '12px 30px', textDecoration: 'none' }}>
      <EditableText edit={edit} k="ctaLabel" value={skin.ctaLabel} />
    </a>
    <div style={{ marginTop: 34 }}>
      <EditableImage edit={edit} k="hero" label="hero image">
        <span style={{ display: 'block', borderRadius: 16, overflow: 'hidden', height: 300 }}>
          {skin.heroImage ? <img src={skin.heroImage} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <Placeholder skin={skin} style={{ width: '100%', height: '100%' }} />}
        </span>
      </EditableImage>
    </div>
    <div style={{ display: 'flex', justifyContent: 'center', gap: 30, marginTop: 22, fontSize: 13, color: t.muted, flexWrap: 'wrap' }}>
      {(skin.isHipaa ? ['🩺 Licensed MDs', '🔒 Private & secure', '📦 Discreet shipping'] : ['🌱 Clean sourcing', '🚚 Fast shipping', '↩ Easy returns']).map((x) => <span key={x}>{x}</span>)}
    </div>
  </div>;
};

// ── Listing ────────────────────────────────────────────────────────────────
const Listing = ({ skin, slug, edit }) => {
  const t = skin.t;
  const grid = GRID_TEMPLATES.has(t.id);
  const qs = edit ? '?edit=1' : '';
  const editBadge = (e, pid, current) => {
    e.preventDefault();
    e.stopPropagation();
    const v = window.prompt('Badge text (leave empty to remove the badge):', current ?? '');
    if (v === null) return;
    edit.setBadge(pid, v.trim());
  };
  return <div style={{ maxWidth: 960, margin: '0 auto', padding: '30px 22px' }}>
    <h2 style={{ fontSize: 24, fontWeight: 700, color: t.dark ? '#fff' : t.ink }}>
      <EditableText edit={edit} k="listingTitle" value={skin.listingTitle} />
    </h2>
    {skin.isHipaa && <div style={{ fontSize: 13, color: t.muted, marginBottom: 10 }}>Provider consult included with every plan</div>}
    <div style={grid
      ? { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16, marginTop: 16 }
      : { display: 'flex', flexDirection: 'column', gap: 12, marginTop: 16 }}>
      {skin.products.filter((p) => !isEnrollmentOnlyProduct(skin, p.id)).map((p) => {
        const badge = skin.badges[p.id];
        const card = {
          background: t.dark ? t.card : '#fff',
          border: t.dark ? '1px solid rgba(255,255,255,.08)' : '1px solid rgba(0,0,0,.08)',
          borderRadius: 12, textDecoration: 'none', color: 'inherit', overflow: 'hidden', position: 'relative',
        };
        const img = p.imageUrl
          ? <img src={p.imageUrl} alt="" style={{ width: grid ? '100%' : 84, height: grid ? 150 : 84, objectFit: 'contain', flex: 'none' }} />
          : <Placeholder skin={skin} style={{ width: grid ? '100%' : 84, height: grid ? 150 : 84, flex: 'none' }} />;
        return <a key={p.id} href={`/marketplace/${slug}/product/${encodeURIComponent(p.id)}${qs}`}
          style={grid ? card : { ...card, display: 'flex', alignItems: 'center', gap: 14, padding: 10 }}>
          {badge && <span onClick={edit ? (e) => editBadge(e, p.id, badge) : undefined} style={{ position: 'absolute', top: 8, left: 8, zIndex: 2, fontSize: 10, fontWeight: 700, background: '#d97706', color: '#fff', borderRadius: 4, padding: '2px 8px', cursor: edit ? 'pointer' : 'default', outline: edit ? EDIT_OUTLINE : 'none', outlineOffset: 2 }}>{badge}</span>}
          {edit && !badge && <span onClick={(e) => editBadge(e, p.id, '')} style={{ position: 'absolute', top: 8, left: 8, zIndex: 2, fontSize: 10, fontWeight: 700, border: EDIT_OUTLINE, color: '#f59f00', borderRadius: 4, padding: '2px 8px', cursor: 'pointer', background: t.dark ? 'rgba(0,0,0,.4)' : 'rgba(255,255,255,.8)' }}>+ badge</span>}
          {img}
          {grid && <ProvenSeal skin={skin} pid={p.id} size={44} />}
          <div style={{ padding: grid ? 12 : 0, flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: t.dark ? '#fff' : t.ink }}>{p.name}</div>
            <div style={{ fontSize: 13, color: t.dark ? t.accent : t.muted, marginTop: 4, fontWeight: t.dark ? 600 : 400 }}>{skin.isHipaa ? `from ${money(p.price)}` : money(p.price)}</div>
            {Array.isArray(p.doses) && p.doses.length > 0 && (
              <span style={{ display: 'block', fontSize: 12, color: t.accent, fontWeight: 600, marginTop: 3 }}>
                {p.doses.length} dosage{p.doses.length === 1 ? '' : 's'} available
              </span>
            )}
          </div>
          {!grid && <span style={{ fontSize: 13, fontWeight: 600, color: t.accent, border: `1px solid ${t.accent}55`, borderRadius: 99, padding: '5px 14px', marginRight: 8, flex: 'none' }}>{skin.isHipaa ? 'Learn' : 'View'}</span>}
        </a>;
      })}
    </div>
    {skin.isHipaa && <div style={{ textAlign: 'center', fontSize: 11, color: t.muted, marginTop: 22 }}>Prescription treatments require provider approval</div>}
  </div>;
};

// ── Product ────────────────────────────────────────────────────────────────
const Product = ({ skin, slug }) => {
  const { productId } = useParams();
  const [selectedDose, setSelectedDose] = useState(0);
  const cart = useCart();
  const t = skin.t;
  const p = skin.products.find((x) => String(x.id) === String(productId));
  const subsOn = skin.mkt.subscriptionsEnabled === true && !skin.isHipaa;
  if (!p) return <div style={{ padding: 40, textAlign: 'center', color: t.muted }}>Product not found. <a href={storeUrl(slug, 'shop')}>Back to shop</a></div>;
  const badge = skin.badges[p.id];
  // Doses arrive server-sorted weakest → strongest, each with the price of
  // its own linked inventory product (parent price when unlinked).
  const doses = (Array.isArray(p.doses) ? p.doses : []).map((d) => (typeof d === 'string' ? { label: d } : d));
  const activeDose = doses[selectedDose] ?? doses[0];
  const dosageBlock = doses.length > 0 && (
    <div style={{ marginTop: 14 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: t.dark ? '#fff' : t.ink, letterSpacing: '.04em', marginBottom: 7 }}>AVAILABLE DOSAGES</div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {doses.map((d, i) => (
          <span key={i} onClick={() => setSelectedDose(i)} style={i === selectedDose
            ? { fontSize: 13, fontWeight: 700, border: `1.5px solid ${t.accent}`, background: t.dark ? 'rgba(255,255,255,.08)' : `${t.accent}18`, color: t.dark ? '#fff' : t.ink, borderRadius: 99, padding: '6px 15px', cursor: 'pointer' }
            : { fontSize: 13, fontWeight: 600, border: t.dark ? '1px solid rgba(255,255,255,.2)' : '1px solid rgba(0,0,0,.12)', color: t.muted, borderRadius: 99, padding: '6px 15px', cursor: 'pointer' }}>
            {d.label}
          </span>
        ))}
      </div>
      {activeDose?.price != null && (
        <div style={{ fontSize: 13, fontWeight: 600, color: t.dark ? '#fff' : t.ink, marginTop: 8 }}>
          {activeDose.label} — {money(activeDose.price)}
        </div>
      )}
      <div style={{ fontSize: 11, color: t.muted, marginTop: 5 }}>Final dosage confirmed by your provider</div>
    </div>
  );
  return <div style={{ maxWidth: 760, margin: '0 auto', padding: '30px 22px' }}>
    <a href={`/marketplace/${slug}/shop`} style={{ fontSize: 13, color: t.muted, textDecoration: 'none' }}>‹ Back</a>
    <div style={{ borderRadius: 14, overflow: 'hidden', height: 280, marginTop: 12, position: 'relative' }}>
      {badge && <span style={{ position: 'absolute', top: 10, left: 10, fontSize: 11, fontWeight: 700, background: '#d97706', color: '#fff', borderRadius: 4, padding: '3px 9px' }}>{badge}</span>}
      {p.imageUrl ? <img src={p.imageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} /> : <Placeholder skin={skin} style={{ width: '100%', height: '100%' }} />}
      <ProvenSeal skin={skin} pid={p.id} size={60} />
    </div>
    <h1 style={{ fontSize: 26, fontWeight: 700, marginTop: 18, color: t.dark ? '#fff' : t.ink }}>{p.name}</h1>
    {p.description && <p style={{ fontSize: 14, color: t.muted, marginTop: 6 }}>{String(p.description).replace(/<[^>]+>/g, '').slice(0, 400)}</p>}

    {skin.isHipaa && <>
      {dosageBlock}
      <div style={{ background: t.dark ? 'rgba(255,255,255,.06)' : '#fff', border: `1px solid ${t.accent}33`, borderRadius: 12, padding: '14px 16px', marginTop: 16, display: 'flex', gap: 10, fontSize: 13, color: t.dark ? '#c7cdd4' : t.ink }}>
        <span>🛡</span>
        <span>Checkout &amp; medical intake happen in our <b>secure patient portal</b> — your health data never touches this site.</span>
      </div>
      <a href={`/marketplace/${slug}/portal/${encodeURIComponent(p.id)}`} style={{ display: 'block', textAlign: 'center', marginTop: 16, fontSize: 15, fontWeight: 600, background: t.accent, color: '#fff', borderRadius: 10, padding: 13, textDecoration: 'none' }}>Get Started →</a>
      <div style={{ textAlign: 'center', fontSize: 11, color: t.muted, marginTop: 8 }}>🔒 Continues in the HIPAA-secure portal</div>
    </>}

    {!skin.isHipaa && <>
      {dosageBlock}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 16 }}>
        {subsOn && <a onClick={() => cart.addAndGo(p.id, 1, true)} style={{ border: `2px solid ${t.accent}`, background: t.dark ? 'rgba(255,255,255,.05)' : `${t.accent}11`, borderRadius: 12, padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', textDecoration: 'none', cursor: 'pointer' }}>
          <span>
            <span style={{ display: 'block', fontSize: 14, fontWeight: 700, color: t.dark ? '#fff' : t.accent }}>Subscribe</span>
            <span style={{ fontSize: 12, color: t.muted }}>Ships every {skin.mkt.subscriptionIntervalDays ?? 30} days · cancel anytime</span>
          </span>
          <span style={{ fontSize: 16, fontWeight: 700, color: t.dark ? t.accent : t.ink }}>{money(p.price)}</span>
        </a>}
        <a onClick={() => cart.addAndGo(p.id, 1, false)} style={{ border: t.dark ? '1px solid rgba(255,255,255,.2)' : '1px solid rgba(0,0,0,.15)', borderRadius: 12, padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', textDecoration: 'none', cursor: 'pointer' }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: t.dark ? '#c7cdd4' : t.muted }}>Add to cart</span>
          <span style={{ fontSize: 16, fontWeight: 600, color: t.dark ? '#fff' : t.ink }}>{money(p.price)}</span>
        </a>
      </div>
      <a onClick={() => cart.addAndGo(p.id, 1, false)} style={{ display: 'block', textAlign: 'center', marginTop: 14, fontSize: 15, fontWeight: 600, background: t.dark ? t.accent : t.ink, color: t.dark ? t.ink : '#fff', borderRadius: 99, padding: 13, textDecoration: 'none', cursor: 'pointer' }}>Add to cart</a>
    </>}
  </div>;
};

// ── HIPAA portal (iframe) ──────────────────────────────────────────────────
// The Wizlo handshake, customer load and coupon resolution live in wizloKit so
// this and the HRT portal share one implementation.
const Portal = ({ skin, slug }) => {
  const { productId } = useParams();
  const t = skin.t;
  const url = skin.mkt.hipaaLinks?.[productId];
  const { customer, customerLoaded } = useWizloCustomer();
  const coupon = useWizloCoupon(slug);
  const src = url ?? null;
  // Autofill is on unless the corporate "Advance Settings" toggle turned it off.
  const autofillEnabled = skin.mkt.portalAutofill !== false;
  const { iframeRef, frameHeight } = useWizloFrame({
    url, customer, autofillEnabled, couponCode: coupon?.code,
  });

  return <div style={{ maxWidth: 960, margin: '0 auto', padding: '18px 22px' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
      <span style={{ fontSize: 12, fontWeight: 700, color: t.accent }}>🔒 SECURE SESSION</span>
      <a href={`/marketplace/${slug}`} style={{ fontSize: 12, color: t.muted }}>Return to homepage</a>
    </div>
    <WizloCouponBanner coupon={coupon} accent={t.accent} />
    {url && !customerLoaded && <div style={{ padding: 60, textAlign: 'center', color: t.muted }}>Preparing your secure session…</div>}
    {url && customerLoaded
      && <iframe ref={iframeRef} src={src} title="Secure portal" allow="payment" style={{ width: '100%', height: frameHeight ? `${frameHeight}px` : '78vh', border: `2px solid ${t.accent}44`, borderRadius: 14, background: '#fff' }} />}
    {!url && <div style={{ padding: 60, textAlign: 'center', color: t.muted }}>This product&apos;s secure portal link is not configured yet.</div>}
    <div style={{ textAlign: 'center', fontSize: 11, color: t.muted, marginTop: 8 }}>HIPAA-compliant intake &amp; payment portal — your progress is saved</div>
  </div>;
};

// ── Checkout ───────────────────────────────────────────────────────────────
// loadAcceptJs/dispatchAcceptData live in editKit.jsx — shared with the new
// guest checkout so card tokenization is implemented exactly once.

const Checkout = (props) => {
  // Corporate-created payment request: locked, prefilled, token-authorized.
  // Checked in a hook-free wrapper so the branch can never break hook order.
  const prToken = new URLSearchParams(window.location.search).get('pr');
  if (prToken) return <PaymentRequestCheckout skin={props.skin} slug={props.slug} token={prToken} />;
  return <CheckoutInner {...props} />;
};

// eslint-disable-next-line react/prop-types
const CheckoutInner = ({ skin, slug }) => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // The palette comes from where the cart was filled, not only from the
  // template: a shopper arriving off the cream sleep-spray lander should not
  // land on the dark template checkout. checkoutTheme returns the same token
  // shape, so this one line re-skins every reader of `t` below.
  const t = checkoutTheme(skin, slug);
  const ckVariant = checkoutVariant(slug, skin);
  const cart = useCart();
  const scope = GetScope();

  const [customer, setCustomer] = useState(null);
  // GA4 identity: a stable customer id (never PII) + type, so reports can
  // be sliced by member/customer/affiliate vs guest. Guests stay "guest".
  useEffect(() => {
    const typeId = String(customer?.customerType?.id ?? customer?.customerType ?? '');
    const typeName = { '1': 'member', '2': 'customer', '3': 'affiliate' }[typeId] || (customer ? 'member' : 'guest');
    setTrackingIdentity({ customerId: customer ? (customer.id ?? scope) : '', customerType: customer ? typeName : 'guest' });
    // Brevo is the exception to the "never PII" rule above, and deliberately
    // so: it is an email tool, the address IS the identifier, and without it
    // a signed-in member who abandons a cart cannot be followed up. GA4 still
    // only ever receives the opaque id.
    if (customer?.emailAddress) identifyEmail(customer.emailAddress);
  }, [customer?.id, customer?.customerType, customer?.emailAddress]); // eslint-disable-line react-hooks/exhaustive-deps
  // A captured sponsor's coupon pre-applies; the quote round-trip validates
  // it server-side and the visitor can always clear the field.
  const captured = getSponsor(slug);
  // A sponsor's coupon wins over a wheel prize: it carries commission and the
  // shopper arrived through that member's link. Either way the quote
  // round-trip validates it and the field can always be cleared.
  const sponsorCoupon = captured?.couponCode || wheelCoupon(slug) || scratchCoupon(slug) || '';
  const [coupon, setCoupon] = useState(sponsorCoupon);
  const [appliedCoupon, setAppliedCoupon] = useState(sponsorCoupon);
  // The guest's sponsor lives HERE (not inside GuestCheckout) so the quote
  // can carry it — the Sponsor Coupon Restriction runs server-side and its
  // messages come back on the quote. Members never send a sponsor.
  const [guestSponsorId, setGuestSponsorId] = useState(!scope && captured?.id ? captured.id : '');
  const [guestSponsorLabel, setGuestSponsorLabel] = useState(
    !scope && captured?.id ? `${captured.fullName}${captured.webAlias ? ` (${captured.webAlias})` : ''}` : '');
  // Same reasoning as guestSponsorId: lives here (not inside GuestCheckout)
  // so the shared quote effect below can carry it — a guest has no saved
  // address for quoteAddress.countryCode to come from, so without this the
  // quote (and therefore shipping-zone matching) always defaulted to no
  // country at all for every guest, regardless of what they'd eventually
  // type at checkout.
  const [guestCountry, setGuestCountry] = useState('US');
  // Snap the seed pick to somewhere the store can actually ship (e.g. a
  // UK-only store must not open on 'US'), so the very first quote — and the
  // guest's country dropdown — start from a shippable country.
  useEffect(() => {
    const list = skin.shippingCountries;
    if (!scope && Array.isArray(list) && list.length > 0 && !list.includes(guestCountry)) setGuestCountry(list[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skin.shippingCountries, scope]);
  const [pointsToUse, setPointsToUse] = useState('');
  const [quote, setQuote] = useState(null);
  // Which shipping method the customer picked, when the matched zone offers
  // more than one (see quote.shippingOptions) — empty means "use whatever
  // the backend defaults to" (the zone's first available option), so a zone
  // with only one method needs no picker and nothing changes for it.
  const [shippingMethodId, setShippingMethodId] = useState('');
  // Recipient phone for MyUS-forwarded (international) orders — prefilled
  // from the customer record, editable, required when the quote is MyUS.
  const [phone, setPhone] = useState('');
  const [phoneCountry, setPhoneCountry] = useState('US');
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [subQuotes, setSubQuotes] = useState([]); // [{interval, quote}] — one recurring group per distinct recurrence
  const [card, setCard] = useState({ number: '', expiry: '', code: '' });
  const nmiRef = useRef(null); // NMI (Collect.js) card fields, when the store's gateway is NMI
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState(null);
  const [seeded, setSeeded] = useState(false);

  // Old deep links (/checkout/{productId}?subscribe=1) seed the cart once
  // and then behave exactly like the plain cart checkout.
  useEffect(() => {
    if (productId) {
      const subscribe = new URLSearchParams(location.search).get('subscribe') === '1';
      const line = cart.items.find((i) => i.productId === String(productId) && i.subscribe === subscribe);
      if (!line) cart.add(productId, 1, subscribe, { silent: true });
    }
    setSeeded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Resolve cart lines against the storefront's product list. Enrollment-only
  // products (kit / First Order / Subscribe & Save) are excluded even if a
  // stale/contaminated cart somehow has one — this checkout creates a
  // Registered Customer, never a Member, so it must never bill one.
  const rows = cart.items
    .map((i) => ({ ...i, p: skin.products.find((x) => String(x.id) === i.productId) }))
    .filter((i) => i.p && !isEnrollmentOnlyProduct(skin, i.productId));
  const hasSub = rows.some((i) => i.subscribe);
  const ls = readLowStock(skin.mkt);
  const lsMap = lowStockMap(ls);
  const lsRows = ls.enabled && ls.checkoutPage ? firstOfProduct(rows) : new Set();
  // The sub-line inside the pay button. Armed by the campaign alone so the row
  // is reserved and the button never changes height; whether it SAYS anything
  // is decided at the button, against that button's own disabled expression.
  const cta = readCtaBadge(skin.mkt);
  const ctaOn = cta.enabled && cta.checkoutButton && !skin.isHipaa;
  const ctaPct = ctaOn ? cartDiscountPct(rows) : 0;
  // A product's own "Overwrite recurring rule" (intervalDays from the
  // storefront payload) wins over the marketplace default.
  const rowInterval = (i) => (Number(i.p?.intervalDays) > 0 ? Number(i.p.intervalDays) : (Number(skin.mkt.subscriptionIntervalDays) || 30));
  const itemsKey = rows.map((i) => `${i.productId}:${i.quantity}:${i.subscribe ? 1 : 0}`).join('|');
  // A one-time order fully covered by redeemed Arego Points needs no card at
  // all — a subscription always does, since every renewal after this one
  // bills the saved card regardless of what covered the first charge.
  const zeroDue = !hasSub && quote?.total === 0;

  // Logged-in customer's saved address, in scope early so the quote effect
  // below can send its country and re-quote once the address arrives —
  // guests don't have this yet at quote time; their country reaches shipping
  // selection only at final checkout (server backfills quoteReq.Country from
  // checkoutReq.Customer.Country there), so the cart preview may show a
  // domestic-zone estimate until they submit.
  const quoteAddress = (customer?.addresses ?? []).find((a) => String(a.type ?? '').toLowerCase() === 'billing') ?? (customer?.addresses ?? [])[0] ?? {};

  useEffect(() => {
    if (!seeded) return;
    if (rows.length === 0) { setQuote(null); setSubQuotes([]); return; }
    // Points only ever apply to the interactive total due today, never to
    // the recurring baseline (subQuote) — a rebill always charges the full
    // renewal amount off the saved card.
    const payload = (list, includePoints) => ({
      // subscribe rides along so the quote prices subscription items at their
      // subscription price — display now matches what checkout charges.
      items: list.map((i) => ({ productId: i.productId, quantity: Number(i.quantity) || 1, subscribe: !!i.subscribe })),
      couponCode: appliedCoupon,
      customerId: scope ?? '',
      sponsorId: scope ? '' : guestSponsorId,
      pointsToRedeem: includePoints ? (Number(pointsToUse) || 0) : 0,
      // Subscription groups ask for the renewal price: a one-time coupon
      // is left off (it only discounts today's order).
      recurring: !includePoints,
      // Drives which shipping zone quotes the order (see PickShippingZone) —
      // a logged-in member's saved address, or the guest's own country
      // picker (guestCountry) since they have no saved address yet.
      country: scope ? (quoteAddress.countryCode ?? '') : guestCountry,
      // Which of the matched zone's methods to use, when it offers more
      // than one — empty defaults to the zone's first available option.
      shippingMethodId,
    });
    // Anonymous visitors (no token) use the public quote route — it degrades
    // to "corporate preview" pricing (customerId="") exactly like the public
    // storefront GET, and works for both signed-in-member and guest checkout.
    const quoteBase = GetToken()?.token ? '/api/v1/Marketplaces' : '/api/v1/public/Marketplaces';
    SendRequest('POST', `${quoteBase}/${skin.mkt.id}/quote`, payload(rows, true), (q) => {
      setQuote(q);
      // A previously-picked carrier can stop being an option (country
      // changed, cart changed) — drop back to "use the default" instead of
      // showing a radio selected for something that's no longer offered.
      if (shippingMethodId && !(q?.shippingOptions ?? []).some((o) => o.id === shippingMethodId)) {
        setShippingMethodId('');
      }
    }, () => setQuote(null));
    const subs = rows.filter((i) => i.subscribe);
    if (subs.length > 0) {
      // Per-product "Overwrite recurring rule": one recurring group (and one
      // subscription) per distinct interval, each with its own quote.
      const groups = [...new Set(subs.map(rowInterval))].sort((a, b) => a - b)
        .map((iv) => ({ interval: iv, items: subs.filter((i) => rowInterval(i) === iv) }));
      const results = [];
      let done = 0;
      groups.forEach((g, idx) => {
        // A failed group quote must not silently erase its block — keep the
        // group with quote:null so the checkout shows a fallback line.
        const finish = () => {
          if (results[idx] === undefined) results[idx] = { interval: g.interval, quote: null };
          if (++done === groups.length) setSubQuotes(results.filter(Boolean));
        };
        SendRequest('POST', `${quoteBase}/${skin.mkt.id}/quote`, payload(g.items, false), (q) => {
          results[idx] = { interval: g.interval, quote: q };
          finish();
        }, finish);
      });
    } else {
      setSubQuotes([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seeded, itemsKey, appliedCoupon, pointsToUse, scope, guestSponsorId, quoteAddress.countryCode, guestCountry, shippingMethodId]);

  // Coupon → sponsor: a valid customer-created coupon fills its creator in
  // as the sponsor when none is chosen yet, and captures them store-wide
  // (banner + persistence) exactly like a sponsor link. Guests only.
  useEffect(() => {
    if (scope || !quote?.couponValid || !quote?.couponOwnerId || guestSponsorId) return;
    setGuestSponsorId(quote.couponOwnerId);
    setGuestSponsorLabel(quote.couponOwnerLabel || '');
    if (getSponsor(slug)?.id !== quote.couponOwnerId) {
      captureSponsorById(slug, quote.couponOwnerId, (sp) => {
        setGuestSponsorLabel(`${sp.fullName}${sp.webAlias ? ` (${sp.webAlias})` : ''}`);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quote, scope, guestSponsorId, slug]);

  useEffect(() => {
    if (!scope) return;
    const fetchCustomer = () => Get(`/api/v1/Customers/${encodeURIComponent(scope)}`, (c) => setCustomer(c), () => { });
    fetchCustomer();
    // Editing the profile (e.g. fixing a shipping country) happens on a
    // different page/tab — a plain <a href> navigation there and back can
    // restore this page from the browser's back/forward cache instead of
    // remounting it, leaving `customer` stale until a manual refresh (the
    // "I fixed it but it still says unsupported" bug). Re-fetch whenever
    // this tab becomes visible/active again, not just once on mount.
    const onPageShow = (e) => { if (e.persisted) fetchCustomer(); };
    const onVisibility = () => { if (document.visibilityState === 'visible') fetchCustomer(); };
    window.addEventListener('pageshow', onPageShow);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('pageshow', onPageShow);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [scope]);

  const perOf = (iv) => (iv === 30 ? '/ month' : `/ ${iv} days`);

  // Shipping needs a real destination — without one on file the customer
  // can review the cart but not pay (the API refuses too). Same address the
  // quote effect above uses, so the displayed quote and the final charge
  // always agree on which shipping zone applies.
  const address = quoteAddress;
  const hasAddress = !!String(address.line1 ?? '').trim();
  // Phone actually sent: what the member typed, else the first number on
  // their record. Only demanded (and shown) for MyUS-forwarded quotes.
  // Prefill from the record, split back into country + local digits so the
  // member is not shown their own number with its dial code jammed into the
  // local field. A legacy number that will not parse keeps its digits.
  const recordEntry = (customer?.phoneNumbers ?? []).find((p) => String(p?.number ?? '').trim()) ?? null;
  const recordSplit = useMemo(
    () => (recordEntry ? safeParsePhone(recordEntry.number, recordEntry.countryCode) : { country: '', national: '' }),
    [recordEntry],
  );
  useEffect(() => {
    if (phoneTouched) return;
    if (recordSplit.national) setPhone(recordSplit.national);
    if (recordSplit.country) setPhoneCountry(recordSplit.country);
  }, [recordSplit, phoneTouched]);
  const effectivePhone = toE164(phoneCountry, phone);
  // The store can demand a phone on every order; a MyUS parcel always needs one.
  const needsPhone = skin.mkt?.requireCheckoutPhone === true || !!quote?.shippingMyus;
  const missingPhone = needsPhone && !phoneIsValid(phoneCountry, phone);

  // GA4 funnel: the shipping option in play (chosen, or the single one the
  // zone offers) → add_shipping_info, once per method.
  //
  // MUST stay above the early returns below (empty cart, and the guest
  // checkout swap). Both of those return before this line, so with the hook
  // underneath them the component rendered one hook fewer on exactly the two
  // paths a real shopper takes — React error #300, which is the whole
  // checkout page crashing to the error boundary.
  useEffect(() => {
    if (!quote?.shippingOk || !(quote?.shippingMethodTitle || quote?.shippingLabel)) return;
    const snap = trackingCartSnapshot(cart, skin);
    trackAddShippingInfo(snap.items, snap.value, quote.shippingMethodTitle || quote.shippingLabel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quote?.shippingMethodId, quote?.shippingLabel]);

  // Marketing consent: unticked until the member ticks it. Declared here, above
  // the early returns, for the same hook-order reason as the effect above.
  const [emailOptIn, setEmailOptIn] = useState(false);

  // Emptied the cart here (removed the last line)? Send them back where they
  // came from — the lander or product page they were shopping — rather than
  // stranding them on an empty checkout. checkoutEntry remembers that page
  // (cart.add stamps it); the store home is the fallback. The old link was
  // also hard-coded to /marketplace/{slug}/shop, which is wrong on a store's
  // own domain.
  if (seeded && rows.length === 0) return <CheckoutEmpty slug={slug} t={t} />;

  const gatewayReady = gatewayIsReady(skin.gateway);

  const applyCoupon = () => setAppliedCoupon(coupon.trim());

  // Anonymous visitor + guest checkout on for this marketplace: swap in the
  // account-creation checkout path instead of the member-login gate below.
  // The authenticated member-checkout path (handlePay, further down) is
  // completely untouched.
  if (!scope && skin.mkt.guestCheckoutEnabled && seeded && rows.length > 0) {
    // Under Sponsor Coupon Restriction, a coupon that set the sponsor locks
    // it — the sponsor can only change once the coupon is removed.
    const sponsorLocked = skin.mkt.sponsorCouponRestriction === true &&
      !!quote?.couponValid && !!quote?.couponOwnerId && guestSponsorId === quote.couponOwnerId;
    return <GuestCheckout skin={skin} slug={slug} rows={rows} quote={quote} subQuotes={subQuotes} rowInterval={rowInterval}
      coupon={coupon} setCoupon={setCoupon} appliedCoupon={appliedCoupon} applyCoupon={applyCoupon}
      sponsorId={guestSponsorId} setSponsorId={setGuestSponsorId}
      sponsorLabel={guestSponsorLabel} setSponsorLabel={setGuestSponsorLabel} sponsorLocked={sponsorLocked}
      country={guestCountry} setCountry={setGuestCountry}
      shippingMethodId={shippingMethodId} setShippingMethodId={setShippingMethodId}
      cart={cart} navigate={navigate} />;
  }

  // walletToken: set when an Apple Pay / Google Pay sheet (NMI) completed.
  const handlePay = async (walletToken) => {
    { const snap = trackingCartSnapshot(cart, skin); trackAddPaymentInfo(snap.items, snap.value, 'card'); }
    setError(null);
    if (!scope) { setError('Please log in as a member to check out.'); return; }
    if (!gatewayReady) { setError('The payment gateway is not configured yet.'); return; }
    if (!hasAddress) { setError('A shipping address is required — add one to your profile first.'); return; }
    const finish = (opaque) => {
      SendRequest('POST', `/api/v1/Marketplaces/${skin.mkt.id}/checkout`, {
        items: rows.map((i) => ({ productId: i.productId, quantity: Number(i.quantity) || 1, subscribe: i.subscribe })),
        couponCode: appliedCoupon,
        customerId: scope,
        pointsToRedeem: Number(pointsToUse) || 0,
        country: address.countryCode ?? '',
        shippingMethodId,
        customer: {
          firstName: customer?.firstName ?? '', lastName: customer?.lastName ?? '',
          email: customer?.emailAddress ?? '',
          address: address.line1 ?? '', city: address.city ?? '',
          state: address.stateCode ?? '', zip: address.zip ?? '', country: address.countryCode ?? 'US',
          phone: effectivePhone,
          phoneCountry,
        },
        payment: opaque ? { dataDescriptor: opaque.dataDescriptor, dataValue: opaque.dataValue } : { dataDescriptor: '', dataValue: '' },
        emailOptIn,
        ...attributionFields(),
      }, (res) => {
        trackPurchase({
          orderId: res.orderId, value: quote?.total, shipping: quote?.shipping,
          coupon: quote?.couponValid ? appliedCoupon : '', checkoutType: 'member', country: address.countryCode ?? '',
          email: customer?.emailAddress, name: `${customer?.firstName ?? ''} ${customer?.lastName ?? ''}`,
          items: rows.map((i) => ({ id: i.productId, name: i.p?.name, price: i.p?.price, quantity: Number(i.quantity) || 1, variant: i.subscribe ? 'subscription' : 'one-time' })),
        });
        cart.clear();
        navigate(storeUrl(slug, `thankyou?order=${encodeURIComponent(res.orderId)}${res.subscriptionId ? '&sub=1' : ''}`));
      }, (body) => {
        // SendRequest onError delivers (responseText, status) — the real
        // reason lives in the JSON error body, never in the status code.
        setPaying(false);
        let msg = '';
        try { msg = JSON.parse(body)?.error ?? ''; } catch { msg = ''; }
        setError(msg || 'Payment failed.');
      });
    };
    if (zeroDue) {
      setPaying(true);
      finish(null);
      return;
    }
    if (isNmiGateway(skin.gateway)) {
      setPaying(true);
      try {
        finish(walletToken?.dataValue ? walletToken : await nmiRef.current.tokenize());
      } catch (e) {
        setPaying(false);
        setError(String(e.message ?? e));
      }
      return;
    }
    // "07/31" → month 07, year 2031 (Accept.js wants them separately).
    const expDigits = card.expiry.replace(/\D/g, '');
    const expMonth = expDigits.slice(0, 2);
    const expYear = expDigits.length >= 4 ? '20' + expDigits.slice(2, 4) : '';
    if (!card.number || !card.code || expDigits.length < 4) { setError('Please fill in the card details.'); return; }
    if (Number(expMonth) < 1 || Number(expMonth) > 12) { setError('The expiry month must be between 01 and 12.'); return; }
    setPaying(true);
    try {
      const Accept = await loadAcceptJs(skin.gateway.sandboxMode === true);
      const secureData = {
        authData: { clientKey: skin.gateway.publicClientKey, apiLoginID: skin.gateway.apiLoginId },
        cardData: { cardNumber: card.number.replace(/\s+/g, ''), month: expMonth, year: expYear, cardCode: card.code },
      };
      dispatchAcceptData(Accept, secureData).then((response) => {
        if (response.messages.resultCode === 'Error') {
          setPaying(false);
          setError(response.messages.message?.[0]?.text ?? 'The card could not be tokenized.');
          return;
        }
        finish(response.opaqueData);
      });
    } catch (e) {
      setPaying(false);
      setError(String(e.message ?? e));
    }
  };

  const panel = { background: t.dark ? t.card : '#fff', border: t.dark ? '1px solid rgba(255,255,255,.1)' : '1px solid rgba(0,0,0,.08)', borderRadius: 12, padding: '12px 14px' };
  const input = { background: t.dark ? '#1a1d22' : '#fff', border: t.dark ? '1px solid rgba(255,255,255,.15)' : '1px solid rgba(0,0,0,.15)', borderRadius: 8, padding: '9px 11px', color: t.dark ? '#fff' : t.ink, width: '100%', fontSize: 14 };

  // Same DOM order, two columns: each region says which column it belongs to
  // and CSS grid places it. Physically moving 200 lines of live checkout JSX
  // to get the same picture would put the money path at risk for a layout.
  const ckNatural = ckVariant === 'natural';
  const fsCfg = readFreeShip(skin.mkt);
  const destKnown = !!(scope ? quoteAddress.countryCode : guestCountry);
  const L = ckNatural ? 'ck-left' : undefined;
  const R = ckNatural ? 'ck-right' : undefined;

  const body = <div className={ckNatural ? 'ck-natural' : undefined}
    style={ckNatural ? undefined : { maxWidth: 560, margin: '0 auto', padding: '26px 22px' }}>
    {ckNatural && <style>{CHECKOUT_GRID_CSS}</style>}
    <div className={ckNatural ? 'ck-head' : undefined}>
      {ckNatural && <div className="ck-bar">
        <a href={checkoutBackUrl(slug, subForPage, storeUrl)}>&lsaquo; Continue shopping</a>
        <span style={{ fontSize: 12.5, color: '#6E685F', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"
            strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
          Encrypted payment
        </span>
      </div>}
      {ckNatural && <h1 style={{ fontSize: 30, fontWeight: 700, letterSpacing: '-.01em', margin: '0 0 14px', color: t.ink }}>Checkout</h1>}
      <CheckoutTimer mkt={skin.mkt} t={t} />
      <FreeShipBanner cfg={fsCfg} quote={quote} destinationKnown={destKnown} isHipaa={skin.isHipaa} dark={t.dark} />
      {ckNatural
        ? <p className="ck-lede">Review your order and complete payment. Your details are encrypted end to end.{skin.gateway.sandboxMode === true && <span style={{ color: '#d97706', fontWeight: 700 }}> · SANDBOX MODE</span>}</p>
        : <div style={{ fontSize: 13, color: t.muted, marginBottom: 12 }}>Secure checkout 🔒 {skin.gateway.sandboxMode === true && <span style={{ color: '#d97706', fontWeight: 700 }}>· SANDBOX MODE</span>}</div>}
    </div>

    <div className={R}>

    {rows.map((i) => <div key={i.productId + (i.subscribe ? '_s' : '')} style={{ ...panel, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
      <span style={{ width: 46, height: 46, borderRadius: 8, background: t.dark ? 'rgba(255,255,255,.06)' : '#f4f3f0', flex: 'none', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {i.p.imageUrl && <img src={i.p.imageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', background: '#fff' }} />}
      </span>
      {/* minWidth floors the name column so the qty/price group wraps to its
          own line on a phone instead of crushing the name to ~31px at 320px. */}
      <span style={{ fontSize: 14, fontWeight: 600, minWidth: 120, flex: 1 }}>
        {i.p.name}{i.subscribe && <span style={{ color: t.accent }}> · Subscription</span>}
        {i.subscribe && <span style={{ display: 'block', fontSize: 11.5, fontWeight: 400, color: t.muted }}>Renews every {rowInterval(i)} days</span>}
        {lsRows.has(i.productId + (i.subscribe ? '_s' : '')) && <span style={{ display: 'block' }}><LowStockPill
          cfg={ls} map={lsMap} productId={i.productId} qty={qtyOfProduct(rows, i.productId)}
          dark={t.dark === true} accent={t.accent} ink={t.ink} isHipaa={skin.isHipaa} /></span>}
      </span>
      <span style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 'none' }}>
        <input type="number" min="1" max={maxQtyFor(skin, i.productId) || undefined} value={i.quantity}
          onChange={(e) => cart.setQty(i.productId, i.subscribe, e.target.value)}
          style={{ ...input, width: 64, padding: '5px 6px' }} />
        {maxQtyFor(skin, i.productId) > 0 && i.quantity >= maxQtyFor(skin, i.productId)
          && <span style={{ fontSize: 11.5, color: t.muted }}>Max {maxQtyFor(skin, i.productId)}</span>}
        <SubscriptionPriceTag
          price={lineDiscountFor(i.p, i.subscribe).was * i.quantity}
          subscriptionPrice={lineDiscountFor(i.p, i.subscribe).now * i.quantity}
          style={{ fontSize: 15, fontWeight: 700 }}
        />
        <span onClick={() => cart.remove(i.productId, i.subscribe)} title="Remove"
          style={{ cursor: 'pointer', color: t.muted, fontSize: 17, lineHeight: 1 }}>×</span>
      </span>
    </div>)}

      <CheckoutDelivery mkt={skin.mkt} quote={quote} t={t} money={money} />
    </div>

    <div className={L}>
    {scope && customer && <div style={{ ...panel, display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10 }}>
      <span style={{ width: 34, height: 34, borderRadius: '50%', background: '#b06f4e', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 600, flex: 'none' }}>
        {(customer.firstName?.[0] ?? '') + (customer.lastName?.[0] ?? '')}
      </span>
      <span style={{ flex: 1, fontSize: 13, color: t.muted, lineHeight: 1.4 }}>
        <FreeShipBadge cfg={fsCfg} quote={quote} destinationKnown={destKnown} isHipaa={skin.isHipaa} />
        <b style={{ color: t.dark ? '#fff' : t.ink }}>{customer.firstName} {customer.lastName}</b> · {customer.emailAddress}<br />
        {hasAddress
          ? `${address.line1}, ${address.city ?? ''} ${address.stateCode ?? ''} ${address.zip ?? ''}${address.countryCode ? `, ${address.countryCode}` : ''}`
          : <span style={{ color: '#d63939', fontWeight: 600 }}>No shipping address on file</span>}
      </span>
      <a href={accountProfileUrl(scope)} style={{ fontSize: 12, fontWeight: 600, color: t.accent }}>Edit</a>
    </div>}
    {scope && customer && !hasAddress && <div style={{ background: '#fbe9e9', color: '#b02a2a', borderRadius: 8, padding: '9px 12px', fontSize: 13, marginBottom: 10 }}>
      A shipping address is required before you can place this order — add one via <a href={accountProfileUrl(scope)} style={{ color: '#b02a2a', fontWeight: 700, textDecoration: 'underline' }}>Edit profile</a>, then come back to checkout.
    </div>}
    {/* hasAddress only means "a street address exists" — it says nothing about
        whether that address's country is one we can actually ship to. The
        backend already refuses to charge when shippingOk is false; this just
        surfaces the same fact before the customer tries to pay, instead of
        letting them fill out payment details first and hit a rejection. */}
    {scope && customer && hasAddress && quote && quote.shippingOk === false && <div style={{ background: '#fbe9e9', color: '#b02a2a', borderRadius: 8, padding: '9px 12px', fontSize: 13, marginBottom: 10 }}>
      We can&apos;t ship to the country on file for your address ({address.countryCode || 'unknown'}) — update it via{' '}
      <a href={accountProfileUrl(scope)} style={{ color: '#b02a2a', fontWeight: 700, textDecoration: 'underline' }}>Edit profile</a> or contact us for help.
    </div>}
    {!scope && <div style={{ ...panel, marginBottom: 10, color: t.muted, fontSize: 13 }}>You are browsing as corporate — checkout requires a member login.</div>}

    </div>

    <div className={R}>
    <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
      <input style={input} placeholder="Coupon code" value={coupon} onChange={(e) => setCoupon(e.target.value)} />
      <button onClick={applyCoupon} style={{ ...input, width: 110, cursor: 'pointer', fontWeight: 600, textAlign: 'center' }}>Apply</button>
    </div>
    {quote?.couponMessage && !quote?.couponValid && appliedCoupon && <div style={{ fontSize: 12, color: '#d63939', marginBottom: 8 }}>{quote.couponMessage}</div>}
    {quote?.couponValid && <div style={{ fontSize: 12, color: '#2f9e6e', marginBottom: 8 }}>Coupon applied — {quote.discountLabel || `${quote.discountPct}% off`} products (shipping is not discounted).</div>}

    {quote?.pointsBalance > 0 && <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
      <span style={{ fontSize: 12.5, flex: 1, color: t.muted }}>You have {money(quote.pointsBalance)} in Arego Points</span>
      <input style={{ ...input, width: 90 }} placeholder="$0.00" inputMode="decimal" value={pointsToUse}
        onChange={(e) => setPointsToUse(e.target.value.replace(/[^0-9.]/g, ''))} />
      <button onClick={() => setPointsToUse(String(quote.pointsBalance))} style={{ ...input, width: 70, cursor: 'pointer', fontWeight: 600, textAlign: 'center', fontSize: 12 }}>Max</button>
    </div>}
    {quote?.pointsRedeemed > 0 && <div style={{ fontSize: 12, color: '#2f9e6e', marginBottom: 8 }}>Using {money(quote.pointsRedeemed)} of your Arego Points.</div>}

    </div>

    <div className={L}>
    {zeroDue
      ? <div style={{ ...panel, marginBottom: 12, fontSize: 13, color: '#2f9e6e' }}>Your Arego Points fully cover this order — no card needed.</div>
      : isNmiGateway(skin.gateway)
      ? <div style={{ marginBottom: 12 }}>
        <NmiCardFields ref={nmiRef} gateway={skin.gateway} inputStyle={input} dark={t.dark}
          total={quote?.total} wallets={!hasSub} onWalletToken={(tok) => { if (!paying) handlePay(tok); }} />
        <div style={{ fontSize: 11, color: t.muted, marginTop: 6 }}>Card details are entered in secure fields hosted by our payment processor — they never touch our servers.{hasSub && ' Your card is saved securely with the processor for the recurring charges.'}</div>
      </div>
      : <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
        <input style={input} placeholder="Card number" value={card.number} onChange={(e) => setCard((c) => ({ ...c, number: e.target.value }))} />
        <div style={{ display: 'flex', gap: 6 }}>
          <input style={input} placeholder="MM/YY" inputMode="numeric" maxLength={5} value={card.expiry}
            onChange={(e) => {
              const d = e.target.value.replace(/\D/g, '').slice(0, 4);
              setCard((c) => ({ ...c, expiry: d.length > 2 ? `${d.slice(0, 2)}/${d.slice(2)}` : d }));
            }} />
          <input style={input} placeholder="CVC" inputMode="numeric" maxLength={4} value={card.code} onChange={(e) => setCard((c) => ({ ...c, code: e.target.value }))} />
        </div>
        <div style={{ fontSize: 11, color: t.muted }}>Card details are tokenized in your browser and sent directly to Authorize.net — they never touch our servers.{hasSub && ' Your card is saved securely at Authorize.net for the recurring charges.'}</div>
      </div>}

    {hasSub && subQuotes.map(({ interval: iv, quote: sq }) => {
      const per = perOf(iv);
      if (!sq) {
        return <div key={iv} style={{ ...panel, marginBottom: 10, padding: '10px 14px' }}>
          <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', color: t.accent, marginBottom: 6 }}>Recurring — every {iv} days</div>
          <div style={{ fontSize: 12, color: t.muted }}>These subscription items renew every {iv} days — the recurring total will be confirmed at payment.</div>
        </div>;
      }
      return <div key={iv} style={{ ...panel, marginBottom: 10, padding: '10px 14px' }}>
        <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', color: t.accent, marginBottom: 6 }}>Recurring totals{subQuotes.length > 1 ? ` — every ${iv} days` : ''}</div>
        <div style={{ fontSize: 13, color: t.muted, display: 'flex', flexDirection: 'column', gap: 3 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Subtotal</span><span>{money(sq.subtotal - (sq.discount || 0))} {per}</span></div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Shipping{sq.shippingLabel ? ` via ${sq.shippingLabel}` : ''}</span><span>{sq.shippingOk === false ? 'Not available' : (sq.shipping === 0 ? 'Free' : `${money(sq.shipping)} ${per}`)}</span></div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, color: t.dark ? '#fff' : t.ink }}><span>Recurring total</span><span>{money(sq.total)} {per}</span></div>
        </div>
        {sq.couponFirstOrderOnly && <div style={{ fontSize: 11.5, color: t.muted, marginTop: 6 }}>Your coupon is for this first order only — renewals are charged at the regular price above.</div>}
        <div style={{ fontSize: 11, color: t.muted, marginTop: 6 }}>Charged automatically every {iv} days for these subscription items — today you pay the full order total below.</div>
      </div>;
    })}

    {skin.mkt.showEarningBox && quote && (quote.pointsToEarn > 0 || subQuotes.some(({ quote: sq }) => sq && sq.pointsToEarn > 0)) && <div style={{ ...panel, marginBottom: 10, padding: '10px 14px' }}>
      <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', color: t.accent, marginBottom: 6 }}>Earning Points</div>
      <div style={{ fontSize: 13, color: t.muted, display: 'flex', flexDirection: 'column', gap: 3 }}>
        {quote.pointsToEarn > 0 && <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>You earn today</span><span style={{ fontWeight: 700, color: t.dark ? '#fff' : t.ink }}>{Number(quote.pointsToEarn).toLocaleString()} points</span></div>}
        {subQuotes.filter(({ quote: sq }) => sq && sq.pointsToEarn > 0).map(({ interval: iv, quote: sq }) => (
          <div key={iv} style={{ display: 'flex', justifyContent: 'space-between' }}><span>Recurring</span><span>{Number(sq.pointsToEarn).toLocaleString()} points {perOf(iv)}</span></div>
        ))}
      </div>
      <div style={{ fontSize: 11, color: t.muted, marginTop: 6 }}>Points are credited to your Arego Points balance each time an order is successfully paid.</div>
    </div>}

    {/* Only shown when the matched zone genuinely offers more than one
        priced option (e.g. a rate sheet with real DHL/FedEx data has been
        uploaded) — a single-method zone shows no picker at all, so this is
        invisible for stores that never touch this feature. */}
    {quote && quote.shippingOptions && quote.shippingOptions.length > 1 && <div style={{ ...panel, marginBottom: 10, padding: '10px 14px' }}>
      <style>{MYUS_RADIO_CSS}</style>
      <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', color: t.accent, marginBottom: 8 }}>Shipping Method</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {quote.shippingOptions.map((opt) => {
          const selected = (shippingMethodId || quote.shippingMethodId) === opt.id;
          return <label key={opt.id} style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, cursor: 'pointer', color: t.dark ? '#fff' : t.ink, padding: '2px 0' }}>
            <input className="myus-radio-input" type="radio" name="shippingMethod" checked={selected} onChange={() => setShippingMethodId(opt.id)} />
            <span className="myus-radio-dot"></span>
            <span style={{ flex: 1 }}>{opt.title}</span>
            <span style={{ fontWeight: 600 }}>{opt.cost === 0 ? 'Free' : money(opt.cost)}</span>
          </label>;
        })}
      </div>
    </div>}

    {/* International (MyUS-forwarded) orders need a recipient phone — the
        carrier and MyUS's merchant API require one. Prefilled from the
        member's record; editable here without leaving checkout. */}
    {needsPhone && <div style={{ ...panel, marginBottom: 10, padding: '10px 14px' }}>
      <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', color: t.accent, marginBottom: 6 }}>Phone for delivery</div>
      <StorePhoneField
        skin={skin}
        theme={t}
        label=""
        country={phoneCountry}
        number={phone}
        onCountry={(iso) => { setPhoneTouched(true); setPhoneCountry(iso); }}
        onNumber={(n) => { setPhoneTouched(true); setPhone(n); }}
        help={quote?.shippingMyus
          ? 'The carrier uses this for delivery and customs.'
          : 'Used to reach you about this order.'}
      />
    </div>}

    </div>

    <div className={R}>
    {quote && <div style={{ fontSize: 13, color: t.muted, display: 'flex', flexDirection: 'column', gap: 3, marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Subtotal</span><span>{money(quote.subtotal)}</span></div>
      {quote.discount > 0 && <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Coupon</span><span>−{money(quote.discount)}</span></div>}
      {quote.pointsRedeemed > 0 && <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Arego Points</span><span>−{money(quote.pointsRedeemed)}</span></div>}
      {/* shippingOk false = the destination matched no zone — never render
          the zero as "Free"; the banner above + pay button carry the block. */}
      <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Shipping{quote.shippingLabel ? ` (${quote.shippingLabel})` : ''}</span><span>{quote.shippingOk === false ? 'Not available' : (quote.shipping === 0 ? 'Free' : money(quote.shipping))}</span></div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 15, fontWeight: 700, color: t.dark ? '#fff' : t.ink }}><span>Total</span><span>{money(quote.total)}</span></div>
    </div>}

    <FreeShipNotice cfg={readFreeShip(skin.mkt)} quote={quote} isHipaa={skin.isHipaa}
      destinationKnown={!!(scope ? quoteAddress.countryCode : guestCountry)} marks={PAYMENT_MARKS} />

    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, margin: '4px 0 10px', fontSize: 13, lineHeight: 1.4, color: t.muted, cursor: 'pointer' }}>
      <input type="checkbox" checked={emailOptIn} onChange={(e) => setEmailOptIn(e.target.checked)} name="emailOptIn"
        style={{ width: 15, height: 15, marginTop: 1, flex: 'none', accentColor: t.accent }} />
      <span>Email me news and offers from {skin.mkt.name || 'this store'}</span>
    </label>

    {error && <div style={{ background: '#fbe9e9', color: '#b02a2a', borderRadius: 8, padding: '9px 12px', fontSize: 13, marginBottom: 10 }}>{error}</div>}

    {(() => {
      const missingAddress = scope && customer && !hasAddress;
      const noShippingHere = scope && customer && hasAddress && quote && quote.shippingOk === false;
      const blocked = missingAddress || noShippingHere || missingPhone;
      const label = paying ? 'Processing…'
        : missingAddress ? 'Add a shipping address to pay'
        : noShippingHere ? 'Shipping unavailable to this address'
        : missingPhone ? 'Add a phone number to pay'
        : `Pay ${quote ? money(quote.total) : ''}`;
      // Bound to the button's own disabled expression rather than to a list of
      // labels, so a blocked state added later is covered without anyone having
      // to remember this. It also has to cover two the brief did not anticipate:
      // no quote yet, where the label is the bare word "Pay"; and a
      // points-funded order, which reaches an enabled button reading "Pay
      // $0.00". And not while paying — the charge is already in flight, so
      // urgency has no function left, and a failure would put the error banner
      // directly under a discount pitch.
      const ctaShow = !paying && !blocked && !!quote && quote.total > 0;
      return <button onClick={handlePay} disabled={paying || !quote || blocked} style={{
        ...(ctaOn ? CTA_STACK : { display: 'block' }),
        width: '100%', textAlign: 'center', fontSize: 15, fontWeight: 700,
        background: t.accent, color: '#fff', borderRadius: 99, padding: 13, border: 'none',
        cursor: paying || blocked ? 'default' : 'pointer',
        opacity: paying || blocked ? 0.55 : 1,
      }}>
        <span>{label}</span>
        {ctaOn && <CtaSubline cfg={cta} pct={ctaPct} show={ctaShow} />}
      </button>;
    })()}
    </div>
  </div>;

  return ckNatural ? <div className="ck-natural-bg">{ckColumns(body)}</div> : body;
};

// ── Thank you ──────────────────────────────────────────────────────────────
const ThankYou = ({ skin, slug }) => {
  const t = skin.t;
  const params = new URLSearchParams(useLocation().search);
  const orderId = params.get('order');
  const isSub = params.get('sub') === '1';
  useGoogleReviewsOptIn(skin, orderId);
  return <div style={{ maxWidth: 480, margin: '0 auto', padding: '70px 22px', textAlign: 'center' }}>
    <span style={{ width: 64, height: 64, borderRadius: '50%', background: t.accent, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, color: '#fff' }}>✓</span>
    <h1 style={{ fontSize: 26, fontWeight: 700, marginTop: 18, color: t.dark ? '#fff' : t.ink }}>Thank you!</h1>
    <p style={{ fontSize: 14, color: t.muted, marginTop: 8 }}>
      Order <b>#{orderId}</b> confirmed.{isSub ? ' Your subscription is active — future shipments are charged automatically to your saved card.' : ' You will receive a confirmation shortly.'}
    </p>
    <a href={storeUrl(slug, 'shop')} style={{ display: 'inline-block', marginTop: 18, fontSize: 13, fontWeight: 600, color: t.accent, textDecoration: 'none' }}>Continue shopping →</a>
  </div>;
};

// ── Template 2: "AregoMD" clinical design ──────────────────────────────────
// Full-page skin (own header/footer/typography) replacing the generic Shell
// for template id '2'. All copy is editable/hideable dummy content
// (edits.texts / edits.md2 / edits.productInfo); products stay dynamic.

// Brand palette (AregoMD) — warm tan/gold + charcoal, no blue.
const MD2 = {
  ink: '#221d18', accent: '#c2a06a', accentDark: '#a8865a', sub: '#5c554c',
  muted: '#7c7368', faint: '#a89f92', border: '#ece7dd', chip: '#efe6d5', navy: '#26211d',
  btn: '#2b2723', tan: '#c2a06a', tanSoft: '#efe6d5',
};

const MD2_CSS = `
/* Typefaces are requested as a <link> in the head (templateFonts.js + seoHead.jsx), not with an @import here: an @import inside a <style> this component renders cannot start until the bundle has mounted, and then costs another round trip before any text can paint. */
.md2{font-family:'Instrument Sans',system-ui,sans-serif;color:${MD2.ink};background:#fff;min-height:100vh;display:flex;flex-direction:column;overflow-x:clip}
.md2 a{text-decoration:none}
.md2-wrap{max-width:1140px;margin:0 auto;padding-left:24px;padding-right:24px;box-sizing:border-box;width:100%}
.md2-hero-grid{display:grid;grid-template-columns:1.05fr .95fr;gap:48px;align-items:center}
.md2-badges-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:16px}
.md2-hiw-grid{display:grid;grid-template-columns:1.1fr .9fr;gap:56px;align-items:center}
.md2-cards{display:grid;grid-template-columns:repeat(3,1fr);gap:18px}
.md2-prog{display:grid;grid-template-columns:repeat(4,1fr);gap:18px}
@media(max-width:1024px){.md2-prog{grid-template-columns:repeat(2,1fr)}}
@media(max-width:560px){.md2-prog{grid-template-columns:1fr}}
.sh-vial{margin-top:-240px;text-align:center}
@media(max-width:900px){.sh-vial{margin-top:24px}}
@media(max-width:640px){.md2-prodgrid{grid-template-columns:1fr!important}}
.wl-band{position:relative;border-radius:20px;min-height:230px;display:flex;align-items:center;overflow:visible;background:#efe6d5;padding:32px 26px}
.wl-bandimg{position:absolute;top:50%;width:300px;height:300px;object-fit:contain;pointer-events:none;filter:drop-shadow(0 22px 34px rgba(34,29,24,.18))}
@media(min-width:821px){.wl-band{background:#fff;box-shadow:rgba(149,157,165,.2) 0px 8px 24px;min-height:200px}.wl-band-left{padding:30px 44px 30px 120px}.wl-band-right{padding:30px 300px 30px 44px}.wl-bandimg-0{transform:translate(-44%,-50%)}.wl-bandimg-1{transform:translate(34%,-50%)}}
@media(min-width:1025px){.wl-band.wl-band-0.wl-band-left{width:80%}.wl-band.wl-band-1.wl-band-right{width:80%;justify-self:end}}
@media(max-width:820px){.wl-band{flex-direction:column;text-align:center;padding:32px 26px!important;background:transparent!important;box-shadow:none!important}.wl-bandimg{position:static!important;transform:none!important;width:190px!important;height:190px!important;margin:0 auto 12px!important}.wl-research-grid{grid-template-columns:1fr!important;gap:22px!important;text-align:center}}
.wl-glp-grid{display:grid;grid-template-columns:1fr 1.05fr 1fr;gap:36px;align-items:center}
@media(max-width:900px){.wl-glp-grid{grid-template-columns:1fr;gap:26px;max-width:460px;margin:0 auto}}
.md2-tele-grid{display:grid;grid-template-columns:1fr 1fr;gap:clamp(30px,5vw,64px);align-items:center}
@media(max-width:900px){.md2-tele-grid{grid-template-columns:1fr;gap:34px}.wl-tele-bottle{width:96px!important;transform:translateX(20%)!important}}
.md2-testi-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:24px;align-items:stretch}
@media(max-width:900px){.md2-testi-grid{grid-template-columns:1fr;max-width:460px;margin:0 auto}}
.md2-benefits-grid{display:grid;grid-template-columns:1.1fr .9fr;gap:clamp(30px,5vw,60px);align-items:center}
@media(max-width:900px){.md2-benefits-grid{grid-template-columns:1fr;gap:26px}}
.md2-lo-trust{display:grid;grid-template-columns:repeat(4,1fr);gap:18px}
@media(max-width:820px){.md2-lo-trust{grid-template-columns:1fr 1fr}}
@media(max-width:480px){.md2-lo-trust{grid-template-columns:1fr}}
.md2-lo-quality{display:grid;grid-template-columns:1fr 1fr;gap:20px}
@media(max-width:700px){.md2-lo-quality{grid-template-columns:1fr}}
.md2-lo-prodgrid{display:grid;grid-template-columns:repeat(4,1fr);gap:22px}
@media(max-width:980px){.md2-lo-prodgrid{grid-template-columns:1fr 1fr}}
@media(max-width:440px){.md2-lo-prodgrid{grid-template-columns:1fr}}
.md2-foot-grid{display:grid;grid-template-columns:repeat(3,1fr) minmax(150px,230px);gap:30px;align-items:start}
.md2-foot-vial{text-align:right}
@media(max-width:820px){.md2-foot-grid{grid-template-columns:1fr 1fr;gap:26px}.md2-foot-vial{grid-column:1 / -1;text-align:center;order:-1}.md2-foot-vial img{max-width:180px}.md2-foot-partners{justify-content:center}}
@media(max-width:520px){.md2-foot-grid{grid-template-columns:1fr}.md2-foot-pay{justify-content:center!important}}
@media(max-width:820px){.md2-cite-grid{grid-template-columns:1fr!important}}
@media(min-width:821px) and (max-width:1024px){.md2-cite-grid{grid-template-columns:1fr 1fr!important}}
.md2-product-grid{display:grid;grid-template-columns:1fr 1fr;gap:48px;align-items:start}
.md2-nav{display:flex;gap:22px;font-size:14px;font-weight:500}
.md2-h1{font-size:clamp(30px,5vw,46px);line-height:1.08;letter-spacing:-1.5px;font-weight:800;margin:0}
.md2-h2{font-size:clamp(26px,3.6vw,34px);font-weight:800;letter-spacing:-1px;margin:0}
.md2-footer-cols{display:flex;justify-content:space-between;gap:40px;flex-wrap:wrap}
@media(max-width:980px){.md2-cards{grid-template-columns:repeat(2,1fr)}}
@media(max-width:900px){.md2-hero-grid,.md2-hiw-grid,.md2-product-grid{grid-template-columns:1fr;gap:28px}.md2-badges-grid{grid-template-columns:repeat(2,1fr)}.md2-hero-visual{height:300px !important}}
@media(max-width:720px){.md2-nav{display:none}}
@media(max-width:620px){.md2-cards{grid-template-columns:1fr}.md2-badges-grid{grid-template-columns:1fr}}
`;

const Md2Wordmark = ({ size = 23 }) => {
  const bar = { width: size * 0.57, height: Math.max(2.5, size * 0.13), background: MD2.ink, borderRadius: 1 };
  return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, fontWeight: 800, fontSize: size, letterSpacing: '-.5px', color: MD2.ink }}>
    ar<span style={{ display: 'inline-flex', flexDirection: 'column', gap: size * 0.13, margin: '0 3px' }}><span style={bar}></span><span style={bar}></span><span style={bar}></span></span>go<span style={{ fontSize: size * 0.52, fontWeight: 700, color: MD2.accent, marginLeft: 2 }}>MD</span>
  </span>;
};

const md2Stripes = 'repeating-linear-gradient(45deg,#eef1f9,#eef1f9 8px,#e5eaf6 8px,#e5eaf6 16px)';

const md2BtnPrimary = { fontSize: 15, fontWeight: 700, background: MD2.btn, color: '#fff', borderRadius: 99, padding: '14px 32px', display: 'inline-block' };
const md2BtnGhost = { fontSize: 15, fontWeight: 700, color: MD2.ink, border: `1.5px solid ${MD2.border}`, borderRadius: 99, padding: '14px 32px', display: 'inline-block' };

export const Md2Shell = ({ skin, slug, edit, children, page }) => {
  const qs = edit ? '?edit=1' : '';
  const home = `/marketplace/${slug}${qs}`;
  const [menuOpen, setMenuOpen] = useState(false);
  // Header nav mirrors aregomd.com: the 4 programs (+ Login/Cart). Links come
  // from the editable Header Menu (Manage → Header Menu); if none are set we
  // fall back to the default program links. Desktop alignment (left/right) is
  // set by mkt.headerNavAlign.
  const defaultProgramLinks = [
    { label: 'Weight Loss', url: `/marketplace/${slug}/weight-loss${qs}` },
    { label: 'Sexual Health', url: `/marketplace/${slug}/sexual-health${qs}` },
    { label: 'Longevity', url: `/marketplace/${slug}/longevity${qs}` },
    ...(hrtEnabled(skin.mkt) ? [{ label: 'HRT', url: `/marketplace/${slug}/hrt${qs}` }] : []),
  ];
  const menuItems = (Array.isArray(skin.mkt.headerMenu) ? skin.mkt.headerMenu : []).filter((l) => l?.label && l?.url);
  const navLinks = menuItems.length ? menuItems : defaultProgramLinks;
  const navAlign = skin.mkt.headerNavAlign === 'right' ? 'right' : 'left';
  const drawerLinks = navLinks;
  return <div className="md2">
    <style>{MD2_CSS}</style>
    <style>{MOBILE_NAV_CSS}</style>
    <style>{customCssFor(skin.mkt, page)}</style>

    {skin.banner?.show && (skin.banner?.text || edit) && (
      <div style={{ background: MD2.accent, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, padding: '9px 20px', fontSize: 13, fontWeight: 500, textAlign: 'center', position: 'relative' }}>
        <EditableText edit={edit ? { setText: (k, v) => edit.setBanner({ ...skin.banner, text: v }) } : null} k="bannerText" value={skin.banner.text} />
        {edit && <span onClick={() => edit.setBanner({ ...skin.banner, show: false })}
          style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: '#fff', color: MD2.accent, borderRadius: 99, fontSize: 11, fontWeight: 700, padding: '2px 10px', cursor: 'pointer' }}>Hide banner ×</span>}
      </div>
    )}
    {edit && !skin.banner?.show && (
      <div onClick={() => edit.setBanner({ show: true, text: skin.banner?.text || skin.t.defaultBanner || 'Free shipping this week only' })}
        style={{ border: EDIT_OUTLINE, textAlign: 'center', fontSize: 12, fontWeight: 600, padding: '6px 12px', color: '#f59f00', cursor: 'pointer' }}>
        Top banner is hidden — click to show it
      </div>
    )}

    <div style={{ position: 'sticky', top: 0, zIndex: 50, background: 'rgba(255,255,255,.94)', backdropFilter: 'blur(10px)', borderBottom: `1px solid ${MD2.border}` }}>
      <div className="md2-wrap mkt-header-desktop" style={{ alignItems: 'center', gap: 28, paddingTop: 14, paddingBottom: 14 }}>
        <a href={home} style={{ display: 'flex', alignItems: 'center' }}>
          <EditableImage edit={edit} k="logo" label="logo" inline>
            {skin.logo ? <img src={skin.logo} alt="logo" style={{ height: 32 }} /> : <Md2Wordmark />}
          </EditableImage>
        </a>
        {navAlign === 'right' && <div style={{ flex: 1 }}></div>}
        <nav className="md2-nav" style={{ color: MD2.sub }}>
          {navLinks.map((l, i) => <a key={i} href={l.url} target={l.newTab ? '_blank' : undefined} rel={l.newTab ? 'noreferrer' : undefined} style={{ color: MD2.sub }}>{l.label}</a>)}
        </nav>
        {navAlign !== 'right' && <div style={{ flex: 1 }}></div>}
        <EnrollHeaderButton skin={skin} slug={slug} edit={edit} accent={MD2.accent} />
        <AuthHeaderButton skin={skin} edit={edit} color={MD2.sub} />
        <CartIcon skin={skin} edit={edit} />
      </div>
      <div className="md2-wrap mkt-header-mobile" style={{ alignItems: 'center', justifyContent: 'space-between', paddingTop: 14, paddingBottom: 14 }}>
        <a href={home} style={{ display: 'flex', alignItems: 'center' }}>
          <EditableImage edit={edit} k="logo" label="logo" inline>
            {skin.logo ? <img src={skin.logo} alt="logo" style={{ height: 32 }} /> : <Md2Wordmark />}
          </EditableImage>
        </a>
        <MobileMenuIcon skin={skin} edit={edit} onClick={() => setMenuOpen(true)} color={MD2.ink} />
      </div>
    </div>
    <MobileDrawer skin={skin} edit={edit} open={menuOpen} onClose={() => setMenuOpen(false)} menuLinks={drawerLinks} ctaSlot={null} />

    <div style={{ flex: 1 }}>{children}</div>

    {(() => {
      const fLine = (l, i) => (l.url ? <a key={i} href={l.url} style={{ fontSize: 15, color: 'rgba(255,255,255,.82)', textDecoration: 'none' }}>{l.label}</a> : <span key={i} style={{ fontSize: 15, color: 'rgba(255,255,255,.82)' }}>{l.label}</span>);
      const footCols = [
        { h: 'Main Menu', links: [{ label: 'Home', url: home }, { label: 'Shop', url: `/marketplace/${slug}/shop${qs}` }, { label: 'About Us', url: home }] },
        { h: 'Our Policies', links: [{ label: 'Payment & Shipping', url: `/marketplace/${slug}/payment-shipping` }, { label: 'Terms & Conditions', url: `/marketplace/${slug}/terms-conditions` }, { label: 'Notice of Privacy Practices', url: `/marketplace/${slug}/privacy-practices` }, { label: 'Privacy Policy', url: `/marketplace/${slug}/privacy` }, { label: 'Builder Policy', url: `/marketplace/${slug}/builder-policy` }] },
        { h: 'About Us', links: [{ label: 'FAQ', url: `/marketplace/${slug}/faq` }, { label: 'Payments', url: `/marketplace/${slug}/payments` }, { label: 'Contact Us', url: `/marketplace/${slug}/contact` }] },
      ];
      return <div style={{ marginTop: 'auto', borderTop: '15px solid #d1b69a', background: '#201b17' }}>
        <div className="md2-wrap" style={{ paddingTop: 46, paddingBottom: 0 }}>
          <img src="https://wp.aregomd.com/wp-content/uploads/2026/03/AregoMD_Logo_White.png" alt="AregoMD" style={{ height: 44, maxWidth: '72%', objectFit: 'contain', display: 'block' }} />
          <div style={{ borderTop: '1px solid rgba(255,255,255,.16)', margin: '26px 0 32px' }} />
          <div className="md2-foot-grid">
            {footCols.map((col) => <div key={col.h}>
              <h4 style={{ fontFamily: "'Playfair Display',Georgia,serif", fontSize: 22, fontWeight: 700, color: '#fff', margin: '0 0 18px' }}>{col.h}</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>{col.links.map(fLine)}</div>
            </div>)}
            <div className="md2-foot-vial"><img src="https://wp.aregomd.com/wp-content/uploads/2026/04/Site_Trio-Vial.png" alt="" style={{ width: '100%', maxWidth: 230, objectFit: 'contain' }} /></div>
          </div>
          <div style={{ borderTop: '1px solid rgba(255,255,255,.16)', margin: '34px 0 26px' }} />
          <p style={{ fontSize: 13.5, color: 'rgba(255,255,255,.6)', lineHeight: 1.75, textAlign: 'justify', margin: 0 }}>
            <Md2Text edit={edit} skin={skin} k="footerLegal">{(v) => <EditableText edit={edit} k="footerLegal" value={v} />}</Md2Text>
          </p>
          <div className="md2-foot-partners" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '44px 0 0', gap: 20, flexWrap: 'wrap' }}>
            <img src="https://wp.aregomd.com/wp-content/uploads/2026/03/arora.png" alt="Arora Health" style={{ height: 40, objectFit: 'contain' }} />
            <a href="https://www.legitscript.com/websites/?checker_keywords=aregomd.com" target="_blank" rel="noreferrer"><img src="https://static.legitscript.com/seals/49063639.png" alt="LegitScript approved" width="73" height="79" style={{ display: 'block' }} /></a>
          </div>
          <p style={{ fontSize: 13.5, color: 'rgba(255,255,255,.6)', lineHeight: 1.7, textAlign: 'justify', margin: '30px 0 0' }}>
            <Md2Text edit={edit} skin={skin} k="footerArora">{(v) => <EditableText edit={edit} k="footerArora" value={v} />}</Md2Text>
          </p>
          <p style={{ fontSize: 13.5, color: 'rgba(255,255,255,.6)', margin: '26px 0 36px' }}>
            <Md2Text edit={edit} skin={skin} k="footerCopyright">{(v) => <EditableText edit={edit} k="footerCopyright" value={v} />}</Md2Text>
          </p>
        </div>
        <div style={{ borderTop: '1px solid rgba(255,255,255,.28)', background: '#181410' }}>
          <div className="md2-wrap md2-foot-pay" style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 12, paddingTop: 16, paddingBottom: 16, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 14, color: '#fff', fontWeight: 500 }}>Payment Method</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }} dangerouslySetInnerHTML={{ __html: PAYMENT_ICONS_HTML }} />
          </div>
        </div>
      </div>;
    })()}

    {edit && <div style={{ position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 100, background: '#1d273b', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 18, padding: '11px 16px', fontSize: 13 }}>
      <span>🖉 <b>Visual editing</b> — click any dashed element to edit it. Changes save automatically.</span>
      <span style={{ color: edit.saving ? '#f59f00' : '#7fe0a7', fontWeight: 700, minWidth: 64 }}>{edit.saving ? 'Saving…' : (edit.flash || 'Saved ✓')}</span>
      <a href={`/marketplace/manage/${skin.mkt.id}`} style={{ color: '#9ec1f7', fontWeight: 600 }}>Exit editing</a>
    </div>}
  </div>;
};

// Product card shared by the home treatments band (on navy) and the listing.
const Md2Card = ({ skin, slug, edit, p, onNavy, big }) => {
  const qs = edit ? '?edit=1' : '';
  const badge = skin.badges[p.id];
  const pi = md2ProductInfo(skin.mkt, p.id);
  const doses = (Array.isArray(p.doses) ? p.doses : []).map((d) => (typeof d === 'string' ? { label: d } : d));
  const desc = p.description ? String(p.description).replace(/<[^>]+>/g, '').slice(0, 110) : '';
  const bc = md2BadgeColors(badge);
  const editBadge = (e) => {
    e.preventDefault(); e.stopPropagation();
    const v = window.prompt('Badge text (leave empty to remove the badge):', badge ?? '');
    if (v === null) return;
    edit.setBadge(p.id, v.trim());
  };
  return <a href={`/marketplace/${slug}/product/${encodeURIComponent(p.id)}${qs}`}
    style={{ background: '#fff', border: onNavy ? 'none' : `1px solid ${MD2.border}`, borderRadius: 18, padding: 18, display: 'flex', flexDirection: 'column', color: 'inherit' }}>
    <div style={{ height: big ? 260 : 150, borderRadius: 12, background: md2Stripes, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', overflow: 'hidden' }}>
      {p.imageUrl && <img src={p.imageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', background: '#fff' }} />}
      {badge && <span onClick={edit ? editBadge : undefined} style={{ position: 'absolute', top: 10, left: 10, fontSize: 10, fontWeight: 800, letterSpacing: '.04em', background: bc.bg, color: bc.fg, borderRadius: 6, padding: '3px 9px', cursor: edit ? 'pointer' : 'default', outline: edit ? EDIT_OUTLINE : 'none', outlineOffset: 2 }}>{badge}</span>}
      {edit && !badge && <span onClick={editBadge} style={{ position: 'absolute', top: 10, left: 10, fontSize: 10, fontWeight: 700, border: EDIT_OUTLINE, color: '#f59f00', borderRadius: 6, padding: '3px 9px', cursor: 'pointer', background: 'rgba(255,255,255,.85)' }}>+ badge</span>}
    </div>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: 14, gap: 8 }}>
      <span style={{ fontSize: big ? 21 : 17, fontWeight: 800, color: MD2.ink, letterSpacing: '-.3px' }}>{p.name}</span>
      <span style={{ fontSize: 13, fontWeight: 700, color: MD2.accent, whiteSpace: 'nowrap' }}>from {money(p.price)}</span>
    </div>
    {pi.category && <span style={{ fontSize: 12, color: MD2.faint, marginTop: 2, fontWeight: 600 }}>{pi.category}</span>}
    {desc && <span style={{ fontSize: 12.5, color: MD2.muted, marginTop: 6, lineHeight: 1.5 }}>{desc}</span>}
    {doses.length > 0 && <div style={{ display: 'flex', gap: 5, marginTop: 12, flexWrap: 'wrap' }}>
      {doses.map((d, i) => <span key={i} style={{ fontSize: 10.5, fontWeight: 700, color: MD2.accent, border: `1px solid ${MD2.chip}`, borderRadius: 99, padding: '3px 10px' }}>{d.label}</span>)}
    </div>}
    <span style={{ marginTop: 16, textAlign: 'center', fontSize: big ? 15 : 13.5, fontWeight: 700, background: MD2.btn, color: '#fff', borderRadius: 99, padding: big ? 14 : 11 }}>Get Started →</span>
  </a>;
};

// ── MD2 homepage ───────────────────────────────────────────────────────────
const Md2Home = ({ skin, slug, edit }) => {
  const qs = edit ? '?edit=1' : '';
  const L = skin.md2.lists;

  return <>
    {/* Hero */}
    <div style={{ background: 'linear-gradient(180deg,#f4f7ff 0%,#fff 90%)' }}>
      <div className="md2-wrap md2-hero-grid" style={{ paddingTop: 56, paddingBottom: 48 }}>
        <div>
          <Md2Text edit={edit} skin={skin} k="ratingLine">{(v) => (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600, color: MD2.sub, background: '#fff', border: `1px solid ${MD2.chip}`, borderRadius: 99, padding: '6px 14px' }}>
              <span style={{ color: '#f0b429', letterSpacing: 1 }}>★★★★★</span><EditableText edit={edit} k="ratingLine" value={v} />
            </div>
          )}</Md2Text>
          <h1 className="md2-h1" style={{ margin: '20px 0 0' }}>
            <Md2Text edit={edit} skin={skin} k="heroTitle">{(v) => <EditableText edit={edit} k="heroTitle" value={v} />}</Md2Text>{' '}
            <span style={{ color: MD2.accent }}><Md2Text edit={edit} skin={skin} k="heroTitleAccent">{(v) => <EditableText edit={edit} k="heroTitleAccent" value={v} />}</Md2Text></span>
          </h1>
          <p style={{ margin: '18px 0 0', fontSize: 17, lineHeight: 1.6, color: MD2.sub, maxWidth: 480 }}>
            <Md2Text edit={edit} skin={skin} k="heroSubtitle">{(v) => <EditableText edit={edit} k="heroSubtitle" value={v} />}</Md2Text>
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, margin: '22px 0 0', fontSize: 14.5, color: '#344054' }}>
            {L.heroChecks.map((c, i) => (
              <span key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ width: 20, height: 20, borderRadius: '50%', background: '#e8f6ee', color: '#1d7a46', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, flex: 'none' }}>✓</span>
                <EditableText edit={md2ItemEdit(edit, skin, 'heroChecks', i)} k={`heroChecks${i}`} value={c} />
                <Md2Del edit={edit} onClick={() => edit.setMd2('heroChecks', L.heroChecks.filter((_, j) => j !== i))} />
              </span>
            ))}
            <Md2Add edit={edit} label="add checkmark" onClick={() => edit.setMd2('heroChecks', [...L.heroChecks, 'New benefit — click to edit'])} />
          </div>
          <div style={{ display: 'flex', gap: 12, marginTop: 28, flexWrap: 'wrap' }}>
            <a href={edit ? undefined : '#programs'} onClick={edit ? undefined : (e) => { e.preventDefault(); document.getElementById('programs')?.scrollIntoView({ behavior: 'smooth' }); }} style={md2BtnPrimary}>
              <Md2Text edit={edit} skin={skin} k="ctaLabel">{(v) => <EditableText edit={edit} k="ctaLabel" value={v} />}</Md2Text>
            </a>
            <a href={edit ? undefined : '#howitworks'} onClick={edit ? undefined : (e) => { e.preventDefault(); document.getElementById('howitworks')?.scrollIntoView({ behavior: 'smooth' }); }} style={md2BtnGhost}>
              <Md2Text edit={edit} skin={skin} k="heroSecondary">{(v) => <EditableText edit={edit} k="heroSecondary" value={v} />}</Md2Text>
            </a>
          </div>
          <div style={{ display: 'flex', gap: 18, marginTop: 18, fontSize: 12.5, color: MD2.muted, flexWrap: 'wrap' }}>
            {L.heroPills.map((x, i) => (
              <span key={i}>
                <EditableText edit={md2ItemEdit(edit, skin, 'heroPills', i)} k={`heroPills${i}`} value={x} />
                <Md2Del edit={edit} onClick={() => edit.setMd2('heroPills', L.heroPills.filter((_, j) => j !== i))} />
              </span>
            ))}
            <Md2Add edit={edit} label="add" onClick={() => edit.setMd2('heroPills', [...L.heroPills, '✓ New point'])} />
          </div>
        </div>
        <div>
          <EditableImage edit={edit} k="hero" label="hero image">
            <span className="md2-hero-visual" style={{ display: 'block', height: 440, borderRadius: 22, background: md2Stripes, overflow: 'hidden' }}>
              {skin.heroImage && <img src={skin.heroImage} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
            </span>
          </EditableImage>
        </div>
      </div>
    </div>

    {/* Press strip */}
    <Md2Section edit={edit} skin={skin} k="press" label="press strip">
      <div style={{ borderTop: `1px solid ${MD2.border}`, borderBottom: `1px solid ${MD2.border}`, background: '#fff' }}>
        <div className="md2-wrap" style={{ paddingTop: 22, paddingBottom: 22, display: 'flex', alignItems: 'center', gap: 36, justifyContent: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, letterSpacing: '.14em', color: MD2.faint, fontWeight: 600, textTransform: 'uppercase' }}>
            <Md2Text edit={edit} skin={skin} k="pressKicker">{(v) => <EditableText edit={edit} k="pressKicker" value={v} />}</Md2Text>
          </span>
          {L.press.map((p, i) => (
            <span key={i} style={{ fontFamily: 'Georgia,serif', fontSize: 17, fontWeight: 700, color: '#b6bdc9', letterSpacing: '-.3px' }}>
              <EditableText edit={md2ItemEdit(edit, skin, 'press', i)} k={`press${i}`} value={p} />
              <Md2Del edit={edit} onClick={() => edit.setMd2('press', L.press.filter((_, j) => j !== i))} />
            </span>
          ))}
          <Md2Add edit={edit} label="add" onClick={() => edit.setMd2('press', [...L.press, 'Press name'])} />
        </div>
      </div>
    </Md2Section>

    {/* Feature badges */}
    <Md2Section edit={edit} skin={skin} k="badges" label="feature badges">
      <div className="md2-wrap md2-badges-grid" style={{ paddingTop: 40, paddingBottom: 40 }}>
        {L.badges4.map((b, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#f8fafc', border: `1px solid ${MD2.border}`, borderRadius: 14, padding: '16px 18px' }}>
            <span style={{ fontSize: 22 }}><EditableText edit={md2ItemEdit(edit, skin, 'badges4', i, 'icon')} k={`b4i${i}`} value={b.icon} /></span>
            <span style={{ fontSize: 13.5, fontWeight: 700, color: MD2.ink, lineHeight: 1.3, flex: 1 }}>
              <EditableText edit={md2ItemEdit(edit, skin, 'badges4', i, 'label')} k={`b4l${i}`} value={b.label} />
            </span>
            <Md2Del edit={edit} onClick={() => edit.setMd2('badges4', L.badges4.filter((_, j) => j !== i))} />
          </div>
        ))}
        {edit && <div style={{ display: 'flex', alignItems: 'center' }}><Md2Add edit={edit} label="add badge" onClick={() => edit.setMd2('badges4', [...L.badges4, { icon: '⭐', label: 'New badge' }])} /></div>}
      </div>
    </Md2Section>

    {/* Explore by Program — 4 program cards (above the Who-We-Are section) */}
    <Md2Section edit={edit} skin={skin} k="programs" label="Explore by Program">
      <div id="programs" style={{ background: '#f8fafc', scrollMarginTop: 80 }}>
        <div className="md2-wrap" style={{ paddingTop: 72, paddingBottom: 80 }}>
          <div style={{ textAlign: 'center', marginBottom: 44 }}>
            <span style={{ fontSize: 12, letterSpacing: '.16em', color: MD2.accent, fontWeight: 700, textTransform: 'uppercase' }}>
              <Md2Text edit={edit} skin={skin} k="treatKicker">{(v) => <EditableText edit={edit} k="treatKicker" value={v} />}</Md2Text>
            </span>
            <h2 className="md2-h2" style={{ margin: '12px 0 0', color: MD2.ink, fontSize: 'clamp(28px,3.8vw,38px)' }}>
              <Md2Text edit={edit} skin={skin} k="treatTitle">{(v) => <EditableText edit={edit} k="treatTitle" value={v} />}</Md2Text>
            </h2>
            <p style={{ margin: '12px auto 0', fontSize: 15, color: MD2.muted, maxWidth: 520 }}>
              <Md2Text edit={edit} skin={skin} k="treatSub">{(v) => <EditableText edit={edit} k="treatSub" value={v} />}</Md2Text>
            </p>
          </div>
          <div className="md2-prog">
            {(Array.isArray(L.programs) ? L.programs : []).map((c, i) => {
              const ext = /^https?:/.test(c.href || '');
              const href = ext ? c.href : `/marketplace/${slug}${c.href || ''}${qs}`;
              const cslug = String(c.href || '').replace(/^\/+/, '').replace(/[^a-z0-9-]+/gi, '-').replace(/^-+|-+$/g, '') || `item-${i}`;
              return <a key={i} href={edit ? undefined : href} target={ext ? '_blank' : undefined} rel={ext ? 'noreferrer' : undefined}
                className={`md2-prog-card md2-prog-card-${i} md2-prog-${cslug}`}
                style={{ background: '#fff', border: `1px solid ${MD2.border}`, borderRadius: 18, overflow: 'hidden', display: 'flex', flexDirection: 'column', color: 'inherit', boxShadow: '0 10px 30px rgba(34,29,24,.06)' }}>
                <div style={{ height: 210, background: md2Stripes, overflow: 'hidden', flex: 'none' }}>
                  <EditableImage edit={edit} k={`programs${i}img`} label="program image" allowUrl onSet={(u) => edit.setMd2('programs', (L.programs || []).map((p, j) => (j === i ? { ...p, img: u } : p)))}>
                    {c.img ? <img src={c.img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ display: 'block', width: '100%', height: '100%' }} />}
                  </EditableImage>
                </div>
                <div style={{ padding: '22px 22px 24px', display: 'flex', flexDirection: 'column', flex: 1 }}>
                  <span style={{ fontSize: 11, letterSpacing: '.12em', textTransform: 'uppercase', fontWeight: 700, color: MD2.accent }}><EditableText edit={md2ItemEdit(edit, skin, 'programs', i, 'eyebrow')} k={`pge${i}`} value={c.eyebrow} /></span>
                  <span style={{ fontSize: 21, fontWeight: 800, color: MD2.ink, marginTop: 6, letterSpacing: '-.3px' }}><EditableText edit={md2ItemEdit(edit, skin, 'programs', i, 'title')} k={`pgt${i}`} value={c.title} /></span>
                  <p style={{ fontSize: 13.5, color: MD2.muted, lineHeight: 1.6, margin: '10px 0 0', flex: 1 }}><EditableText edit={md2ItemEdit(edit, skin, 'programs', i, 'desc')} k={`pgd${i}`} value={c.desc} /></p>
                  <span style={{ marginTop: 18, alignSelf: 'flex-start', fontSize: 13, fontWeight: 700, background: MD2.btn, color: '#fff', borderRadius: 99, padding: '10px 26px' }}>Explore →</span>
                </div>
              </a>;
            })}
          </div>
        </div>
      </div>
    </Md2Section>

    {/* Who We Are */}
    <Md2Section edit={edit} skin={skin} k="hiw" label="Who We Are">
      <div className="md2-wrap" style={{ paddingTop: 64, paddingBottom: 44, textAlign: 'center' }}>
        <span style={{ fontSize: 12, letterSpacing: '.14em', fontWeight: 700, color: MD2.accent, textTransform: 'uppercase' }}>
          <Md2Text edit={edit} skin={skin} k="wwKicker">{(v) => <EditableText edit={edit} k="wwKicker" value={v} />}</Md2Text>
        </span>
        <h2 className="md2-h2" style={{ margin: '12px auto 0', maxWidth: 720 }}><Md2Text edit={edit} skin={skin} k="wwTitle">{(v) => <EditableText edit={edit} k="wwTitle" value={v} />}</Md2Text></h2>
        <p style={{ margin: '14px auto 0', fontSize: 16, color: MD2.sub, lineHeight: 1.6, maxWidth: 680 }}><Md2Text edit={edit} skin={skin} k="wwBody">{(v) => <EditableText edit={edit} k="wwBody" value={v} />}</Md2Text></p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 22, margin: '40px auto 0', maxWidth: 720 }}>
          {(Array.isArray(L.wwStats) ? L.wwStats : []).map((s, i) => <div key={i}>
            <div style={{ fontSize: 40, fontWeight: 800, color: MD2.accent }}><EditableText edit={md2ItemEdit(edit, skin, 'wwStats', i, 'num')} k={`wwn${i}`} value={s.num} /></div>
            <div style={{ fontSize: 14, color: MD2.muted, marginTop: 4 }}><EditableText edit={md2ItemEdit(edit, skin, 'wwStats', i, 'label')} k={`wwl${i}`} value={s.label} /></div>
          </div>)}
        </div>
      </div>
    </Md2Section>

    {/* How It Works — Simple Steps */}
    <Md2Section edit={edit} skin={skin} k="hiw2" label="How it works">
      <div id="howitworks" style={{ background: '#f8fafc', borderTop: `1px solid ${MD2.border}`, scrollMarginTop: 80 }}>
        <div className="md2-wrap" style={{ paddingTop: 56, paddingBottom: 56 }}>
          <div style={{ textAlign: 'center', marginBottom: 34 }}>
            <span style={{ fontSize: 12, letterSpacing: '.14em', fontWeight: 700, color: MD2.accent, textTransform: 'uppercase' }}>
              <Md2Text edit={edit} skin={skin} k="hiw2Kicker">{(v) => <EditableText edit={edit} k="hiw2Kicker" value={v} />}</Md2Text>
            </span>
            <h2 className="md2-h2" style={{ margin: '12px 0 0' }}><Md2Text edit={edit} skin={skin} k="hiw2Title">{(v) => <EditableText edit={edit} k="hiw2Title" value={v} />}</Md2Text></h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 22 }}>
            {(Array.isArray(L.hiwSteps) ? L.hiwSteps : []).map((s, i) => <div key={i} style={{ background: '#fff', border: `1px solid ${MD2.border}`, borderRadius: 16, padding: '24px 22px' }}>
              <span style={{ width: 36, height: 36, borderRadius: '50%', background: MD2.accent, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 800 }}>{i + 1}</span>
              <div style={{ fontSize: 16.5, fontWeight: 800, color: MD2.ink, margin: '12px 0 6px' }}><EditableText edit={md2ItemEdit(edit, skin, 'hiwSteps', i, 'title')} k={`hw2t${i}`} value={s.title} /></div>
              <p style={{ fontSize: 13.5, color: MD2.muted, lineHeight: 1.6, margin: 0 }}><EditableText edit={md2ItemEdit(edit, skin, 'hiwSteps', i, 'sub')} k={`hw2s${i}`} value={s.sub} /></p>
            </div>)}
          </div>
        </div>
      </div>
    </Md2Section>

    {/* CTA banner — full-width light band */}
    <Md2Section edit={edit} skin={skin} k="cta" label="CTA banner">
      <div style={{ background: '#fff', borderTop: `1px solid ${MD2.border}`, borderBottom: `1px solid ${MD2.border}` }}>
        <div className="md2-wrap" style={{ paddingTop: 'clamp(52px,7vw,76px)', paddingBottom: 'clamp(52px,7vw,76px)', textAlign: 'center', color: MD2.ink }}>
          <span style={{ fontSize: 12, letterSpacing: '.16em', fontWeight: 700, color: MD2.accentDark, textTransform: 'uppercase' }}>
            <Md2Text edit={edit} skin={skin} k="ctaKicker">{(v) => <EditableText edit={edit} k="ctaKicker" value={v} />}</Md2Text>
          </span>
          <h2 className="md2-h2" style={{ margin: '14px auto 0', maxWidth: 640, lineHeight: 1.15, color: MD2.ink }}>
            <Md2Text edit={edit} skin={skin} k="ctaTitle">{(v) => <EditableText edit={edit} k="ctaTitle" value={v} />}</Md2Text>
          </h2>
          <p style={{ margin: '16px auto 0', fontSize: 15, color: MD2.sub, maxWidth: 540, lineHeight: 1.6 }}>
            <Md2Text edit={edit} skin={skin} k="ctaSub">{(v) => <EditableText edit={edit} k="ctaSub" value={v} />}</Md2Text>
          </p>
          <a href={edit ? undefined : '#programs'} style={{ display: 'inline-block', marginTop: 28, fontSize: 15, fontWeight: 700, background: MD2.btn, color: '#fff', borderRadius: 99, padding: '15px 38px' }}>
            <Md2Text edit={edit} skin={skin} k="ctaButton">{(v) => <EditableText edit={edit} k="ctaButton" value={v} />}</Md2Text>
          </a>
        </div>
      </div>
    </Md2Section>
  </>;
};

// ── MD2 program landing pages (Weight Loss / Sexual Health / Longevity) ──────
// v1: hero + intro + treatments CTA. Editable per-marketplace via the pg*
// text keys; refined into full aregomd.com clones per program in later passes.
const MD2_PROGRAM_CFG = {
  'weight-loss': { key: 'wl', eyebrow: 'Weight Management', title: 'Weight Loss', sub: 'GLP-1 therapies like Semaglutide and Tirzepatide for sustainable, doctor-guided weight loss.', img: 'https://wp.aregomd.com/wp-content/uploads/2026/04/hero-img-new.png' },
  'sexual-health': { key: 'sh', eyebrow: 'Intimacy and Confidence', title: 'Sexual Health', sub: 'Physician-guided treatments to restore performance, desire, and connection.', img: 'https://wp.aregomd.com/wp-content/uploads/2026/05/hero-couple.jpg' },
  'longevity': { key: 'lo', eyebrow: 'Healthy Aging', title: 'Longevity', sub: 'Peptides and wellness protocols to boost energy, recovery, and lasting vitality.', img: 'https://wp.aregomd.com/wp-content/uploads/2026/06/aregomd-womens-hrt-hero-v3.jpg' },
};
const Md2ProgramPage = ({ skin, slug, edit, program }) => {
  const cfg = MD2_PROGRAM_CFG[program] || MD2_PROGRAM_CFG['weight-loss'];
  const qs = edit ? '?edit=1' : '';
  const t = (k, d) => skin.md2.text(`${cfg.key}${k}`) ?? d;
  return <>
    <div style={{ background: 'linear-gradient(180deg,#f4f7ff 0%,#fff 90%)' }}>
      <div className="md2-wrap md2-hero-grid" style={{ paddingTop: 56, paddingBottom: 48 }}>
        <div>
          <span style={{ display: 'inline-block', fontSize: 12, letterSpacing: '.14em', fontWeight: 700, color: MD2.accent, textTransform: 'uppercase' }}>
            <EditableText edit={edit} k={`${cfg.key}Eyebrow`} value={t('Eyebrow', cfg.eyebrow)} />
          </span>
          <h1 className="md2-h1" style={{ margin: '14px 0 0' }}><EditableText edit={edit} k={`${cfg.key}Title`} value={t('Title', cfg.title)} /></h1>
          <p style={{ margin: '18px 0 0', fontSize: 17, lineHeight: 1.6, color: MD2.sub, maxWidth: 480 }}><EditableText edit={edit} k={`${cfg.key}Sub`} value={t('Sub', cfg.sub)} /></p>
          <div style={{ display: 'flex', gap: 12, marginTop: 28, flexWrap: 'wrap' }}>
            <a href={edit ? undefined : `/marketplace/${slug}/shop${qs}`} style={md2BtnPrimary}>View treatments</a>
            <a href={edit ? undefined : `/marketplace/${slug}${qs}#howitworks`} style={md2BtnGhost}>How it works</a>
          </div>
        </div>
        <div>
          <EditableImage edit={edit} k={`${cfg.key}Hero`} label="hero image">
            <span className="md2-hero-visual" style={{ display: 'block', height: 440, borderRadius: 22, background: md2Stripes, overflow: 'hidden' }}>
              <img src={skin.md2.text(`${cfg.key}Hero`) || cfg.img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </span>
          </EditableImage>
        </div>
      </div>
    </div>
    <div style={{ background: MD2.navy }}>
      <div className="md2-wrap" style={{ paddingTop: 64, paddingBottom: 72 }}>
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <h2 className="md2-h2" style={{ margin: 0, color: '#fff', fontSize: 'clamp(28px,3.8vw,36px)' }}>Available treatments</h2>
          <p style={{ margin: '10px auto 0', fontSize: 15, color: '#9aa7c7', maxWidth: 520 }}>Every protocol includes a licensed provider consult, prescription review and free 1–2 day cold-chain shipping.</p>
        </div>
        <div className="md2-cards">
          {skin.products.filter((p) => !isEnrollmentOnlyProduct(skin, p.id)).map((p) => <Md2Card key={p.id} skin={skin} slug={slug} edit={edit} p={p} onNavy />)}
        </div>
        <div style={{ textAlign: 'center', marginTop: 28, fontSize: 12.5, color: '#9aa7c7' }}>
          <Md2Text edit={edit} skin={skin} k="portalNote">{(v) => <EditableText edit={edit} k="portalNote" value={v} />}</Md2Text>
        </div>
      </div>
    </div>
  </>;
};

// The "Research Study" cards on the AregoMD Weight Loss and Longevity pages
// send visitors to the AregoRX store, which lives on its own domain. A relative
// /marketplace/aregorx would be resolved against whatever domain the visitor is
// currently on — on aregomd.com the masked router strips the prefix and bounces
// them straight back to the AregoMD home page — so link to it absolutely.
const AREGORX_STORE_URL = 'https://aregorx.com';

// ── Weight Loss page — faithful clone of aregomd.com/home-2, recolored to the
// md2 palette. All copy/lists editable via the wl* keys (md2Defaults).
const Md2WeightLoss = ({ skin, slug, edit }) => {
  const qs = edit ? '?edit=1' : '';
  const L = skin.md2.lists;
  const T = (k) => <Md2Text edit={edit} skin={skin} k={k}>{(v) => <EditableText edit={edit} k={k} value={v} />}</Md2Text>;
  const shop = edit ? undefined : `/marketplace/${slug}/shop${qs}`;
  const researchImg = skin.mkt.edits?.images?.wlResearchImg || 'https://wp.aregomd.com/wp-content/uploads/2026/04/Protocol1-Reta.avif';
  const glpImg = skin.mkt.edits?.images?.wlGlpImg || 'https://wp.aregomd.com/wp-content/uploads/2026/04/women-health-center.png';
  const phoneImg = skin.mkt.edits?.images?.wlPhoneImg || 'https://wp.aregomd.com/wp-content/uploads/2025/10/Woman-using-phone.png';
  const stepBottle = 'https://wp.aregomd.com/wp-content/uploads/2026/02/semi-gp1.png';
  const benefitsImg = skin.mkt.edits?.images?.wlBenefitsImg || blobUrl('17862458940963751');
  const plane = <svg width="17" height="17" viewBox="0 0 24 24" fill="none" style={{ flex: 'none' }}><path d="M21.5 2.5L2 10.8l7.2 2.6 2.6 7.6 9.7-18.5z" stroke="#c2a06a" strokeWidth="1.4" strokeLinejoin="round" strokeLinecap="round" /><path d="M9.2 13.4l4.4-4.4" stroke="#c2a06a" strokeWidth="1.4" strokeLinecap="round" /></svg>;
  const wlP = skin.products.filter((p) => !isEnrollmentOnlyProduct(skin, p.id) && !/nad/i.test(p.name || '') && /sema|tirz|glp|weight|b12/i.test(`${p.name} ${md2ProductInfo(skin.mkt, p.id).category || ''}`));
  const products = wlP.length ? wlP : skin.products.filter((p) => !isEnrollmentOnlyProduct(skin, p.id));
  const kicker = (k) => <span style={{ fontSize: 12, letterSpacing: '.14em', fontWeight: 700, color: MD2.accent, textTransform: 'uppercase' }}>{T(k)}</span>;
  return <>
    <div style={{ background: MD2.accent, color: '#fff', textAlign: 'center', fontSize: 13, fontWeight: 600, padding: '9px 20px' }}>{T('wlAnnounce')}</div>
    {/* Hero */}
    <div style={{ background: 'linear-gradient(180deg,#f4f7ff 0%,#fff 92%)' }}>
      <div className="md2-wrap md2-hero-grid" style={{ paddingTop: 52, paddingBottom: 46 }}>
        <div>
          {kicker('wlHeroKicker')}
          <h1 className="md2-h1" style={{ margin: '14px 0 0' }}>{T('wlHeroTitle')}</h1>
          <p style={{ margin: '16px 0 0', fontSize: 16.5, lineHeight: 1.6, color: MD2.sub, maxWidth: 500 }}>{T('wlHeroBody')}</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, margin: '20px 0 0', fontSize: 14.5, color: '#344054' }}>
            {(L.wlHeroChecks || []).map((c, i) => <span key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>{!/^\s*[\u{1F000}-\u{1FAFF}\u{2600}-\u{27BF}\u{1F1E6}-\u{1F1FF}]/u.test(String(c)) && <span style={{ width: 20, height: 20, borderRadius: '50%', background: '#e8f6ee', color: '#1d7a46', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, flex: 'none' }}>✓</span>}<EditableText edit={md2ItemEdit(edit, skin, 'wlHeroChecks', i)} k={`wlhc${i}`} value={c} /></span>)}
          </div>
          <a href={edit ? undefined : '#wl-products'} style={{ ...md2BtnPrimary, marginTop: 26 }}>{T('wlHeroCta')}</a>
          <p style={{ margin: '14px 0 0', fontSize: 11.5, color: MD2.faint }}>{T('wlHeroDisc')}</p>
        </div>
        <div><EditableImage edit={edit} k="wlHero" label="hero image"><span className="md2-hero-visual" style={{ display: 'block', height: 460, borderRadius: 22, background: MD2.tanSoft, overflow: 'hidden' }}><img src={skin.mkt.edits?.images?.wlHero || 'https://wp.aregomd.com/wp-content/uploads/2026/04/hero-imga_girl2.png'} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} /></span></EditableImage></div>
      </div>
    </div>
    {/* Category bands — full-width, product image half-outside the box */}
    <div className="md2-wrap" style={{ paddingTop: 48, display: 'grid', gap: 26 }}>
      {(L.wlTiles || []).map((t, i) => {
        const side = t.side === 'right' ? 'right' : 'left';
        return <div key={i} className={`wl-band wl-band-${i} wl-band-${side}`}>
          {t.img && <img src={t.img} alt="" className={`wl-bandimg wl-bandimg-${i}`} style={{ [side]: 0 }} />}
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, letterSpacing: '.12em', fontWeight: 700, color: MD2.accent, textTransform: 'uppercase' }}><EditableText edit={md2ItemEdit(edit, skin, 'wlTiles', i, 'kicker')} k={`wltk${i}`} value={t.kicker} /></div>
            <p style={{ fontSize: 15, color: MD2.sub, lineHeight: 1.6, margin: '12px 0 20px', maxWidth: 560 }}><EditableText edit={md2ItemEdit(edit, skin, 'wlTiles', i, 'desc')} k={`wltd${i}`} value={t.desc} /></p>
            <a href={edit ? undefined : '#wl-products'} style={{ ...md2BtnPrimary, fontSize: 13.5, padding: '13px 30px' }}><EditableText edit={md2ItemEdit(edit, skin, 'wlTiles', i, 'btn')} k={`wltb${i}`} value={t.btn} /></a>
          </div>
        </div>;
      })}
    </div>
    {/* Products — 2 wide cards span the full width; 3 across otherwise */}
    <div id="wl-products" className="md2-wrap" style={{ paddingTop: 56, paddingBottom: 56, scrollMarginTop: 80 }}>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(products.length, 3) || 1},1fr)`, gap: 24 }} className="md2-prodgrid">{products.map((p) => <Md2Card key={p.id} skin={skin} slug={slug} edit={edit} p={p} big={products.length <= 2} />)}</div>
      <p style={{ margin: '22px 0 0', fontSize: 10.5, color: MD2.faint, lineHeight: 1.7 }}>{T('wlProductsDisc')}</p>
    </div>
    {/* Research study — white card, bottle on the left */}
    <div style={{ background: '#f5f7fa' }}><div className="md2-wrap" style={{ paddingTop: 38, paddingBottom: 38 }}>
      <div style={{ background: '#fff', border: `1px solid ${MD2.border}`, borderRadius: 18, boxShadow: '0 16px 40px rgba(34,29,24,.06)', padding: 'clamp(20px,2.6vw,30px)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 14 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><rect x="4.5" y="10.5" width="15" height="10" rx="2" stroke="#8b6fd6" strokeWidth="1.8" /><path d="M8 10.5V8a4 4 0 0 1 8 0v2.5" stroke="#8b6fd6" strokeWidth="1.8" /></svg>
          <span style={{ fontSize: 12, letterSpacing: '.14em', color: MD2.sub, fontWeight: 700, textTransform: 'uppercase' }}>{T('wlResearchKicker')}</span>
        </div>
        <div className="wl-research-grid" style={{ display: 'grid', gridTemplateColumns: '190px 1fr', gap: 32, alignItems: 'center' }}>
          <div style={{ textAlign: 'center' }}>
            <EditableImage edit={edit} k="wlResearchImg" label="research image" inline value={researchImg}>
              <img src={researchImg} alt="" style={{ maxWidth: 180, width: '100%', maxHeight: 210, objectFit: 'contain', filter: 'drop-shadow(0 18px 30px rgba(34,29,24,.16))' }} />
            </EditableImage>
          </div>
          <div>
            <h2 style={{ fontFamily: "'Playfair Display',Georgia,serif", fontSize: 'clamp(21px,2.4vw,27px)', fontWeight: 700, color: MD2.ink, margin: 0, lineHeight: 1.2 }}>{T('wlResearchTitle')}</h2>
            <p style={{ margin: '12px 0 0', fontSize: 14.5, color: MD2.sub, lineHeight: 1.6 }}>{T('wlResearchBody')}</p>
            <p style={{ margin: '10px 0 0', fontSize: 13, fontStyle: 'italic', color: MD2.muted, lineHeight: 1.55 }}>{T('wlResearchDisc')}</p>
            <a href={edit ? undefined : AREGORX_STORE_URL} style={{ display: 'inline-block', marginTop: 18, fontSize: 14, fontWeight: 700, background: MD2.btn, color: '#fff', borderRadius: 99, padding: '11px 28px' }}>{T('wlResearchCta')}</a>
          </div>
        </div>
      </div>
    </div></div>
    {/* Citations */}
    <div className="md2-wrap md2-cite-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 22, paddingTop: 48 }}>
      {(L.wlCitations || []).map((c, i) => <div key={i} style={{ border: `1px solid ${MD2.border}`, borderRadius: 16, padding: '22px 22px' }}>
        <div style={{ height: 34, display: 'flex', alignItems: 'center', marginBottom: 4 }}>
          {c.logo ? <img src={c.logo} alt="" style={{ maxHeight: 34, maxWidth: 170, objectFit: 'contain' }} /> : <span style={{ fontSize: 12, fontWeight: 800, color: MD2.accent }}>{c.source}</span>}
        </div>
        <div style={{ fontSize: 15.5, fontWeight: 700, color: MD2.ink, margin: '8px 0 6px' }}><EditableText edit={md2ItemEdit(edit, skin, 'wlCitations', i, 'title')} k={`wlct${i}`} value={c.title} /></div>
        <p style={{ fontSize: 13, color: MD2.muted, lineHeight: 1.6, margin: 0 }}><EditableText edit={md2ItemEdit(edit, skin, 'wlCitations', i, 'body')} k={`wlcb${i}`} value={c.body} /></p>
      </div>)}
    </div>
    {/* Trust badges */}
    {(L.wlTrust || []).length > 0 && <div style={{ background: MD2.navy, marginTop: 44 }}>
      <div className="md2-wrap" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 30, flexWrap: 'wrap', paddingTop: 26, paddingBottom: 26 }}>
        {(L.wlTrust || []).map((src, i) => <img key={i} src={src} alt="" style={{ height: 30, maxWidth: 200, objectFit: 'contain' }} />)}
      </div>
    </div>}
    {/* GLP-1 Weight Loss — dark feature: stat card + woman image + What's Included chips */}
    <div style={{ background: '#211c17' }}>
      <div className="md2-wrap" style={{ paddingTop: 'clamp(48px,6vw,72px)', paddingBottom: 0 }}>
        <div style={{ textAlign: 'center', marginBottom: 'clamp(26px,4vw,44px)' }}>
          <span style={{ fontSize: 12.5, letterSpacing: '.18em', fontWeight: 600, color: 'rgba(255,255,255,.72)', textTransform: 'uppercase' }}>{T('wlStatKicker')}</span>
          <h2 style={{ fontFamily: "'Playfair Display',Georgia,serif", fontSize: 'clamp(28px,4.2vw,50px)', fontWeight: 700, color: '#b9a38d', margin: '14px 0 0', lineHeight: 1.12 }}>{T('wlStatTitle')}</h2>
        </div>
        <div className="wl-glp-grid">
          <div style={{ textAlign: 'center' }}>
            <p style={{ fontSize: 16, color: 'rgba(255,255,255,.9)', lineHeight: 1.55, margin: 0 }}>{T('wlStatLead')}</p>
            <div style={{ background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.14)', borderRadius: 16, padding: '26px 24px', margin: '22px 0' }}>
              <div style={{ fontFamily: "'Playfair Display',Georgia,serif", fontSize: 'clamp(40px,5vw,58px)', fontWeight: 700, color: '#fff', lineHeight: 1 }}>{T('wlStatNum')}</div>
              <div style={{ fontSize: 14, color: 'rgba(255,255,255,.7)', marginTop: 10 }}>{T('wlStatNumLabel')}</div>
            </div>
            <p style={{ fontSize: 13.5, color: 'rgba(255,255,255,.6)', lineHeight: 1.65, margin: 0 }}>{T('wlStatBody')}</p>
          </div>
          <div style={{ textAlign: 'center' }}>
            <EditableImage edit={edit} k="wlGlpImg" label="section image" inline value={glpImg}>
              <img src={glpImg} alt="" style={{ maxWidth: '100%', maxHeight: 560, objectFit: 'contain' }} />
            </EditableImage>
          </div>
          <div>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: '#fff', margin: '0 0 18px' }}>{T('wlIncludedTitle')}</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>{(L.wlIncluded || []).map((x, i) => <span key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.14)', borderRadius: 12, padding: '13px 18px', fontSize: 14.5, color: '#fff' }}>{plane}<EditableText edit={md2ItemEdit(edit, skin, 'wlIncluded', i)} k={`wli${i}`} value={x} /></span>)}</div>
          </div>
        </div>
      </div>
    </div>
    {/* Telehealth — Simple Steps (left) + woman-on-phone image with bottle (right) */}
    <div style={{ background: '#fff', borderTop: `1px solid ${MD2.border}` }}>
      <div className="md2-wrap md2-tele-grid" style={{ paddingTop: 'clamp(44px,6vw,68px)', paddingBottom: 'clamp(44px,6vw,68px)' }}>
        <div>
          <span style={{ fontSize: 12.5, letterSpacing: '.16em', fontWeight: 600, color: MD2.sub, textTransform: 'uppercase' }}>{T('wlStepsKicker')}</span>
          <h2 style={{ fontFamily: "'Playfair Display',Georgia,serif", fontSize: 'clamp(28px,3.6vw,44px)', fontWeight: 700, color: '#b9a38d', margin: '10px 0 0', lineHeight: 1.1 }}>{T('wlStepsTitle')}</h2>
          <p style={{ margin: '10px 0 0', fontSize: 15, color: MD2.muted }}>{T('wlStepsSub')}</p>
          <div style={{ marginTop: 'clamp(24px,3vw,38px)', display: 'flex', flexDirection: 'column', gap: 28 }}>
            {(L.wlSteps || []).map((s, i) => <div key={i} style={{ display: 'flex', gap: 20 }}>
              <span style={{ flex: 'none', width: 52, height: 52, borderRadius: '50%', border: `1.5px solid ${MD2.ink}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Playfair Display',Georgia,serif", fontSize: 19, fontWeight: 600, color: MD2.ink }}>{String(i + 1).padStart(2, '0')}</span>
              <div>
                {s.time && <div style={{ fontSize: 13.5, fontStyle: 'italic', color: MD2.muted, marginBottom: 3 }}><EditableText edit={md2ItemEdit(edit, skin, 'wlSteps', i, 'time')} k={`wlsti${i}`} value={s.time} /></div>}
                <div style={{ fontSize: 18, fontWeight: 800, color: MD2.ink, marginBottom: 6 }}><EditableText edit={md2ItemEdit(edit, skin, 'wlSteps', i, 'title')} k={`wlst${i}`} value={s.title} /></div>
                <p style={{ fontSize: 14, color: MD2.sub, lineHeight: 1.6, margin: 0, maxWidth: 470 }}><EditableText edit={md2ItemEdit(edit, skin, 'wlSteps', i, 'sub')} k={`wlss${i}`} value={s.sub} /></p>
              </div>
            </div>)}
          </div>
        </div>
        <div>
          <div style={{ position: 'relative' }}>
            <EditableImage edit={edit} k="wlPhoneImg" label="section image" inline value={phoneImg}>
              <img src={phoneImg} alt="" style={{ width: '100%', borderRadius: 16, display: 'block' }} />
            </EditableImage>
            <img src={stepBottle} alt="" className="wl-tele-bottle" style={{ position: 'absolute', right: 0, bottom: '6%', width: 'clamp(88px,11vw,140px)', transform: 'translateX(40%)', filter: 'drop-shadow(0 18px 30px rgba(0,0,0,.3))', pointerEvents: 'none' }} />
          </div>
          <p style={{ margin: '30px 0 0', fontSize: 13, color: MD2.muted, lineHeight: 1.65 }}>{T('wlStepsDisc')}</p>
        </div>
      </div>
    </div>
    {/* Real People — dark translucent testimonial cards */}
    <div style={{ background: '#211c17' }}>
      <div className="md2-wrap" style={{ paddingTop: 'clamp(48px,6vw,72px)', paddingBottom: 'clamp(40px,5vw,60px)' }}>
        <h2 style={{ textAlign: 'center', fontFamily: "'Playfair Display',Georgia,serif", fontSize: 'clamp(28px,3.6vw,44px)', fontWeight: 700, margin: '0 0 clamp(28px,4vw,46px)', color: '#fff' }}>
          <span style={{ color: '#b9a38d' }}>Real</span> People. <span style={{ color: '#b9a38d' }}>Actual</span> Results.
        </h2>
        <div className="md2-testi-grid">{(L.wlTestimonials || []).map((tt, i) => <div key={i} style={{ background: 'rgba(255,255,255,.07)', border: '1px solid rgba(255,255,255,.14)', borderRadius: 16, padding: 'clamp(24px,2.4vw,32px)', display: 'flex', flexDirection: 'column' }}>
          <div style={{ fontFamily: "'Playfair Display',Georgia,serif", fontSize: 22, fontWeight: 700, color: '#fff', lineHeight: 1.25 }}><EditableText edit={md2ItemEdit(edit, skin, 'wlTestimonials', i, 'title')} k={`wltt${i}`} value={tt.title} /></div>
          <p style={{ fontSize: 14.5, color: 'rgba(255,255,255,.72)', lineHeight: 1.65, margin: '16px 0 0', flex: 1 }}>“<EditableText edit={md2ItemEdit(edit, skin, 'wlTestimonials', i, 'quote')} k={`wltq${i}`} value={tt.quote} />”</p>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#fff', marginTop: 24 }}><EditableText edit={md2ItemEdit(edit, skin, 'wlTestimonials', i, 'name')} k={`wltn${i}`} value={tt.name} /></div>
          <div style={{ color: '#e8dcc4', fontSize: 15, letterSpacing: 3, marginTop: 10 }}>★★★★★</div>
        </div>)}</div>
        <p style={{ margin: '30px auto 0', fontSize: 12.5, color: 'rgba(255,255,255,.45)', textAlign: 'center' }}>{T('wlTestiDisc')}</p>
      </div>
    </div>
    {/* Benefits — copy on the left, vials image on the right */}
    <div className="md2-wrap md2-benefits-grid" style={{ paddingTop: 'clamp(44px,5vw,60px)', paddingBottom: 'clamp(48px,6vw,72px)' }}>
      <div>
        {kicker('wlBenefitsKicker')}
        <h2 className="md2-h2" style={{ margin: '12px 0 0' }}>{T('wlBenefitsTitle')}</h2>
        <p style={{ margin: '12px 0 16px', fontSize: 15, color: MD2.sub, lineHeight: 1.6 }}>{T('wlBenefitsIntro')}</p>
        <ul style={{ margin: 0, paddingLeft: 22, color: MD2.sub, fontSize: 14.5, lineHeight: 2 }}>{(L.wlBenefits || []).map((b, i) => <li key={i}><EditableText edit={md2ItemEdit(edit, skin, 'wlBenefits', i)} k={`wlb${i}`} value={b} /></li>)}</ul>
      </div>
      <div style={{ textAlign: 'center' }}>
        <EditableImage edit={edit} k="wlBenefitsImg" label="benefits image" inline value={benefitsImg}>
          <img src={benefitsImg} alt="" style={{ maxWidth: '100%', maxHeight: 420, objectFit: 'contain', filter: 'drop-shadow(0 22px 40px rgba(34,29,24,.16))' }} />
        </EditableImage>
      </div>
    </div>
  </>;
};

const Md2SexualHealth = ({ skin, slug, edit }) => {
  const [tab, setTab] = useState(0);
  const qs = edit ? '?edit=1' : '';
  const L = skin.md2.lists;
  const T = (k) => <Md2Text edit={edit} skin={skin} k={k}>{(v) => <EditableText edit={edit} k={k} value={v} />}</Md2Text>;
  const pt = skin.products.find((p) => /pt-?141|bremelanotide/i.test(p.name || ''));
  const ptId = pt ? pt.id : 'C-PROD000239';
  const cta = edit ? undefined : `/marketplace/${slug}/portal/${encodeURIComponent(ptId)}`;
  const vialImg = skin.mkt.edits?.images?.shHero || (pt && pt.imageUrl) || 'https://wp.aregomd.com/wp-content/uploads/2026/05/hero-couple.jpg';
  const heroBg = skin.md2.text('shHeroBg');
  const usageBg = skin.md2.text('shUsageBg');
  const TABS = ['Description', 'Chemical Info', 'Storage', 'Research'];
  const truck = <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={{ flex: 'none' }}><path d="M2 6.5h11.5v9.5H2z" stroke={MD2.ink} strokeWidth="1.5" /><path d="M13.5 9.5H18l3 3.2V16h-7.5z" stroke={MD2.ink} strokeWidth="1.5" strokeLinejoin="round" /><circle cx="6.5" cy="18" r="1.7" fill={MD2.ink} /><circle cx="17.5" cy="18" r="1.7" fill={MD2.ink} /></svg>;
  return <>
    {/* Hero band (bg image, text right) + white content with the vial overlapping up */}
    <div style={{ position: 'relative' }}>
      <div style={{ position: 'relative', backgroundImage: heroBg ? `url(${heroBg})` : undefined, backgroundSize: 'cover', backgroundPosition: 'center', minHeight: 380 }}>
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg,rgba(24,18,12,.35),rgba(24,18,12,.5))' }} />
        <div className="md2-wrap md2-hero-grid" style={{ position: 'relative', paddingTop: 60, paddingBottom: 60, alignItems: 'center' }}>
          <div />
          <div>
            <span style={{ fontSize: 13, letterSpacing: '.12em', fontWeight: 600, color: 'rgba(255,255,255,.85)', textTransform: 'uppercase' }}>{T('shKicker')}</span>
            <h1 style={{ margin: '12px 0 0', color: '#fff', fontFamily: "'Playfair Display',Georgia,serif", fontSize: 'clamp(26px,3.1vw,38px)', fontWeight: 700, lineHeight: 1.22 }}>{T('shTitle')}</h1>
            <p style={{ margin: '20px 0 0', fontSize: 13, letterSpacing: '.1em', fontWeight: 600, color: 'rgba(255,255,255,.82)', textTransform: 'uppercase' }}>{T('shSubtitle')}</p>
          </div>
        </div>
      </div>
      <div style={{ height: 8, background: MD2.tan }} />
      <div style={{ background: '#fff' }}>
        <div className="md2-wrap md2-hero-grid" style={{ paddingTop: 30, paddingBottom: 60, alignItems: 'start' }}>
          <div className="sh-vial">
            <EditableImage edit={edit} k="shHero" label="product image" inline>
              <img src={vialImg} alt="" style={{ maxWidth: 500, width: '100%', objectFit: 'contain', filter: 'drop-shadow(0 26px 40px rgba(0,0,0,.28))' }} />
            </EditableImage>
            {skin.md2.text('shBadges') && <img src={skin.md2.text('shBadges')} alt="" style={{ width: 200, maxWidth: '100%', marginTop: 26, objectFit: 'contain' }} />}
          </div>
          <div style={{ paddingTop: 30 }}>
            <h2 style={{ fontSize: 30, fontWeight: 800, color: MD2.ink, margin: 0 }}>{T('shProduct')}</h2>
            <p style={{ margin: '14px 0 0', fontSize: 15, lineHeight: 1.65, color: MD2.sub }}>{T('shDesc')}</p>
            <a href={cta} style={{ ...md2BtnGhost, marginTop: 24 }}>{T('shCta')}</a>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 22, paddingBottom: 20, borderBottom: `1px solid ${MD2.border}`, fontSize: 15, color: MD2.ink }}>{truck}<span><b>FREE Shipping</b> <span style={{ color: MD2.muted }}>on orders over $100</span></span></div>
            <div style={{ marginTop: 20 }}>
              <div style={{ fontSize: 12.5, fontWeight: 800, letterSpacing: '.04em', color: MD2.ink }}>{T('shFormulaLabel')}</div>
              <div style={{ fontSize: 14, color: MD2.muted, marginTop: 2 }}>{T('shFormula')}</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 22 }}>
              {skin.md2.text('shSteth') && <img src={skin.md2.text('shSteth')} alt="" style={{ height: 30, width: 30, objectFit: 'contain' }} />}
              <span style={{ fontSize: 17, color: MD2.ink }}><b>Medical Consultation</b> <span style={{ color: MD2.muted }}>included</span></span>
            </div>
            <p style={{ margin: '14px 0 0', fontSize: 13.5, color: MD2.muted, lineHeight: 1.7 }}>{T('shConsult')}</p>
            <p style={{ margin: '12px 0 0', fontSize: 13.5, color: MD2.muted, lineHeight: 1.7 }}>{T('shConsult2')}</p>
          </div>
        </div>
      </div>
    </div>
    {/* Product usage — transparent disclaimer with attention icon */}
    <div style={{ background: 'transparent' }}>
      <div className="md2-wrap" style={{ paddingTop: 36, paddingBottom: 36 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '31px 1fr', gap: '10px 15px', background: 'transparent', padding: 20, borderRadius: 10 }}>
          <div style={{ gridColumn: 1, gridRow: '1 / 3' }}><img src="https://wp.aregorx.com/wp-content/uploads/2026/02/attention.png.png" alt="" style={{ width: 31, height: 31, objectFit: 'cover', display: 'block' }} /></div>
          <p style={{ gridColumn: 2, gridRow: 1, fontSize: 14, fontWeight: 700, margin: 0, lineHeight: 1.4, color: '#0F2B32' }}>{T('shUsageTitle')}</p>
          <p style={{ gridColumn: 2, gridRow: 2, fontSize: 14, lineHeight: 1.4, textAlign: 'justify', margin: 0, color: '#0F2B32' }}>{T('shUsage')}</p>
        </div>
      </div>
    </div>
    {/* Tabs — slightly light background */}
    <div style={{ background: '#f9f9f9' }}>
      <div className="md2-wrap" style={{ paddingTop: 44, paddingBottom: 64 }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {TABS.map((t, i) => <span key={i} onClick={() => setTab(i)} style={{ cursor: 'pointer', fontSize: 14, fontWeight: 700, padding: '12px 22px', borderRadius: 10, border: `1px solid ${tab === i ? MD2.btn : MD2.border}`, background: tab === i ? MD2.btn : '#fff', color: tab === i ? '#fff' : MD2.ink }}>{t}</span>)}
        </div>
        <div style={{ border: `1px solid ${MD2.border}`, borderRadius: 16, padding: '26px 28px', marginTop: 14, background: '#fff' }}>
          {tab === 0 && <><h3 style={{ fontSize: 19, fontWeight: 800, color: MD2.ink, margin: '0 0 12px' }}>{T('shDescTitle')}</h3><p style={{ fontSize: 14.5, color: MD2.sub, lineHeight: 1.7, margin: 0 }}>{T('shDesc')}</p></>}
          {tab === 1 && <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 32px' }}>{(L.shChem || []).map((r, i) => <div key={i} style={{ borderBottom: `1px solid ${MD2.border}`, paddingBottom: 10 }}><div style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: MD2.faint }}><EditableText edit={md2ItemEdit(edit, skin, 'shChem', i, 'k')} k={`shck${i}`} value={r.k} /></div><div style={{ fontSize: 15, fontWeight: 700, color: MD2.ink, marginTop: 3 }}><EditableText edit={md2ItemEdit(edit, skin, 'shChem', i, 'v')} k={`shcv${i}`} value={r.v} /></div></div>)}</div>}
          {tab === 2 && <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px 32px' }}>{(L.shStorage || []).map((s, i) => <span key={i} style={{ display: 'flex', gap: 10, fontSize: 14, color: MD2.sub, lineHeight: 1.5 }}><span style={{ width: 20, height: 20, borderRadius: '50%', background: MD2.accent, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, flex: 'none' }}>✓</span><EditableText edit={md2ItemEdit(edit, skin, 'shStorage', i)} k={`shs${i}`} value={s} /></span>)}</div>}
          {tab === 3 && (L.shResearch || []).map((r, i) => <div key={i} style={{ marginBottom: 18 }}><h4 style={{ fontSize: 16, fontWeight: 800, color: MD2.ink, margin: '0 0 6px', borderBottom: `1px solid ${MD2.border}`, paddingBottom: 8 }}><EditableText edit={md2ItemEdit(edit, skin, 'shResearch', i, 'h')} k={`shrh${i}`} value={r.h} /></h4><p style={{ fontSize: 14, color: MD2.muted, lineHeight: 1.7, margin: 0 }}><EditableText edit={md2ItemEdit(edit, skin, 'shResearch', i, 'b')} k={`shrb${i}`} value={r.b} /></p></div>)}
        </div>
      </div>
    </div>
  </>;
};

// ── Longevity page — clone of aregomd.com/arego-peptides. Shows the store's
// peptide products (GLP-1 weight products excluded) in the reference order.
const LO_RANK = (name) => {
  const n = String(name || '').toLowerCase();
  if (/wolverine/.test(n) || (n.includes('bpc') && n.includes('tb'))) return 9;
  if (/^bpc-?157$/.test(n) || (n.includes('bpc') && !n.includes('tb'))) return 0;
  if (n.includes('cjc') || n.includes('ipamorelin')) return 1;
  if (n.includes('ghk')) return 2;
  if (n.includes('lipo')) return 3;
  if (n.includes('mots')) return 4;
  if (n.includes('nad')) return 5;
  if (n.includes('pt-141') || n.includes('pt141') || n.includes('bremelanotide')) return 6;
  if (n.includes('sermorelin')) return 7;
  if (n.includes('tb-500') || n.includes('tb500')) return 8;
  return 50;
};
const loDefaultDesc = (name) => {
  const n = String(name || '').toLowerCase();
  if (/wolverine/.test(n) || (n.includes('bpc') && n.includes('tb'))) return 'BPC-157/TB-500 is a synthetic peptide blend combining two potent compounds: BPC-157, derived from Body Protection';
  if (n.includes('bpc')) return 'BPC-157 is a high-purity peptide offered in a 15mg lyophilized powder format, intended strictly for laboratory research';
  if (n.includes('cjc') || n.includes('ipamorelin')) return 'This dual-peptide complex combines the GHRH analog CJC-1295 (No DAC) with the selective Ghrelin mimetic Ipamorelin to...';
  if (n.includes('ghk')) return 'GHK-Cu (glycyl-L-histidyl-L-lysine copper) is a naturally occurring copper peptide that plays a critical role in tissue remodeling';
  if (n.includes('lipo')) return 'Lipo-C Injections are a powerful, non-invasive treatment designed to support fat loss, boost metabolism, and increase energy. This unique';
  if (n.includes('mots')) return 'MOTS-C is a specialized research reagent encoded by the mitochondrial DNA rather than nuclear DNA.';
  if (n.includes('pt-141') || n.includes('pt141') || n.includes('bremelanotide')) return 'PT-141 (Bremelanotide) is a melanocortin receptor agonist used for sexual dysfunction in men and women by activating brain pathways, rather than just blood flow';
  if (n.includes('sermorelin')) return 'Sermorelin is a synthetic peptide widely researched for its ability to stimulate growth hormone release.';
  if (n.includes('tb-500') || n.includes('tb500')) return 'TB-500 is a synthetic peptide commonly referenced in research settings for its role in cellular migration and tissue support processes.';
  return '';
};
const loTrustIcon = (label) => {
  const l = String(label || '').toLowerCase();
  const s = { width: 30, height: 30, viewBox: '0 0 24 24', fill: 'none', stroke: '#fff', strokeWidth: 1.6, strokeLinecap: 'round', strokeLinejoin: 'round' };
  if (l.includes('purity') || l.includes('99')) return <svg {...s}><path d="M9 3h6M10 3v6.2L6.2 17.4A2 2 0 0 0 8 20.4h8a2 2 0 0 0 1.8-3L14 9.2V3" /><path d="M8.6 14h6.8" /></svg>;
  if (l.includes('tested') || l.includes('usa')) return <svg {...s}><path d="M12 3l7 3v5c0 4.5-3 7.6-7 9-4-1.4-7-4.5-7-9V6l7-3z" /><path d="M9 12l2 2 4-4" /></svg>;
  if (l.includes('shipping') || l.includes('day')) return <svg {...s}><path d="M2 6.5h10.5v9H2z" /><path d="M12.5 9.5H17l4 3.5v2.5h-8.5z" /><circle cx="6" cy="18" r="1.6" /><circle cx="17" cy="18" r="1.6" /></svg>;
  return <svg {...s}><path d="M4 13v-1a8 8 0 0 1 16 0v1" /><rect x="3" y="13" width="4" height="6.5" rx="1.5" /><rect x="17" y="13" width="4" height="6.5" rx="1.5" /><path d="M20 19a5 5 0 0 1-5 4h-2" /></svg>;
};
const Md2Longevity = ({ skin, slug, edit }) => {
  const qs = edit ? '?edit=1' : '';
  const L = skin.md2.lists;
  const T = (k) => <Md2Text edit={edit} skin={skin} k={k}>{(v) => <EditableText edit={edit} k={k} value={v} />}</Md2Text>;
  const shop = edit ? undefined : `/marketplace/${slug}/shop${qs}`;
  const heroBg = skin.mkt.edits?.images?.loHero || 'https://wp.aregomd.com/wp-content/uploads/2026/06/arego-longevity-hero-scaled.jpg';
  const researchImg = skin.mkt.edits?.images?.wlResearchImg || 'https://wp.aregomd.com/wp-content/uploads/2026/04/Protocol1-Reta.avif';
  const lock = <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><rect x="4.5" y="10.5" width="15" height="10" rx="2" stroke="#8b6fd6" strokeWidth="1.8" /><path d="M8 10.5V8a4 4 0 0 1 8 0v2.5" stroke="#8b6fd6" strokeWidth="1.8" /></svg>;
  const products = skin.products
    .filter((p) => !isEnrollmentOnlyProduct(skin, p.id) && !/semaglutide|tirzepatide|\bglp\b/i.test(p.name || ''))
    .sort((a, b) => LO_RANK(a.name) - LO_RANK(b.name));
  return <>
    {/* Hero — runners background */}
    <div style={{ position: 'relative', backgroundImage: `linear-gradient(90deg,rgba(8,14,22,.62) 0%,rgba(8,14,22,.12) 68%),url(${heroBg})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
      <div className="md2-wrap" style={{ minHeight: 'clamp(360px,42vw,480px)', display: 'flex', flexDirection: 'column', justifyContent: 'center', paddingTop: 40, paddingBottom: 40 }}>
        <h1 style={{ fontFamily: "'Playfair Display',Georgia,serif", fontSize: 'clamp(30px,4.4vw,52px)', fontWeight: 700, color: '#fff', margin: 0, lineHeight: 1.14, maxWidth: 640 }}>
          <span style={{ display: 'block' }}>{T('loKicker')}</span>
          <span style={{ display: 'block' }}>{T('loTitle')}</span>
        </h1>
        <p style={{ margin: '18px 0 0', fontSize: 17, color: 'rgba(255,255,255,.9)', maxWidth: 440, lineHeight: 1.55 }}>{T('loIntro')}</p>
      </div>
    </div>
    <div style={{ height: 15, background: '#d1b69a' }} />
    {/* 3-badges + study text */}
    <div className="md2-wrap" style={{ paddingTop: 'clamp(40px,5vw,60px)', paddingBottom: 'clamp(16px,2.5vw,26px)', textAlign: 'center' }}>
      <EditableImage edit={edit} k="loBadgesImg" label="badges image" inline value="https://wp.aregomd.com/wp-content/uploads/2026/04/3-badges-v2.png">
        <img src={skin.mkt.edits?.images?.loBadgesImg || 'https://wp.aregomd.com/wp-content/uploads/2026/04/3-badges-v2.png'} alt="" style={{ width: 260, maxWidth: '72%', objectFit: 'contain' }} />
      </EditableImage>
      <p style={{ margin: '22px auto 0', fontSize: 14, color: MD2.sub, maxWidth: 920, lineHeight: 1.7 }}>{T('loBody')}</p>
    </div>
    {/* 4 trust badges — dark */}
    <div style={{ background: '#201b17' }}>
      <div className="md2-wrap md2-lo-trust" style={{ paddingTop: 'clamp(28px,3.5vw,42px)', paddingBottom: 'clamp(28px,3.5vw,42px)' }}>
        {(L.loTrust || []).map((b, i) => <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14, border: '1px solid rgba(255,255,255,.4)', borderRadius: 4, padding: '18px 20px' }}>
          <span style={{ flex: 'none', display: 'flex' }}>
            <EditableImage edit={edit} k={`loTrust${i}img`} label="badge icon" inline>
              {skin.mkt.edits?.images?.[`loTrust${i}img`] ? <img src={skin.mkt.edits.images[`loTrust${i}img`]} alt="" style={{ width: 34, height: 34, objectFit: 'contain' }} /> : loTrustIcon(b.label)}
            </EditableImage>
          </span>
          <span style={{ fontSize: 15, fontWeight: 700, color: '#fff', lineHeight: 1.3 }}><EditableText edit={md2ItemEdit(edit, skin, 'loTrust', i, 'label')} k={`lotr${i}`} value={b.label} /></span>
        </div>)}
      </div>
    </div>
    {/* 2-col quality — light */}
    <div style={{ background: '#f9f9f9' }}>
      <div className="md2-wrap md2-lo-quality" style={{ paddingTop: 'clamp(30px,4vw,46px)', paddingBottom: 'clamp(30px,4vw,46px)' }}>
        {(L.loBadges || []).map((b, i) => <div key={i} style={{ background: '#fff', borderRadius: 6, padding: '26px 28px' }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#0F2B32', marginBottom: 8 }}><EditableText edit={md2ItemEdit(edit, skin, 'loBadges', i, 'title')} k={`lobt${i}`} value={b.title} /></div>
          <p style={{ fontSize: 14, color: '#0F2B32', lineHeight: 1.5, margin: 0 }}><EditableText edit={md2ItemEdit(edit, skin, 'loBadges', i, 'desc')} k={`lobd${i}`} value={b.desc} /></p>
        </div>)}
      </div>
    </div>
    {/* Products — WooCommerce-style cards */}
    <div className="md2-wrap" style={{ paddingTop: 'clamp(34px,4.5vw,54px)', paddingBottom: 8 }}>
      <div className="md2-lo-prodgrid">{products.map((p) => {
        const dkey = `loDesc_${p.id}`;
        const desc = skin.md2.text(dkey) || loDefaultDesc(p.name) || (p.description ? String(p.description).replace(/<[^>]+>/g, '').trim().slice(0, 130) : '');
        const cmp = Number(p.compareAtPrice || p.compareTo || 0);
        return <a key={p.id} href={edit ? undefined : `/marketplace/${slug}/product/${encodeURIComponent(p.id)}${qs}`} style={{ background: '#fff', border: `1px solid ${MD2.border}`, borderRadius: 14, boxShadow: '0 10px 30px rgba(34,29,24,.06)', padding: 20, display: 'flex', flexDirection: 'column', color: 'inherit' }}>
          <div style={{ height: 190, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{p.imageUrl && <img src={p.imageUrl} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />}</div>
          <div style={{ fontSize: 16, fontWeight: 800, color: '#0F2B32', marginTop: 12 }}>{p.name}</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#000', marginTop: 8 }}>{money(p.price)}{cmp > p.price ? <span style={{ fontSize: 13, fontWeight: 400, color: '#AAAEBA', textDecoration: 'line-through', marginLeft: 8 }}>{money(cmp)}</span> : null}</div>
          {(desc || edit) && <p style={{ fontSize: 12.5, color: '#0F2B32', lineHeight: 1.5, margin: '10px 0 14px', paddingBottom: 14, borderBottom: '1px solid #D5D8E0', flex: 1 }}><EditableText edit={edit} k={dkey} value={desc} /></p>}
          <span style={{ textAlign: 'center', fontSize: 13, fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', background: '#0F2B32', color: '#fff', borderRadius: 8, padding: 13 }}>View Details</span>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#0F2B32', lineHeight: 1.5, marginTop: 12 }}><EditableText edit={edit} k="loProductsNote" value={skin.md2.text('loProductsNote')} /></div>
        </a>;
      })}</div>
    </div>
    {/* Research Study — white card with purple RESEARCH STUDY pill + lock */}
    <div style={{ background: '#201b17', marginTop: 'clamp(30px,4vw,48px)' }}>
      <div className="md2-wrap" style={{ paddingTop: 'clamp(40px,5vw,60px)', paddingBottom: 'clamp(40px,5vw,60px)' }}>
        <div style={{ background: '#fff', borderRadius: 16, boxShadow: '0 26px 60px rgba(0,0,0,.25)', padding: 'clamp(24px,3vw,42px)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 22 }}>
            <span style={{ background: '#ead9fb', color: '#7c3aed', fontSize: 12, fontWeight: 700, letterSpacing: '.12em', padding: '6px 18px', borderRadius: 99 }}>{T('wlResearchKicker')}</span>
            {lock}
          </div>
          <div className="wl-research-grid" style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: 40, alignItems: 'center' }}>
            <div style={{ textAlign: 'center' }}>
              <EditableImage edit={edit} k="wlResearchImg" label="research image" inline value={researchImg}>
                <img src={researchImg} alt="" style={{ maxWidth: 190, width: '100%', maxHeight: 260, objectFit: 'contain', filter: 'drop-shadow(0 18px 30px rgba(34,29,24,.16))' }} />
              </EditableImage>
            </div>
            <div>
              <h2 style={{ fontFamily: "'Playfair Display',Georgia,serif", fontSize: 'clamp(22px,2.6vw,30px)', fontWeight: 700, color: MD2.ink, margin: 0, lineHeight: 1.2 }}>{T('wlResearchTitle')}</h2>
              <p style={{ margin: '16px 0 0', fontSize: 15, color: MD2.sub, lineHeight: 1.7 }}>{T('wlResearchBody')}</p>
              <p style={{ margin: '16px 0 0', fontSize: 13.5, fontStyle: 'italic', color: MD2.muted, lineHeight: 1.65 }}>{T('wlResearchDisc')}</p>
              <a href={edit ? undefined : AREGORX_STORE_URL} style={{ display: 'inline-block', marginTop: 24, fontSize: 13.5, fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', background: MD2.btn, color: '#fff', borderRadius: 8, padding: '13px 30px' }}>{T('wlResearchCta')}</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </>;
};

// ── MD2 treatments listing ─────────────────────────────────────────────────
const Md2Listing = ({ skin, slug, edit }) => {
  const [cat, setCat] = useState('All');
  // Chips come from per-marketplace category overrides + inventory categories,
  // narrowed by the Settings → "Storefront filters" selection.
  const cats = useMemo(() => storefrontCategories(skin), [skin]);
  const shown = filterByCategory(skin, cat);

  return <>
    <div style={{ background: 'linear-gradient(180deg,#f4f7ff 0%,#fff 100%)', borderBottom: `1px solid ${MD2.border}` }}>
      <div className="md2-wrap" style={{ paddingTop: 44, paddingBottom: 36, textAlign: 'center' }}>
        <span style={{ fontSize: 12, letterSpacing: '.16em', color: MD2.accent, fontWeight: 700, textTransform: 'uppercase' }}>
          <Md2Text edit={edit} skin={skin} k="treatKicker">{(v) => <EditableText edit={edit} k="treatKicker" value={v} />}</Md2Text>
        </span>
        <h1 className="md2-h1" style={{ margin: '12px auto 0', fontSize: 'clamp(30px,4.5vw,40px)' }}>
          <Md2Text edit={edit} skin={skin} k="listingTitle">{(v) => <EditableText edit={edit} k="listingTitle" value={v} />}</Md2Text>
        </h1>
        <p style={{ margin: '12px auto 0', fontSize: 15.5, color: MD2.sub, maxWidth: 560, lineHeight: 1.6 }}>
          <Md2Text edit={edit} skin={skin} k="listingSub">{(v) => <EditableText edit={edit} k="listingSub" value={v} />}</Md2Text>
        </p>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginTop: 16, fontSize: 12.5, color: MD2.muted, flexWrap: 'wrap' }}>
          {skin.md2.lists.heroPills.map((x, i) => <span key={i}>{x}</span>)}
        </div>
      </div>
    </div>
    <div className="md2-wrap" style={{ paddingTop: 28, paddingBottom: 64 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 22, flexWrap: 'wrap' }}>
        {cats.length > 0 && ['All', ...cats].map((label) => {
          const on = cat === label;
          return <span key={label} onClick={() => setCat(label)}
            style={{ fontSize: 13, fontWeight: 600, padding: '8px 18px', borderRadius: 99, background: on ? MD2.ink : '#fff', color: on ? '#fff' : MD2.sub, border: `1px solid ${on ? MD2.ink : MD2.chip}`, cursor: 'pointer' }}>{label}</span>;
        })}
        <span style={{ marginLeft: 'auto', fontSize: 13, color: MD2.faint }}>{shown.length} treatment{shown.length === 1 ? '' : 's'}</span>
      </div>
      <div className="md2-cards">
        {shown.map((p) => <Md2Card key={p.id} skin={skin} slug={slug} edit={edit} p={p} />)}
      </div>
      <div style={{ textAlign: 'center', marginTop: 28, fontSize: 12.5, color: MD2.faint }}>
        <Md2Text edit={edit} skin={skin} k="portalNote">{(v) => <EditableText edit={edit} k="portalNote" value={v} />}</Md2Text>
      </div>
    </div>
  </>;
};

// ── MD2 product page ───────────────────────────────────────────────────────
const Md2Product = ({ skin, slug, edit }) => {
  const { productId } = useParams();
  const cart = useCart();
  const [selectedDose, setSelectedDose] = useState(0);
  const [openSec, setOpenSec] = useState(0);
  const qs = edit ? '?edit=1' : '';
  const p = skin.products.find((x) => String(x.id) === String(productId));
  const subsOn = skin.mkt.subscriptionsEnabled === true && !skin.isHipaa;
  const [plan, setPlan] = useState('once');
  useEffect(() => { setPlan(subsOn ? 'sub' : 'once'); }, [subsOn]);
  if (!p) return <div style={{ padding: 60, textAlign: 'center', color: MD2.muted }}>Product not found. <a href={storeUrl(slug, 'shop')} style={{ color: MD2.accent }}>Back to treatments</a></div>;

  const pi = md2ProductInfo(skin.mkt, p.id);
  const badge = skin.badges[p.id];
  const bc = md2BadgeColors(badge);
  const doses = (Array.isArray(p.doses) ? p.doses : []).map((d) => (typeof d === 'string' ? { label: d } : d));
  const activeDose = doses[selectedDose] ?? doses[0];
  const price = activeDose?.price ?? p.price;
  const subPrice = activeDose?.subscriptionPrice ?? p.subscriptionPrice ?? price;
  const doseNote = (i, n) => (n === 1 ? 'standard' : i === 0 ? 'starter' : i === n - 1 ? 'advanced' : 'standard');
  const desc = p.description ? String(p.description).replace(/<[^>]+>/g, '').slice(0, 400) : '';
  const tiles = Array.isArray(pi.tiles) ? pi.tiles : [];
  const sections = Array.isArray(pi.sections) ? pi.sections : [];
  const related = skin.products.filter((x) => String(x.id) !== String(p.id)).slice(0, 3);
  const ctaHref = skin.isHipaa
    ? `/marketplace/${slug}/portal/${encodeURIComponent(p.id)}`
    : `/marketplace/${slug}/checkout/${encodeURIComponent(p.id)}${plan === 'sub' ? '?subscribe=1' : ''}`;

  const setPi = (patch) => edit.setProductInfo(p.id, patch);
  const secEdit = (i, field) => edit && { setText: (_k, v) => setPi({ sections: sections.map((s, j) => (j === i ? { ...s, [field]: v } : s)) }) };
  const tileEdit = (i, field) => edit && { setText: (_k, v) => setPi({ tiles: tiles.map((s, j) => (j === i ? { ...s, [field]: v } : s)) }) };

  return <>
    <div className="md2-wrap" style={{ paddingTop: 18, fontSize: 13, color: MD2.faint }}>
      <a href={`/marketplace/${slug}/shop${qs}`} style={{ color: MD2.muted }}>Treatments</a>
      <span style={{ margin: '0 6px' }}>/</span>
      <span style={{ color: MD2.ink, fontWeight: 600 }}>{p.name}</span>
    </div>

    <div className="md2-wrap md2-product-grid" style={{ paddingTop: 24, paddingBottom: 64 }}>
      {/* Image + trust tiles */}
      <div>
        <div style={{ height: 'min(520px,60vw)', borderRadius: 20, background: md2Stripes, position: 'relative', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {p.imageUrl && <img src={p.imageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', background: '#fff' }} />}
          {badge && <span style={{ position: 'absolute', top: 14, left: 14, fontSize: 11, fontWeight: 800, letterSpacing: '.04em', background: bc.bg, color: bc.fg, borderRadius: 7, padding: '4px 11px' }}>{badge}</span>}
        </div>
        {tiles.length > 0 && <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(tiles.length, 3)},1fr)`, gap: 10, marginTop: 14 }}>
          {tiles.map((tl, i) => (
            <div key={i} style={{ background: '#f8fafc', border: `1px solid ${MD2.border}`, borderRadius: 12, padding: 12, textAlign: 'center', position: 'relative' }}>
              <div style={{ fontSize: 18 }}><EditableText edit={tileEdit(i, 'icon')} k={`ti${i}`} value={tl.icon} /></div>
              <div style={{ fontSize: 11.5, fontWeight: 700, color: MD2.ink, marginTop: 4 }}>
                <EditableText edit={tileEdit(i, 'label')} k={`tl${i}`} value={tl.label} />
              </div>
              {edit && <span style={{ position: 'absolute', top: 2, right: 2 }}><Md2Del edit={edit} onClick={() => setPi({ tiles: tiles.filter((_, j) => j !== i) })} /></span>}
            </div>
          ))}
        </div>}
        {edit && <div style={{ marginTop: 8 }}><Md2Add edit={edit} label="add trust tile" onClick={() => setPi({ tiles: [...tiles, { icon: '⭐', label: 'New tile' }] })} /></div>}
      </div>

      {/* Buy panel */}
      <div>
        {(pi.category || edit) && <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '.1em', color: MD2.accent, textTransform: 'uppercase' }}>
          <EditableText edit={edit && { setText: (_k, v) => setPi({ category: v }) }} k="picat" value={pi.category || (edit ? 'Category — click to edit' : '')} />
        </span>}
        <h1 style={{ margin: '8px 0 0', fontSize: 'clamp(26px,3.6vw,34px)', fontWeight: 800, letterSpacing: '-1px', color: MD2.ink }}>{p.name}</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8, flexWrap: 'wrap' }}>
          {(pi.rating || edit) && <>
            <span style={{ color: '#f0b429', fontSize: 14, letterSpacing: 1 }}>★★★★★</span>
            <span style={{ fontSize: 13, color: MD2.muted }}>
              <EditableText edit={edit && { setText: (_k, v) => setPi({ rating: v }) }} k="pirating" value={pi.rating || (edit ? 'rating line' : '')} />
              {edit && <Md2Del edit={edit} onClick={() => setPi({ rating: '' })} />}
            </span>
          </>}
          {(pi.stockLabel || edit) && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 700, color: '#1d7a46' }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#22a45d' }}></span>
            <EditableText edit={edit && { setText: (_k, v) => setPi({ stockLabel: v }) }} k="pistock" value={pi.stockLabel || (edit ? 'stock label' : '')} />
            {edit && <Md2Del edit={edit} onClick={() => setPi({ stockLabel: '' })} />}
          </span>}
        </div>
        {desc && <p style={{ margin: '14px 0 0', fontSize: 14.5, color: MD2.sub, lineHeight: 1.65 }}>{desc}</p>}

        {doses.length > 0 && <div style={{ marginTop: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '.06em', color: MD2.ink, textTransform: 'uppercase' }}>Dosage</span>
            <span style={{ fontSize: 11.5, color: MD2.faint }}>Final dosage confirmed by your provider</span>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 9, flexWrap: 'wrap' }}>
            {doses.map((d, i) => (
              <span key={i} onClick={() => setSelectedDose(i)}
                style={{ flex: 1, minWidth: 90, textAlign: 'center', fontSize: 13.5, fontWeight: 700, border: i === selectedDose ? `2px solid ${MD2.accent}` : `1px solid ${MD2.chip}`, background: i === selectedDose ? '#eef1fb' : '#fff', color: MD2.ink, borderRadius: 12, padding: 12, cursor: 'pointer' }}>
                {d.label}
                <span style={{ display: 'block', fontSize: 10.5, fontWeight: 500, color: MD2.muted, marginTop: 2 }}>
                  {d.price != null ? money(d.price) : doseNote(i, doses.length)}
                </span>
              </span>
            ))}
          </div>
        </div>}

        {subsOn && <div style={{ marginTop: 18 }}>
          <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '.06em', color: MD2.ink, textTransform: 'uppercase', marginBottom: 9 }}>Plan</div>
          <div onClick={() => setPlan('sub')} style={{ border: plan === 'sub' ? `2px solid ${MD2.accent}` : `1px solid ${MD2.chip}`, background: plan === 'sub' ? '#eef1fb' : '#fff', borderRadius: 14, padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', gap: 10 }}>
            <span><span style={{ display: 'block', fontSize: 14, fontWeight: 800, color: MD2.accent }}>Subscribe</span><span style={{ fontSize: 12, color: MD2.muted }}>Ships every {skin.mkt.subscriptionIntervalDays ?? 30} days · pause or cancel anytime</span></span>
            <SubscriptionPriceTag price={price} subscriptionPrice={subPrice} style={{ fontSize: 18, fontWeight: 800, color: MD2.ink, whiteSpace: 'nowrap' }} />
          </div>
          <div onClick={() => setPlan('once')} style={{ border: plan === 'once' ? `2px solid ${MD2.accent}` : `1px solid ${MD2.chip}`, background: plan === 'once' ? '#eef1fb' : '#fff', borderRadius: 14, padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', marginTop: 8, gap: 10 }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: MD2.sub }}>One-time purchase</span>
            <span style={{ fontSize: 18, fontWeight: 800, color: MD2.ink, whiteSpace: 'nowrap' }}>{money(price)}</span>
          </div>
        </div>}

        <a href={edit ? undefined : (skin.isHipaa ? ctaHref : undefined)}
          onClick={edit || skin.isHipaa ? undefined : () => cart.addAndGo(p.id, 1, plan === 'sub')}
          style={{ display: 'block', textAlign: 'center', marginTop: 18, fontSize: 15.5, fontWeight: 700, background: MD2.accent, color: '#fff', borderRadius: 99, padding: 16, cursor: 'pointer' }}>
          Get Started{price != null ? ` — ${money(plan === 'sub' ? subPrice : price)}` : ''} →
        </a>
        <div style={{ textAlign: 'center', fontSize: 12, color: MD2.faint, marginTop: 9 }}>
          <Md2Text edit={edit} skin={skin} k="portalNote">{(v) => <EditableText edit={edit} k="portalNote" value={v} />}</Md2Text>
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginTop: 12, fontSize: 12, color: MD2.muted, flexWrap: 'wrap' }}>
          {skin.md2.lists.heroPills.map((x, i) => <span key={i}>{x}</span>)}
        </div>

        {(sections.length > 0 || edit) && <div style={{ marginTop: 22, borderTop: `1px solid ${MD2.border}` }}>
          {sections.map((s, i) => (
            <div key={i} style={{ borderBottom: `1px solid ${MD2.border}` }}>
              <div onClick={() => setOpenSec(openSec === i ? -1 : i)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 2px', cursor: 'pointer', gap: 8 }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: MD2.ink }}>
                  <EditableText edit={secEdit(i, 'title')} k={`sect${i}`} value={s.title} />
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Md2Del edit={edit} onClick={() => setPi({ sections: sections.filter((_, j) => j !== i) })} />
                  <span style={{ fontSize: 16, color: MD2.faint }}>{openSec === i ? '−' : '+'}</span>
                </span>
              </div>
              {openSec === i && <div style={{ padding: '0 2px 16px', fontSize: 13.5, color: MD2.sub, lineHeight: 1.65 }}>
                <EditableText edit={secEdit(i, 'body')} k={`secb${i}`} value={s.body} />
              </div>}
            </div>
          ))}
          {edit && <div style={{ marginTop: 10 }}><Md2Add edit={edit} label="add section" onClick={() => setPi({ sections: [...sections, { title: 'New section', body: 'Click to edit this content.' }] })} /></div>}
        </div>}
      </div>
    </div>

    {related.length > 0 && <div className="md2-wrap" style={{ paddingBottom: 64 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 18, gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 20, fontWeight: 800, color: MD2.ink, letterSpacing: '-.4px' }}>Pairs well with</span>
        <a href={`/marketplace/${slug}/shop${qs}`} style={{ fontSize: 13.5, fontWeight: 600, color: MD2.accent }}>All treatments →</a>
      </div>
      <div className="md2-cards">
        {related.map((r) => {
          const rdesc = r.description ? String(r.description).replace(/<[^>]+>/g, '').slice(0, 40) : md2ProductInfo(skin.mkt, r.id).category;
          return <a key={r.id} href={`/marketplace/${slug}/product/${encodeURIComponent(r.id)}${qs}`}
            style={{ border: `1px solid ${MD2.border}`, borderRadius: 16, padding: 14, display: 'flex', gap: 14, alignItems: 'center', color: 'inherit' }}>
            <span style={{ width: 64, height: 64, borderRadius: 12, background: md2Stripes, flex: 'none', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {r.imageUrl && <img src={r.imageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', background: '#fff' }} />}
            </span>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: 'block', fontSize: 14.5, fontWeight: 700, color: MD2.ink }}>{r.name}</span>
              {rdesc && <span style={{ fontSize: 12, color: MD2.muted }}>{rdesc}</span>}
            </span>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: MD2.accent, flex: 'none' }}>from {money(r.price)}</span>
          </a>;
        })}
      </div>
    </div>}
  </>;
};

// ── MD2 Information article page (What's GLP-1 / Instructions / Safety / Privacy / California) ──
function md2RenderBlocks(blocks) {
  const out = []; let list = null;
  const flush = (key) => { if (list) { out.push(<ul key={'ul' + key} style={{ margin: '0 0 20px', paddingLeft: 22, color: MD2.sub, lineHeight: 1.7, fontSize: 15 }}>{list}</ul>); list = null; } };
  (blocks || []).forEach((b, i) => {
    const k = b[0], t = b[1];
    if (k === 'l') { if (!list) list = []; list.push(<li key={i} style={{ marginBottom: 7 }}>{t}</li>); return; }
    flush(i);
    if (k === 'h') out.push(<h2 key={i} style={{ fontFamily: "'Playfair Display',Georgia,serif", fontSize: 'clamp(19px,2.1vw,25px)', fontWeight: 700, color: MD2.ink, margin: '30px 0 12px', lineHeight: 1.25 }}>{t}</h2>);
    else out.push(<p key={i} style={{ fontSize: 15, color: MD2.sub, lineHeight: 1.75, margin: '0 0 14px' }}>{t}</p>);
  });
  flush('end');
  return out;
}
const Md2Info = ({ skin, slug, pageKey }) => {
  const [pages, setPages] = useState(null);
  useEffect(() => { let ok = true; import('./md2InfoPages.js').then((m) => { if (ok) setPages(m.INFO_PAGES); }); return () => { ok = false; }; }, []);
  const page = pages && pages[pageKey];
  return <div style={{ background: '#fff' }}>
    <div className="md2-wrap" style={{ paddingTop: 'clamp(36px,4.5vw,56px)', paddingBottom: 'clamp(48px,6vw,72px)', maxWidth: 880 }}>
      <a href={`/marketplace/${slug}`} style={{ fontSize: 13, fontWeight: 600, color: MD2.accentDark, textDecoration: 'none' }}>← Back to home</a>
      <h1 style={{ fontFamily: "'Playfair Display',Georgia,serif", fontSize: 'clamp(30px,4vw,46px)', fontWeight: 700, color: MD2.ink, margin: '18px 0 0', lineHeight: 1.15 }}>{page ? page.title : (pages ? 'Not found' : 'Information')}</h1>
      <div style={{ height: 3, width: 64, background: MD2.accent, borderRadius: 3, margin: '18px 0 8px' }} />
      {!pages && <p style={{ marginTop: 20, color: MD2.muted }}>Loading…</p>}
      {pages && !page && <p style={{ marginTop: 20, color: MD2.muted }}>This page could not be found.</p>}
      {page && <div style={{ marginTop: 20 }}>{md2RenderBlocks(page.blocks)}</div>}
    </div>
  </div>;
};

// ── MD2 Contact Us — form that emails the store's support inbox via the API ──
const Md2Contact = ({ skin, slug }) => {
  const [f, setF] = useState({ name: '', email: '', subject: '', message: '', website: '' });
  const [status, setStatus] = useState('');
  const [err, setErr] = useState('');
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const submit = (e) => {
    e.preventDefault();
    if (!f.name.trim() || !f.email.trim() || !f.message.trim()) { setErr('Please fill in your name, email, and message.'); setStatus('error'); return; }
    setStatus('sending'); setErr('');
    SendRequest('POST', `/api/v1/public/Marketplaces/${encodeURIComponent(slug)}/contact`, f,
      () => { setStatus('sent'); setF({ name: '', email: '', subject: '', message: '', website: '' }); },
      (x) => { setErr((x && (x.error || x.message)) || 'Could not send your message. Please try again.'); setStatus('error'); });
  };
  const inp = { width: '100%', fontSize: 15, padding: '13px 15px', borderRadius: 10, border: `1px solid ${MD2.border}`, background: '#fff', color: MD2.ink, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' };
  return <div style={{ background: '#fff' }}>
    <div className="md2-wrap" style={{ paddingTop: 'clamp(36px,4.5vw,56px)', paddingBottom: 'clamp(48px,6vw,72px)', maxWidth: 680 }}>
      <a href={`/marketplace/${slug}`} style={{ fontSize: 13, fontWeight: 600, color: MD2.accentDark, textDecoration: 'none' }}>← Back to home</a>
      <h1 style={{ fontFamily: "'Playfair Display',Georgia,serif", fontSize: 'clamp(30px,4vw,46px)', fontWeight: 700, color: MD2.ink, margin: '18px 0 0' }}>Contact Us</h1>
      <div style={{ height: 3, width: 64, background: MD2.accent, borderRadius: 3, margin: '18px 0 8px' }} />
      <p style={{ fontSize: 15, color: MD2.sub, lineHeight: 1.7, marginTop: 16 }}>Have a question? Send us a message and our team will get back to you. You can also email us directly at <a href="mailto:support@aregomd.com" style={{ color: MD2.accentDark }}>support@aregomd.com</a>.</p>
      {status === 'sent'
        ? <div style={{ marginTop: 24, background: '#eef7f0', border: '1px solid #bfe3c9', borderRadius: 12, padding: '20px 22px', color: '#1d6b39', fontSize: 15 }}><b>Thank you!</b> Your message has been sent — we'll be in touch soon.</div>
        : <form onSubmit={submit} style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <input type="text" placeholder="Your name" value={f.name} onChange={set('name')} style={inp} />
          <input type="email" placeholder="Your email" value={f.email} onChange={set('email')} style={inp} />
          <input type="text" placeholder="Subject (optional)" value={f.subject} onChange={set('subject')} style={inp} />
          <textarea placeholder="How can we help?" value={f.message} onChange={set('message')} rows={6} style={{ ...inp, resize: 'vertical' }} />
          <input type="text" tabIndex={-1} autoComplete="off" value={f.website} onChange={set('website')} aria-hidden="true" style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }} />
          {status === 'error' && <div style={{ color: '#b42318', fontSize: 13.5 }}>{err}</div>}
          <button type="submit" disabled={status === 'sending'} style={{ marginTop: 6, alignSelf: 'flex-start', fontSize: 15, fontWeight: 700, background: MD2.btn, color: '#fff', border: 'none', borderRadius: 99, padding: '14px 38px', cursor: 'pointer', opacity: status === 'sending' ? 0.6 : 1 }}>{status === 'sending' ? 'Sending…' : 'Send message'}</button>
        </form>}
    </div>
  </div>;
};

// Where to send a shopper whose cart just went empty: the page they were on
// when they added the first item, else the store home.
const CheckoutEmpty = ({ slug, t }) => {
  const navigate = useNavigate();
  const back = useMemo(() => checkoutBackUrl(slug, subForPage, storeUrl), [slug]);
  useEffect(() => {
    const timer = setTimeout(() => navigate(back, { replace: true }), 900);
    return () => clearTimeout(timer);
  }, [back, navigate]);
  return <div style={{ padding: 40, textAlign: 'center', color: t.muted }}>
    Your cart is empty. <a href={back} style={{ color: t.accent, fontWeight: 600 }}>Continue shopping</a>
  </div>;
};

// ── Members-only gate ───────────────────────────────────────────────────────
// Shown instead of a raw "Marketplace not found" when a storefront can't load.
// A private (non-public) marketplace 404s for guests — offer them a way in.
const MembersOnlyGate = () => {
  const location = useLocation();
  const loggedIn = !!GetToken();
  const scope = GetScope();
  // A private store in an iPhone Home Screen app signs in here too.
  const signInEl = new URLSearchParams(location.search).has(APP_SIGNIN_PARAM)
    && <React.Suspense fallback={null}><AppSignIn skin={null} /></React.Suspense>;
  const primary = () => {
    if (scope) { window.location.href = maskedActive() ? dashboardUrl(scope) : `/customers/${encodeURIComponent(scope)}/dashboard`; return; }
    if (loggedIn) { window.location.href = maskedActive() ? officeUrl('/') : '/'; return; }
    window.location.href = loginUrl();
  };
  return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f6f5f2', padding: 24, fontFamily: "'Instrument Sans',system-ui,sans-serif" }}>
    <div style={{ maxWidth: 448, width: '100%', background: '#fff', border: '1px solid #ece7dd', borderRadius: 20, boxShadow: '0 24px 60px rgba(34,29,24,.09)', padding: 'clamp(30px,5vw,44px) clamp(26px,4vw,40px)', textAlign: 'center' }}>
      <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#f3ede1', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none"><rect x="4.5" y="10.5" width="15" height="10" rx="2" stroke="#a8865a" strokeWidth="1.9" /><path d="M8 10.5V8a4 4 0 0 1 8 0v2.5" stroke="#a8865a" strokeWidth="1.9" /></svg>
      </div>
      <h1 style={{ fontSize: 'clamp(23px,3vw,28px)', fontWeight: 800, color: '#221d18', margin: 0, letterSpacing: '-.4px' }}>Members Only</h1>
      <p style={{ fontSize: 15, color: '#5c554c', lineHeight: 1.65, margin: '14px 0 28px' }}>
        {loggedIn
          ? "Your account doesn't have access to this marketplace. If you believe this is a mistake, please contact support."
          : 'This marketplace can only be accessed by members. Please log in to continue.'}
      </p>
      <button onClick={primary} style={{ width: '100%', fontSize: 15, fontWeight: 700, background: '#2b2723', color: '#fff', border: 'none', borderRadius: 99, padding: '14px', cursor: 'pointer' }}>
        {loggedIn ? (scope ? 'Go to my dashboard' : 'Return to home') : 'Log in'}
      </button>
    </div>
    {signInEl}
  </div>;
};

// ── Page not found ──────────────────────────────────────────────────────────
// Rendered inside the store's own shell (header/footer/cart) for a path that
// is neither a page nor a valid referral handle. Colours come from the
// template tokens so it sits naturally on light and dark stores alike.
// Custom pages (created through Settings → MCP or by an admin): authored HTML
// rendered with the store's tokens and cart hooks inside the store shell.
// Only published pages are public; signed-in staff also see drafts, flagged.
const findCustomPage = (mkt, pageSlug) => (Array.isArray(mkt?.customPages) ? mkt.customPages : [])
  .find((p) => String(p?.slug ?? '').toLowerCase() === String(pageSlug ?? '').toLowerCase()
    && (p.status === undefined || p.status === 'published' || (GetToken()?.token && GetScope() == undefined)));

// eslint-disable-next-line react/prop-types
const CustomPageBody = ({ skin, slug, pageSlug }) => {
  const cart = useCart();
  const cp = findCustomPage(skin.mkt, pageSlug);
  if (!cp) return <StorefrontNotFound skin={skin} slug={slug} />;
  const css = String(cp.css ?? '').replace(/<\/style/gi, '');
  return <div className="ag-custom-page" style={{ maxWidth: 1120, margin: '0 auto', padding: 'clamp(20px, 4vw, 48px) 16px' }}>
    {cp.status && cp.status !== 'published' && <div style={{ background: '#fff4d6', color: '#7a5a00', borderRadius: 8, padding: '8px 12px', fontSize: 13, marginBottom: 16 }}>
      Draft — only visible to signed-in staff. Publish it to make it public.</div>}
    <style>{`.ag-custom-page img,.ag-custom-page video,.ag-custom-page iframe{max-width:100%;height:auto}.ag-custom-page{overflow-wrap:anywhere}${css}`}</style>
    <CustomHtml html={String(cp.html ?? '')} skin={skin} cart={cart} />
  </div>;
};

const StorefrontNotFound = ({ skin, slug }) => {
  const t = skin?.t ?? {};
  const ink = t.ink ?? '#191817';
  const muted = t.muted ?? '#6f6b63';
  const card = t.card ?? (t.dark ? '#12151c' : '#ffffff');
  // Templates whose accent equals their ink (9, 11) would render the button
  // text invisible against itself — fall back to ink-on-bg there.
  const accent = t.accent && t.accent !== t.ink ? t.accent : ink;
  const onAccent = t.dark && accent === ink ? (t.bg ?? '#000') : '#fff';
  return <div style={{ minHeight: '62vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'clamp(40px,8vw,96px) 20px' }}>
    <div style={{ maxWidth: 520, width: '100%', textAlign: 'center', background: card, border: `1px solid ${t.dark ? 'rgba(255,255,255,.08)' : 'rgba(0,0,0,.07)'}`, borderRadius: 24, padding: 'clamp(32px,6vw,56px) clamp(24px,5vw,48px)', boxShadow: t.dark ? 'none' : '0 24px 60px rgba(34,29,24,.07)' }}>
      <div style={{ fontSize: 'clamp(64px,14vw,104px)', fontWeight: 800, lineHeight: 1, letterSpacing: '-3px', color: accent, opacity: 0.9 }}>404</div>
      <h1 style={{ fontSize: 'clamp(22px,3.2vw,30px)', fontWeight: 700, color: ink, margin: '18px 0 0', letterSpacing: '-.3px' }}>This page doesn&apos;t exist</h1>
      <p style={{ fontSize: 15.5, color: muted, lineHeight: 1.65, margin: '12px 0 30px' }}>
        The link may be broken, or the page may have been moved.
      </p>
      <a href={storeUrl(slug, '')} style={{ display: 'inline-block', minWidth: 220, fontSize: 15, fontWeight: 700, background: accent, color: onAccent, borderRadius: 99, padding: '14px 28px', textDecoration: 'none' }}>
        Return to homepage
      </a>
      <div style={{ marginTop: 18 }}>
        <a href={storeUrl(slug, 'shop')} style={{ fontSize: 14, color: muted, textDecoration: 'underline', textUnderlineOffset: 3 }}>Browse products</a>
      </div>
    </div>
  </div>;
};


// ── Engine ─────────────────────────────────────────────────────────────────
const Storefront = ({ page, previewData }) => {
  const routeParams = useParams();
  const { slug: paramSlug } = routeParams;
  // On a masked custom domain the route has no :slug — resolve it from the host.
  const slug = paramSlug || getMaskedSlug();
  const location = useLocation();
  const { data, error, setData } = useStorefront(slug, previewData);
  const skin = useSkin(data);
  const [editSaving, setEditSaving] = useState(false);
  const [editFlash, setEditFlash] = useState('');
  // Proving Room conversion: count who looks at each product page (any
  // store, any template). Not in previews or the visual editor.
  useEffect(() => {
    if (page === 'product' && routeParams.productId && !previewData && !/[?&]edit=1/.test(window.location.search)) {
      spPing(slug, 'pview', routeParams.productId);
    }
  }, [page, routeParams.productId, slug, previewData]);

  // Visual editing: authenticated corporate/back-office users with ?edit=1.
  // Every commit auto-saves. GetScope() is undefined for a guest AND a scoped
  // member, so it alone would let a logged-out visitor edit — require a real
  // auth token (GetToken() is undefined unless signed in) so only a corporate
  // login (token present, no member scope) can enter edit mode.
  const wantsEdit = new URLSearchParams(location.search).get('edit') === '1';
  const isCorporate = !!GetToken() && GetScope() == undefined;
  const editOn = wantsEdit && isCorporate && !!data?.marketplace;

  // savePatch writes any top-level marketplace field. The API PUT is a shallow
  // key-by-key merge, so a key the server has never seen (customHtml) persists
  // with no backend change — the same way customCss does.
  const savePatch = (patch) => {
    const mkt = data.marketplace;
    setEditSaving(true);
    SendRequest('PUT', `/api/v1/Marketplaces/${mkt.id}`, { ...mkt, ...patch }, (res) => {
      setData((d) => ({ ...d, marketplace: res }));
      setEditSaving(false);
      setEditFlash('Saved ✓');
      setTimeout(() => setEditFlash(''), 1500);
    }, (body) => {
      setEditSaving(false);
      let msg = '';
      try { msg = JSON.parse(body)?.error ?? ''; } catch { msg = ''; }
      alert(msg || 'The change could not be saved.');
    });
  };
  const saveEdits = (nextEdits) => savePatch({ edits: nextEdits });

  let edit = null;
  if (editOn && skin) {
    const edits = skin.mkt.edits ?? {};
    edit = {
      saving: editSaving,
      flash: editFlash,
      setText: (k, v) => saveEdits({ ...edits, texts: { ...(edits.texts ?? {}), [k]: v } }),
      setImage: (k, url) => saveEdits({ ...edits, images: { ...(edits.images ?? {}), [k]: url } }),
      // Media slots that accept video store a "<key>Kind" flag alongside the
      // URL — uploaded blob URLs are extension-less, so kind can't be sniffed.
      setImageMeta: (k, url, kind) => saveEdits({ ...edits, images: { ...(edits.images ?? {}), [k]: url, [k + 'Kind']: kind } }),
      setBanner: (banner) => saveEdits({ ...edits, topBanner: banner }),
      setBadge: (pid, text) => saveEdits({ ...edits, badges: { ...(edits.badges ?? {}), [pid]: text } }),
      setMd2: (k, v) => saveEdits({ ...edits, md2: { ...(edits.md2 ?? {}), [k]: v } }),
      setProductInfo: (pid, patch) => saveEdits({
        ...edits,
        productInfo: { ...(edits.productInfo ?? {}), [pid]: { ...((edits.productInfo ?? {})[pid] ?? {}), ...patch } },
      }),
      // Page HTML blocks ([{scope, html}], the customCss twin). Deliberately
      // NOT auto-saved like text edits — half-typed markup must not round-trip
      // to the server, so the code panel commits on an explicit Save.
      html: Array.isArray(skin.mkt.customHtml) ? skin.mkt.customHtml : [],
      setHtml: (blocks) => savePatch({ customHtml: blocks }),
      // Per-page SEO rows (mkt.seoPages, keyed by store-relative path, "/"
      // for home). The API PUT merges one level deep, so the WHOLE map is
      // rewritten on every save; a null row deletes the page's override.
      setPageSeo: (key, row) => {
        const all = { ...(skin.mkt.seoPages ?? {}) };
        if (row) all[key] = row; else delete all[key];
        savePatch({ seoPages: all });
      },
    };
  }

  if (error) return <MembersOnlyGate />;
  if (!skin) return <DataLoading title="Loading storefront" tokens={readStoreSkin(slug)} />;

  // Per-route head tags + the analytics runtime — storefront contexts only
  // (never the admin), and never inside the back-office preview iframe.
  const headEl = !previewData && <>
    <SeoHead skin={skin} slug={slug} page={page} />
    <StorefrontTracking skin={skin} slug={slug} page={page} />
    {/* Live chat: off for every store until Manage → Integrations turns it
        on, and then only on the pages picked there. */}
    <StorefrontChatwoot skin={skin} page={page} edit={!!edit} />
  </>;
  // ?edit=1 bottom dock — rendered by the engine so EVERY page of every
  // template (and any page added later) gets the Page SEO editor without
  // the template having to mount anything.
  // Lead-collection popup. Same guards as the head/dock: never in the
  // back-office preview iframe, never while an admin is editing the page.
  const wheelEl = !previewData && !edit && <StorefrontLeadWheel skin={skin} slug={slug} page={page} />;
  const socialEl = !previewData && !edit && <StorefrontSocialProof skin={skin} slug={slug} page={page} />;
  const scratchEl = !previewData && !edit && <StorefrontScratchCard skin={skin} slug={slug} page={page} />;
  // Web push opt-in, the store's web app manifest and the iPhone Add to Home
  // Screen helper. Off unless Settings → Push Notifications turns the store on.
  const pushEl = !previewData && !edit && <StorefrontWebPush skin={skin} slug={slug} page={page} />;
  const signInEl = !previewData && !edit && new URLSearchParams(location.search).has(APP_SIGNIN_PARAM)
    && <React.Suspense fallback={null}><AppSignIn skin={skin} /></React.Suspense>;
  const dockEl = edit && !previewData && <EditDock edit={edit} label={pageByKey(page)?.label ?? page}>
    <PageSeoEditor edit={edit} mkt={skin.mkt} seo={resolvePageSeo({ skin, slug, page, pathname: location.pathname, productId: routeParams.productId, postSlug: routeParams.postSlug })} />
  </EditDock>;

  // Dedicated skins (2 AregoMD, 8 AdaptX1, 9 Editorial, 10 Aritey) have their
  // own shells + pages; portal/checkout/thankyou reuse the shared components.
  if (skin.md2) {
    const kit = kitFor(skin.md2.tid);
    let body;
    switch (page) {
      case 'shop': body = <kit.Listing skin={skin} slug={slug} edit={edit} />; break;
      case 'product': body = kit.ProductLander && homeLanderOn(skin.mkt)
        ? <kit.ProductLander skin={skin} slug={slug} edit={edit} />
        : <kit.Product skin={skin} slug={slug} edit={edit} />; break;
      case 'second-page': body = kit.SecondPage ? <kit.SecondPage skin={skin} slug={slug} edit={edit} /> : <kit.Home skin={skin} slug={slug} edit={edit} />; break;
      case 'policy': body = kit.Policy ? <kit.Policy skin={skin} slug={slug} edit={edit} /> : <kit.Home skin={skin} slug={slug} edit={edit} />; break;
      // Refund: the template's own page where it has one (Adapt X.1), else
      // the shared policy — falling back to Home left every other store
      // (Auera first) without a refund page at all.
      case 'refund': body = kit.Refund ? <kit.Refund skin={skin} slug={slug} edit={edit} /> : <LegalFallback skin={skin} page="refund" />; break;
      case 'terms': case 'cookie': case 'shipping': case 'income': case 'privacy': case 'builder-policy':
        // Falling back to Home here meant the cookie-consent banner's "cookie
        // policy" link opened the shop on every template without its own
        // Legal component. LegalFallback reads the same md2 keys, so a store
        // that has written its policy keeps it.
        body = kit.Legal ? <kit.Legal skin={skin} slug={slug} edit={edit} page={page} /> : <LegalFallback skin={skin} page={page} />; break;
      case 'blog': case 'blog-post':
        // Every template: the AI blog renders inside the template's own Shell.
        body = <StorefrontBlogBody skin={skin} slug={slug} page={page} />; break;
      case 'lab-tested': body = kit.LabTested ? <kit.LabTested skin={skin} slug={slug} edit={edit} /> : <kit.Home skin={skin} slug={slug} edit={edit} />; break;
      case 'peptides': body = kit.Peptides ? <kit.Peptides skin={skin} slug={slug} edit={edit} /> : <kit.Home skin={skin} slug={slug} edit={edit} />; break;
      case 'testing': body = kit.Testing ? <kit.Testing skin={skin} slug={slug} edit={edit} /> : <kit.Home skin={skin} slug={slug} edit={edit} />; break;
      case 'coa-library': body = kit.COALibrary ? <kit.COALibrary skin={skin} slug={slug} edit={edit} /> : <kit.Home skin={skin} slug={slug} edit={edit} />; break;
      case 'sleep-spray-hub': body = kit.SleepSprayHub ? <kit.SleepSprayHub skin={skin} slug={slug} edit={edit} /> : <kit.Home skin={skin} slug={slug} edit={edit} />; break;
      case 'sleep-spray-melatonin-free': body = kit.SleepSpray ? <kit.SleepSpray skin={skin} slug={slug} edit={edit} /> : <kit.Home skin={skin} slug={slug} edit={edit} />; break;
      case 'sleep-spray-natural': body = kit.NaturalSleep ? <kit.NaturalSleep skin={skin} slug={slug} edit={edit} /> : <kit.Home skin={skin} slug={slug} edit={edit} />; break;
      case 'sleep-spray-aid': body = kit.SleepAid ? <kit.SleepAid skin={skin} slug={slug} edit={edit} /> : <kit.Home skin={skin} slug={slug} edit={edit} />; break;
      case 'sleep-spray-deprivation': body = kit.SleepDeprivation ? <kit.SleepDeprivation skin={skin} slug={slug} edit={edit} /> : <kit.Home skin={skin} slug={slug} edit={edit} />; break;
      case 'sleep-spray-grogginess': body = kit.Grogginess ? <kit.Grogginess skin={skin} slug={slug} edit={edit} /> : <kit.Home skin={skin} slug={slug} edit={edit} />; break;
      case 'sleep-spray-adaptogens': body = kit.Adaptogens ? <kit.Adaptogens skin={skin} slug={slug} edit={edit} /> : <kit.Home skin={skin} slug={slug} edit={edit} />; break;
      case 'sleep-spray-stress': body = kit.StressRelief ? <kit.StressRelief skin={skin} slug={slug} edit={edit} /> : <kit.Home skin={skin} slug={slug} edit={edit} />; break;
      case 'sleep-spray-oral': body = kit.OralSpray ? <kit.OralSpray skin={skin} slug={slug} edit={edit} /> : <kit.Home skin={skin} slug={slug} edit={edit} />; break;
      case 'sleep-spray-vegan': body = kit.VeganSpray ? <kit.VeganSpray skin={skin} slug={slug} edit={edit} /> : <kit.Home skin={skin} slug={slug} edit={edit} />; break;
      case 'sleep-spray-pillow': body = kit.PillowSpray ? <kit.PillowSpray skin={skin} slug={slug} edit={edit} /> : <kit.Home skin={skin} slug={slug} edit={edit} />; break;
      case 'sleep-spray-best': body = kit.BestSpray ? <kit.BestSpray skin={skin} slug={slug} edit={edit} /> : <kit.Home skin={skin} slug={slug} edit={edit} />; break;
      case 'sleep-spray-melatonin': body = kit.MelatoninSpray ? <kit.MelatoninSpray skin={skin} slug={slug} edit={edit} /> : <kit.Home skin={skin} slug={slug} edit={edit} />; break;
      case 'weight-loss':
        body = String(skin.md2.tid) === '2' ? <Md2WeightLoss skin={skin} slug={slug} edit={edit} /> : <kit.Home skin={skin} slug={slug} edit={edit} />; break;
      case 'sexual-health':
        body = String(skin.md2.tid) === '2' ? <Md2SexualHealth skin={skin} slug={slug} edit={edit} /> : <kit.Home skin={skin} slug={slug} edit={edit} />; break;
      case 'longevity':
        body = String(skin.md2.tid) === '2' ? <Md2Longevity skin={skin} slug={slug} edit={edit} /> : <kit.Home skin={skin} slug={slug} edit={edit} />; break;
      case 'whats-glp-1': case 'glp-1-instructions': case 'glp-1-safety-instructions': case 'privacy-practices': case 'for-california-residents':
      case 'payment-shipping': case 'terms-conditions': case 'faq': case 'payments':
        body = String(skin.md2.tid) === '2' ? <Md2Info skin={skin} slug={slug} pageKey={page} /> : <kit.Home skin={skin} slug={slug} edit={edit} />; break;
      case 'contact':
        body = String(skin.md2.tid) === '2' ? <Md2Contact skin={skin} slug={slug} /> : <kit.Home skin={skin} slug={slug} edit={edit} />; break;
      case 'compliance-packet': case 'compliance-action-checklist': case 'benefits': case 'ingredients': case 'privacy-policy':
        body = String(skin.md2.tid) === '17' ? <T17Info slug={slug} pageKey={page} /> : <kit.Home skin={skin} slug={slug} edit={edit} />; break;
      case 'hrt': body = String(skin.md2.tid) === '2' ? <Md2HrtHome skin={skin} slug={slug} edit={edit} /> : <kit.Home skin={skin} slug={slug} edit={edit} />; break;
      case 'hrt-men': body = String(skin.md2.tid) === '2' ? <Md2HrtGender skin={skin} slug={slug} edit={edit} gender="men" /> : <kit.Home skin={skin} slug={slug} edit={edit} />; break;
      case 'hrt-women': body = String(skin.md2.tid) === '2' ? <Md2HrtGender skin={skin} slug={slug} edit={edit} gender="women" /> : <kit.Home skin={skin} slug={slug} edit={edit} />; break;
      case 'hrt-portal': body = String(skin.md2.tid) === '2' ? (!GetToken()?.token ? <AuthGateModal /> : <Md2HrtPortal skin={skin} slug={slug} />) : <kit.Home skin={skin} slug={slug} edit={edit} />; break;
      case 'portal': body = (String(skin.md2.tid) === '2' && !GetToken()?.token) ? <AuthGateModal /> : <Portal skin={skin} slug={slug} />; break;
      case 'register': body = <RegisterPage skin={skin} slug={slug} />; break;
      // A corporate pay link (?pr=TOKEN) must reach the payment form even on
      // a HIPAA store — that flow is Wizlo-mirrored server-side and is the
      // only checkout a HIPAA store has.
      case 'checkout': body = (skin.isHipaa && !new URLSearchParams(window.location.search).get('pr')) ? <kit.Listing skin={skin} slug={slug} edit={edit} /> : <Checkout skin={skin} slug={slug} />; break;
      case 'thankyou': body = <ThankYou skin={skin} slug={slug} />; break;
      case 'sponsor': body = <SponsorPage skin={skin} slug={slug} />; break;
      case 'custom': body = <CustomPageBody skin={skin} slug={slug} pageSlug={routeParams.webalias} />; break;
      case 'notfound': body = <StorefrontNotFound skin={skin} slug={slug} />; break;
      // Template 14's homepage in the Natural Sleep lander's design
      // (homeContent.js) when the store has it on — the default there.
      default: body = kit.HomeLander && homeLanderOn(skin.mkt)
        ? <kit.HomeLander skin={skin} slug={slug} edit={edit} />
        : <kit.Home skin={skin} slug={slug} edit={edit} />;
    }
    return <CartProvider slug={slug} skin={skin} persist={!previewData} page={page}>
      {headEl}
      {!previewData && <SponsorBanner slug={slug} />}
      <React.Suspense fallback={<DataLoading title="Loading storefront" tokens={skin.t} />}>
        <kit.Shell skin={skin} slug={slug} edit={edit} page={page}>{body}</kit.Shell>
      </React.Suspense>
      <CartDrawer skin={skin} slug={previewData ? 'preview' : slug} page={page} />
      {wheelEl}
      {scratchEl}
      {socialEl}
      {pushEl}
      {signInEl}
      {dockEl}
    </CartProvider>;
  }

  let body;
  switch (page) {
    case 'shop': body = <Listing skin={skin} slug={slug} edit={edit} />; break;
    case 'product': body = <Product skin={skin} slug={slug} />; break;
    case 'blog': case 'blog-post': body = <StorefrontBlogBody skin={skin} slug={slug} page={page} />; break;
    case 'portal': body = <Portal skin={skin} slug={slug} />; break;
    case 'checkout': body = (skin.isHipaa && !new URLSearchParams(window.location.search).get('pr')) ? <Listing skin={skin} slug={slug} edit={edit} /> : <Checkout skin={skin} slug={slug} />; break;
    case 'thankyou': body = <ThankYou skin={skin} slug={slug} />; break;
    case 'sponsor': body = <SponsorPage skin={skin} slug={slug} />; break;
    case 'custom': body = <CustomPageBody skin={skin} slug={slug} pageSlug={routeParams.webalias} />; break;
    case 'notfound': body = <StorefrontNotFound skin={skin} slug={slug} />; break;
    default: body = <Landing skin={skin} slug={slug} edit={edit} />;
  }
  return <CartProvider slug={slug} skin={skin} persist={!previewData} page={page}>
    {headEl}
    {!previewData && <SponsorBanner slug={slug} />}
    <React.Suspense fallback={<DataLoading title="Loading storefront" tokens={skin.t} />}>
      <Shell skin={skin} slug={slug} edit={edit} page={page}>{body}</Shell>
    </React.Suspense>
    <CartDrawer skin={skin} slug={previewData ? 'preview' : slug} page={page} />
    {wheelEl}
    {scratchEl}
    {socialEl}
    {pushEl}
    {signInEl}
    {dockEl}
  </CartProvider>;
};

export const StorefrontLanding = () => <Storefront page="landing" />;
export const StorefrontShop = () => <Storefront page="shop" />;
export const StorefrontProduct = () => <Storefront page="product" />;
export const StorefrontBlog = () => <Storefront page="blog" />;
export const StorefrontBlogPost = () => <Storefront page="blog-post" />;
export const StorefrontSecondPage = () => <Storefront page="second-page" />;
export const StorefrontPolicy = () => <Storefront page="policy" />;
export const StorefrontRefund = () => <Storefront page="refund" />;
export const StorefrontTerms = () => <Storefront page="terms" />;
export const StorefrontCookie = () => <Storefront page="cookie" />;
export const StorefrontShipping = () => <Storefront page="shipping" />;
export const StorefrontIncome = () => <Storefront page="income" />;
export const StorefrontPrivacy = () => <Storefront page="privacy" />;
export const StorefrontBuilderPolicy = () => <Storefront page="builder-policy" />;
export const StorefrontLabTested = () => <Storefront page="lab-tested" />;
export const StorefrontPeptides = () => <Storefront page="peptides" />;
export const StorefrontTesting = () => <Storefront page="testing" />;
export const StorefrontCOALibrary = () => <Storefront page="coa-library" />;
export const StorefrontWeightLoss = () => <Storefront page="weight-loss" />;
export const StorefrontSexualHealth = () => <Storefront page="sexual-health" />;
export const StorefrontLongevity = () => <Storefront page="longevity" />;
export const StorefrontInfoWhatsGlp1 = () => <Storefront page="whats-glp-1" />;
export const StorefrontInfoGlp1Instructions = () => <Storefront page="glp-1-instructions" />;
export const StorefrontInfoGlp1Safety = () => <Storefront page="glp-1-safety-instructions" />;
export const StorefrontInfoPrivacyPractices = () => <Storefront page="privacy-practices" />;
export const StorefrontInfoCalifornia = () => <Storefront page="for-california-residents" />;
export const StorefrontInfoPaymentShipping = () => <Storefront page="payment-shipping" />;
export const StorefrontInfoTerms = () => <Storefront page="terms-conditions" />;
export const StorefrontInfoFaq = () => <Storefront page="faq" />;
export const StorefrontInfoPayments = () => <Storefront page="payments" />;
export const StorefrontContact = () => <Storefront page="contact" />;
export const StorefrontAriCompliancePacket = () => <Storefront page="compliance-packet" />;
export const StorefrontAriComplianceChecklist = () => <Storefront page="compliance-action-checklist" />;
export const StorefrontAriBenefits = () => <Storefront page="benefits" />;
export const StorefrontAriIngredients = () => <Storefront page="ingredients" />;
export const StorefrontAriPrivacy = () => <Storefront page="privacy-policy" />;
export const StorefrontHrt = () => <Storefront page="hrt" />;
export const StorefrontHrtMen = () => <Storefront page="hrt-men" />;
export const StorefrontHrtWomen = () => <Storefront page="hrt-women" />;
export const StorefrontHrtPortal = () => <Storefront page="hrt-portal" />;
export const StorefrontPortal = () => <Storefront page="portal" />;
export const StorefrontRegister = () => <Storefront page="register" />;
export const StorefrontCheckout = () => <Storefront page="checkout" />;
export const StorefrontThankYou = () => <Storefront page="thankyou" />;
export const StorefrontSponsor = () => <Storefront page="sponsor" />;
// Adapt X.1 paid-search landing page — the Google Ads campaign's Final URL.
export const StorefrontSleepSpray = () => <Storefront page="sleep-spray-melatonin-free" />;
// Adapt X.1 category hub: the crawlable parent of the two landers above
// (/sleep-spray → both landers, products, blog), so neither lander is an orphan.
export const StorefrontSleepSprayHub = () => <Storefront page="sleep-spray-hub" />;
// Adapt X.1 second lander, built against the store's CRO checklist — the
// Google Ads / paid-social Final URL going forward.
export const StorefrontNaturalSleep = () => <Storefront page="sleep-spray-natural" />;
export const StorefrontSleepAid = () => <Storefront page="sleep-spray-aid" />;
export const StorefrontSleepDeprivation = () => <Storefront page="sleep-spray-deprivation" />;
export const StorefrontGrogginess = () => <Storefront page="sleep-spray-grogginess" />;
export const StorefrontAdaptogens = () => <Storefront page="sleep-spray-adaptogens" />;
export const StorefrontStressRelief = () => <Storefront page="sleep-spray-stress" />;
export const StorefrontOralSpray = () => <Storefront page="sleep-spray-oral" />;
export const StorefrontVeganSpray = () => <Storefront page="sleep-spray-vegan" />;
export const StorefrontPillowSpray = () => <Storefront page="sleep-spray-pillow" />;
export const StorefrontBestSpray = () => <Storefront page="sleep-spray-best" />;
export const StorefrontMelatoninSpray = () => <Storefront page="sleep-spray-melatonin" />;
// Referral links and every storefront path no page route claimed (both
// routers' catch-alls point here): a valid "<page>/<handle>" captures the
// sponsor and lands on that page; anything else gets the designed 404 inside
// the store's own chrome.
export const StorefrontRefCapture = () => {
  const location = useLocation();
  const { slug: paramSlug, webalias } = useParams();
  const { data } = useStorefront(paramSlug || getMaskedSlug());
  // A custom page with this slug wins over a referral handle.
  if (data && findCustomPage(data.marketplace, webalias)) return <Storefront page="custom" />;
  // Remember WHICH path failed rather than a bare flag: a reset effect here
  // would run after the child's and immediately undo it, and navigating on to
  // another dead link must re-resolve rather than keep showing the old 404.
  const [failedPath, setFailedPath] = useState('');
  if (failedPath === location.pathname) return <Storefront page="notfound" />;
  return <RefCapture key={location.pathname} onNotFound={() => setFailedPath(location.pathname)} />;
};

export default Storefront;
